import { setOrganizationContext } from '@src/analytics/EventTracker';
import { getRendererConfig } from '@src/config/rendererConfig';
import { KARAOKE_TRACK_NUMBER } from '@src/editor/constants';
import { Element } from '@src/editor/models/Element';
import { KaraokeConfigSchema } from '@src/editor/models/karaoke/KaraokeConfig';
import { DEFAULT_INIT_FRAMERATE } from '@src/editor/models/Timeline';
import { EditorChangeType } from '@src/editor/types';
import { PERSON_BY_NAME_QUERY } from '@src/gql/person-gql';
import { produceFirstCaptions } from '@src/hooks/produceFirstCaptions';
import {
  type FaceDetection,
  fetchFaceDetectionsApi,
} from '@src/hooks/useFaceDetections';
import { LogLevel, WorkflowType } from '@src/monitoring/types';
import { TranscriptVersion } from '@src/repositories/TranscriptRepository';
import { AIClipProcessor } from '@src/services/AIClipProcessor';
import {
  type BackgroundSaveResult,
  backgroundVideoSaveService,
} from '@src/services/BackgroundVideoSaveService';
import { ReconcileLoadedVideoService } from '@src/services/clipLoad/ReconcileLoadedVideoService';
import { clipProcessorDepsFromStore } from '@src/services/clipProcessorDepsFromStore';
import { syncVideoToStoryEntries } from '@src/services/common/syncVideoToStory';
import CustomFontsService from '@src/services/CustomFontsService';
import {
  fetchParsedScenesApi,
  type SceneData,
} from '@src/services/framing/SceneDetectionService';
import {
  areCaptionsHidden,
  setCaptionsHidden,
} from '@src/services/karaoke/captionsVisibility';
import { DEFAULT_KARAOKE_CONFIG } from '@src/services/karaoke/karaokeConstants';
import KaraokeProducer from '@src/services/karaoke/KaraokeProducer';
import { KaraokeTemplateVariantService } from '@src/services/karaoke/KaraokeTemplateVariantService';
import {
  buildLocalCanvasUrl,
  isLocalCanvasSource,
  probeVideoDecodable,
  requiresNativeLocalPreview,
  waitForMediaLoadSettle,
} from '@src/services/LocalCanvasPreview';
import { publishFanoutDepsFromStore } from '@src/services/publish/publishFanoutDepsFromStore';
import { PublishFanoutService } from '@src/services/publish/PublishFanoutService';
import { downloadVideo } from '@src/services/videoDownloadService';
import VideoPublisher from '@src/services/VideoPublisher';
import {
  populateVideoVersionWithResources,
  requestPublishRecordResources,
} from '@src/services/VideoResourcesProvider';
import { WarmedRenderer } from '@src/services/WarmedRenderer';
import { isTempClipId } from '@src/services/WriteQueueService';
import {
  captureKaraokeLanguageState,
  type KaraokeLanguageSnapshot,
  resolveRestoredUiLanguage,
  seedKaraokeLanguageFromSnapshot,
} from '@src/stores-v2/karaokeLanguageSnapshot';
import { type PublishRecord, PublishStatus } from '@src/types.ts/publishRecord';
import { bootstrapAudioFirstMedia } from '@src/utility/audioFirstSwap';
import {
  deleteElementMeta,
  getElementMetaObject,
} from '@src/utility/extraElementData';
import { findClosestTranscriptIndexToTimestamp } from '@src/utility/findClosestTranscriptIndex';
import { delay } from '@src/utility/general';
import { recordLastEditedClip } from '@src/utility/lastEditedClip';
import { getMasterPlaybackVolumeFactor } from '@src/utility/masterPlaybackVolume';
import { isSameMediaFile } from '@src/utility/mediaUrl';
import {
  getImageNaturalDimensions,
  getVideoNaturalDimensions,
} from '@src/utility/naturalDimensions';
import { isPublishRecordFinished } from '@src/utility/publishRecord';
import { fetchSpeakerVoiceMappings } from '@src/utility/speakerUtils';
import { pickUnderlyingVideoElements } from '@src/utility/stackedLayout';
import { dispatchStaleVideoDetected } from '@src/utility/staleVideoEvents';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import { hydrateOriginalMediaDuration } from '@src/utility/timeline';
import { extractAllAssetRefs } from '@src/utility/timelineAssetUrls';
import { extractTranscriptionText } from '@src/utility/transcriptFormatUtils';
import {
  VIDEO_RENDER_FINISHED_EVENT,
  type VideoRenderFinishedEventDetail,
} from '@src/utility/videoRenderEvents';
import { repairNaNLayoutProps } from '@src/utility/videoSourceRepair';
import {
  computed,
  makeAutoObservable,
  observable,
  reaction,
  runInAction,
  toJS,
} from 'mobx';
import { v4 as uuid } from 'uuid';
import WaveformData from 'waveform-data';
import z from 'zod';

import { ShapeProducer } from '../components/shapeProcessor/ShapeProducer';
import { TextBrandingService } from '../components/textProcessor/TextBrandingService';
import { TextProducer } from '../components/textProcessor/TextProducer';
import { MAX_CANVAS_WIDTH } from '../components/timeline/WaveForm';
import { type ElementState, mapToElementState } from '../renderer/ElementState';
import CaptionService from '../services/CaptionService';
import {
  AIProducerCard,
  DesignCard,
  KaraokeCard,
  MediaCard,
  SidebarOption,
} from '../types.ts/general';
import { PhotoArtifactTab } from '../types.ts/story';
import {
  AspectRatio,
  ClipReviewStatus,
  DEFAULT_CLIP_REVIEW_STATUS,
  TranscriptionStatus,
  VideoDimensionsHeight,
  VideoDimensionsWidth,
  VideoResolution,
  VideoStatus,
} from '../types.ts/video';
import { getAudioDurationFromUrl } from '../utility/audio';
import {
  CAPTIONS_LANGUAGE_CHANGED_EVENT,
  isOriginalLang,
} from '../utility/captionLanguage';
import { inDebugMode } from '../utility/debug';
import { deepClone } from '../utility/deepClone';
import {
  createSnapBoundaries,
  PLAYHEAD_SNAP_BOUNDARY_ID,
} from '../utility/elementSnapping';
import { extractErrorInfo } from '../utility/error';
import { getStandardDimensionsForAspectRatio as getStandardDimensionsForAspectRatio_ } from '../utility/getStandardDimensionsForAspectRatio';
import {
  getGraphicFontSubstitute,
  makeBrandFontVariantLookup,
} from '../utility/graphicFontSubstitution';
import { groupBy } from '../utility/groupBy';
import { LogManager } from '../utility/Logger';
import parsePercent from '../utility/parsePercent';
import { pathStartsWithDirectory } from '../utility/url';
import { resolvePublishResolutionLevel } from '../utility/videoResolution';
import {
  areVolumeKeyPointsSupportedBy,
  formatAndFixOffTimeVolumeKeyPoints,
  getVolumeKeyPointsOf,
  interpolateVolumeAtTime,
} from '../utility/volumeKeyPoints';
import { projectCompositionTranslations } from '../videoTranscriptionProcessor/translation/projectCompositionTranslations';
import { readActiveCaptionLanguage } from '../videoTranscriptionProcessor/translation/readActiveCaptionLanguage';
import { AdaptivePlaybackSourceManager } from './lib/AdaptivePlaybackSourceManager';
import { autoFrameNewAspectRatio } from './lib/AutoFramingHelper';
import { ClipEditorUsableTracker } from './lib/ClipEditorUsableTracker';
import CompositionScopeService from './lib/CompositionScopeService';
import detectAndApplyManualChangesToKaraokeConfig from './lib/onStateChangeHandlers/detectAndApplyManualChangesToKaraokeConfig';
import detectAndApplyManualChangesToTextConfig from './lib/onStateChangeHandlers/detectAndApplyManualChangesToTextConfig';
import PunchListManager from './lib/PunchListManager';
import RefreshManager from './lib/RefreshManager';
import { createScenesFromDetection } from './lib/SceneCreationHelper';
import SceneLayoutSelectionManager from './lib/SceneLayoutSelectionManager';
import StateChangeObserver from './lib/StateChangeObserver';
import StatusUpdateManager from './lib/StatusUpdateManager';
import { TrackManager } from './lib/TrackManager';
import TimelineStore from './TimelineStore';
import {
  MAX_VISIBLE_TOASTS,
  resolveToastDelay,
  type Toast,
  type ToastRequest,
} from './toast';

import type {
  NaturalDimensions,
  NaturalDimensionsMaybe,
} from '../editor/types/aspectRatio';
import type { RendererState } from '../renderer/RendererState';
import type {
  ImageKey,
  ImageWithType,
  TalkingPointContent,
} from '../types.ts/general';
import type {
  AssociatedVideo,
  ClipMetadata,
  ContentViewData,
  DatoStory,
  ExtraElementData,
  Music,
  myAudio,
  OtherVideo,
  Person,
  PhotoAssetData,
  ShareableImageType,
  Showcase,
  SourcePlatform,
  Story,
  StoryDTO,
  StoryVideo,
  Video,
  VideoAction,
  VideoClip,
  VideoItem,
  VideoRenderingStatus,
  VideoVersion,
  VolumeKeyPoint,
} from '../types.ts/story';
import type { TranscriptChange, TranscriptData } from '../types.ts/transcript';
import type { WaveformState } from '../types.ts/video';
import type { ElementBoundary } from '../utility/elementSnapping';
import type { IVideoCreatorStore } from './IVideoCreatorStore';
import type { SimpleSchemaTypes } from '@datocms/cma-client-browser';
import type { Command } from '@src/editor';
import type { TimelineClipboard } from '@src/hooks/timeline/useTimeline';
import type { AssetPrewarmingService } from '@src/services/AssetPrewarmingService';
import type { VideoResourceField } from '@src/services/VideoResourcesProvider';
import type { VideoSource } from '@src/stores-v2';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import type { RootStore } from '@src/stores-v2/RootStore';
import type { RenderModalCtx } from 'datocms-plugin-sdk';

/**
 * PLAT-11228: how long the local-canvas upgrade waits for the renderer to
 * finish loading the cached local bytes before treating it as wedged and
 * reverting to the audio-first source. Generous — a large original can take
 * a while to start decoding — but bounded, so a stuck load never leaves a
 * black canvas for the rest of the session.
 */
const LOCAL_CANVAS_LOAD_TIMEOUT_MS = 12_000;

export { KARAOKE_TRACK_NUMBER };
export const DEFAULT_PREVIEW_VIDEO_RES = VideoResolution.Low;
const MAX_UNDO_REDO_STACK_SIZE = 10;
const AUTOSAVE_INTERVAL = 2 * 60 * 1000; // 2 minutes

export type CurrentLoadedVideo = Video & { versionId?: string } & {
  editor?: SimpleSchemaTypes.User | SimpleSchemaTypes.ItemVersion['editor'];
  ai_generated_content?: Array<{
    attributes: { prompt: string; generated_content: string };
  }>;
};

/**
 * A whole-video undo/redo snapshot: the video plus the caption-language state a
 * restore needs (PLAT-10816) and the label shown on the undo button. Shared by
 * both stacks — `VideoCreatorStore` and `ClipVideoCreatorAdapter` — so the two
 * cannot describe a snapshot differently.
 */
export type VideoStateSnapshot = CurrentLoadedVideo &
  KaraokeLanguageSnapshot & { label?: string };

const VIDEO_TEMPLATE_SETTINGS_KEY = 'video_template_settings';

export type KnownFlags = never;
type NumericFlags = 'sceneManipulationLimit' | 'enableAutoFraming';
type StringFlags =
  | 'contentGenerationChatGptModel'
  | 'creatorStudioLayoutStrategy'
  | 'autoFramingStrategy';
const logger = LogManager.getLogger('VideoCreatorStore');

function typedKeys<T extends object>(obj: T): (keyof T)[] {
  // eslint-disable-next-line no-restricted-syntax
  return Object.keys(obj) as (keyof T)[];
}

function safeClone<T>(value: T, fieldName: string): T {
  if (value == null) return value;
  try {
    return structuredClone(value);
  } catch (err) {
    logger.warn('prepareAsCurrentLoadedVideo:clone-failed', {
      fieldName,
      error: err instanceof Error ? err.message : String(err),
    });
    return value;
  }
}

// Drops the parentVideo back-ref so an associatedVideos entry can't form a
// parent<->child cycle that overflows MobX's deep enhancer / toJS.
function flattenAssociatedVideo(video: AssociatedVideo): AssociatedVideo {
  return { ...video, parentVideo: undefined, associatedVideos: [] };
}

interface LoadVersionsHistoryOptions {
  /**
   * When true (default), the loaded video's `versionId` is updated to the
   * page's `is_current` (head) version. This is correct after a save/render —
   * the editor reflects the new head, but `currentVideo` wasn't reassigned, so
   * its `versionId` would otherwise be stale. It must be FALSE for plain
   * pagination: paging through history is a read and must never change which
   * version is considered loaded, otherwise returning to page 1 (which holds
   * the head) would re-mark the head as the active row even while an older
   * version is actually loaded.
   */
  syncLoadedVersionToHead?: boolean;
}

class VideoCreatorStore implements IVideoCreatorStore {
  // Feature flags (mirrored from useFlagsCombination)
  private flags: Record<KnownFlags, boolean> &
    Partial<Record<NumericFlags, number>> &
    Partial<Record<StringFlags, string>> = {};
  // New Context Store.  Only giving access here to simpify refactor.
  rootStore: RootStore;

  /**
   * Constructor-injected infrastructure service. Lives in `ServiceContainer`,
   * not in `RootStore` (it has no MobX-reactive state). Used only to
   * construct the `WarmedRenderer` in `initializeVideoPlayer`; once the
   * renderer exists, all pre-warming flows through `this.renderer.warmAsset`
   * / `warmAssets`. Keep this `private` to enforce that — there should be
   * exactly one path from this store to the service.
   */
  private readonly assetPrewarmingService: AssetPrewarmingService;

  datoClientStore: DatoClientStore;

  renderer?: WarmedRenderer = undefined;

  timelineStore: TimelineStore;

  _textBrandingService: TextBrandingService | null = null;

  customFontsService: CustomFontsService | null = null;

  karaokeProducer: KaraokeProducer;

  textProducer: TextProducer;

  shapeProducer: ShapeProducer;

  /**
   * Mirror of the Creatomate renderer's state. After PLAT-9562 timeline
   * windowing, `state.elements` is a **windowed subset** of the timeline —
   * only the elements near the playhead. Reads of scalar fields
   * (`duration`, `width`, `height`) and the currently-selected element are
   * fine; anything that needs the full timeline must read from the editor
   * model instead (e.g. `editorStore.allElementStates`,
   * `editorStore.getElementStateById(id)`,
   * `editorStore.editor.timeline.allElements`).
   *
   * Comparing this state against a previously-stored state to detect what
   * the renderer changed is correct — both snapshots share the same window.
   */
  state?: RendererState = undefined;

  stateReady = false;

  // Computed: whether karaoke elements have been generated (track 32 present)
  // Delegates to EditorStore.hasKaraokeTrack for single source of truth
  get isKaraokeGenerated(): boolean {
    return this.rootStore.editorStore?.hasKaraokeTrack ?? false;
  }

  // Flag to control whether to auto-switch to karaoke sidebar when subtitles are shown
  autoSwitchToKaraokeSidebar: boolean = true;

  activeElementIds: string[] = [];

  private compositionScopeService: CompositionScopeService;

  selectedTrack: number = -1;

  isInitialized = false;

  isLoading = true;
  isLoadingAssets = false;
  private pendingIsLoadingAssets: boolean = false;

  thumbnailLoadingElements = new Set<string>();

  // Workflow tracking for video loading monitoring
  currentVideoLoadingWorkflowId: string | null = null;

  isVersionsHistoryLoading = false;

  isError = false;

  justLoaded = false;

  isSaving = false;

  isBackgroundSaving = false;

  private _pendingNextSave: {
    withRenderer: boolean;
    autoSave: boolean;
  } | null = null;

  private _backgroundSaveResolvers: Array<{
    resolve: () => void;
    reject: (err: Error) => void;
  }> = [];

  showRepublishOnSaveModal = false;

  setShowRepublishOnSaveModal(value: boolean) {
    this.showRepublishOnSaveModal = value;
  }

  setOpenReframeVideoModal(value: boolean) {
    this.openReframeVideoModal = value;
  }

  savingStockPhoto = false;

  savingError?: IVideoCreatorStore['savingError'];

  isPlaying = false;

  whenPaused: number | null = null;

  time = 0;

  smootherTime = 0;

  keyPointsLastUpdateTime = 0;

  isSnapGuideVisible = false;

  snapGuideTime = 0; // in seconds

  isPlayheadSnapHighlighted = false;

  duration = 180;

  timelineScale = 100;

  defaultTimelineScale = 100;

  maxTimelineScale = 400;

  maxCanvasScale = 400;

  isScrubbing = false;

  /**
   * Monotonic seek counter, so a seek that waited on a window rebuild can
   * tell it has been superseded and stand down (PLAT-11119).
   */
  private _seekSeq = 0;

  flushPendingIsLoadingAssets() {
    if (this.pendingIsLoadingAssets === false) {
      return;
    }

    runInAction(() => {
      this.isLoadingAssets = this.pendingIsLoadingAssets;
      this.pendingIsLoadingAssets = false;
    });
  }

  // startingState?: RendererState = undefined;

  renderingStatus = ''; // todo video.status

  renderQueueing = false;

  renderVideoResLevel: VideoResolution | null = null;

  /**
   * Derived from the tracking store's in-flight records. Readers get a live
   * view without manual push/prune bookkeeping. Not cached by MobX — derives
   * fresh on each access so it always reflects the current tracking state.
   */
  get renderingVideoIds(): string[] {
    const result = new Set<string>();
    for (const id of this.rootStore.publishTrackingStore.inFlightIds) {
      const record = this.rootStore.publishTrackingStore.records.get(id);
      if (record?.videoId) result.add(record.videoId);
    }
    return Array.from(result);
  }
  // Transcription state is now managed by TranscriptionStore

  storyId?: string;

  storyName?: string = '';

  datoContext: Partial<RenderModalCtx> = {};

  originalVideoDuration: string = '0 s';

  originalVideoAudioUrl?: string;

  sidebarOptions: SidebarOption = SidebarOption.none;

  // The panel that was open before the last collapse, so reopening a tab can
  // restore where the user left off (e.g. primary video settings) instead of
  // treating the reopen as a tab switch that clears the active element.
  lastExpandedSidebarOption: SidebarOption = SidebarOption.none;

  showSubtitles = false;

  aiProducerSubMenu: AIProducerCard = AIProducerCard.photo;

  mediaSubMenu: MediaCard = MediaCard.photo;

  designSubMenu: DesignCard = DesignCard.text;

  karaokeSubMenu: KaraokeCard = KaraokeCard.templates;

  musicOptions?: Music['collection'] = undefined;

  musicProducerLoading: boolean = false;

  uploadingMyAudio: boolean = false;

  selectedPhotoAssets: PhotoAssetData = {
    tab: PhotoArtifactTab.story,
    resource: undefined,
    lastSelectedStock: undefined,
    lastSelectedAi: undefined,
    selectedId: undefined,
  };

  stockMusic: Music[] = [];

  organization?: Showcase = undefined;

  story?: Story = undefined;

  faceDetections: FaceDetection[] | null = null;

  sceneDetections: SceneData[] | null = null;

  originalWaveForm: WaveformData | null = null;

  resampledOriginalWaveForm: WaveformState | null = null;

  maxOriginalWaveformSample: number = 0;

  audioTracksData: Record<
    string,
    | {
        waveform: WaveformData;
        originalTrackDuration: number;
        resampledWaveform?: WaveformData;
      }
    | 'loading'
  > = {};

  audioContext: AudioContext | null = null;

  audioContextReady = false;

  // NOTE: the `= undefined` initializers are required, not cosmetic. The build
  // sets `useDefineForClassFields: false` (tsconfig target es2015), so an
  // optional field with no initializer emits no runtime property and
  // `makeAutoObservable` (called in the constructor) cannot observe it. Without
  // them these fields update as plain values but never trigger re-renders
  // (e.g. the History tab's active-version highlight). See `renderer`/`state`
  // above for the same pattern.
  currentVideo?: CurrentLoadedVideo = undefined;

  currentVideoVersions?: VideoVersion[] = undefined;

  currentVideoVersionsPage: number = 0;

  currentVideoVersionsTotalPages?: number = undefined;

  punchListManager: PunchListManager;

  punchListLoading = false;

  addedPunchListItemId: string | null = null;

  fuse: unknown = undefined;

  trackManager: TrackManager;

  statusUpdateManager: StatusUpdateManager;

  sceneLayoutSelectionManager: SceneLayoutSelectionManager;

  refreshManager: RefreshManager;

  private _adaptivePlaybackSourceManager?: AdaptivePlaybackSourceManager;

  private clipEditorUsableTracker: ClipEditorUsableTracker;

  stateChangeObserver: StateChangeObserver;

  unsavedShareableImages:
    | Omit<ShareableImageType, '_allReferencingSharedContents'>[]
    | null = null;

  // Initialized (see the NOTE above) so streamed generations re-render as
  // they arrive; without it Pull Quotes showed nothing until the stream ended.
  contentStudioGeneratedContent?: ContentViewData = undefined;

  isPlayerFullScreen = false;

  karaokeLoading = false;

  isPlayheadDragging: boolean = false;

  tempDragTime: number | null = null;

  openPhotoElementReplacementModal: IVideoCreatorStore['openPhotoElementReplacementModal'] =
    null;

  openReframeVideoModal: boolean = false;

  replacementImages: {
    blog: Record<
      string,
      {
        value: ImageWithType[ImageKey] | null;
        isRemoved: boolean;
      }
    >;
    socialPost: Record<
      string,
      {
        value: ImageWithType[ImageKey] | null;
        isRemoved: boolean;
      }
    >;
    email: { value: ImageWithType[ImageKey] | null; isRemoved: boolean };
  } = { email: { value: null, isRemoved: false }, blog: {}, socialPost: {} };

  savedItemReplacementImages: {
    blogs: Record<
      number,
      Record<
        string,
        {
          value: ImageWithType[ImageKey] | null;
          isRemoved: boolean;
        }
      >
    >;
    socialPost: Record<
      number,
      Record<
        string,
        {
          value: ImageWithType[ImageKey] | null;
          isRemoved: boolean;
        }
      >
    >;
    emails: Record<
      number,
      { value: ImageWithType[ImageKey] | null; isRemoved: boolean }
    >;
  } = { emails: {}, blogs: {}, socialPost: {} };

  selectedGeneratedContent: {
    blog: {
      id?: string;
      type: 'generating' | 'generated' | 'saved';
      content?: string;
    } | null;
    socialPost: {
      id?: string;
      type: 'generating' | 'generated' | 'saved';
      content?: string;
    } | null;
    email: {
      id?: string;
      type: 'generating' | 'generated' | 'saved';
      content?: string;
    } | null;
    reelOutline: {
      id?: string;
      type: 'generating' | 'generated' | 'saved';
      content?: string;
    } | null;
  } = { blog: null, socialPost: null, email: null, reelOutline: null };

  /** Visible notification toasts, newest first. Replaced wholesale so MobX
   *  sees every change without deep observation. */
  toasts: Toast[] = [];

  private lastToastId = 0;

  showRefreshStoryForSocialProfile: boolean = false;

  pendingSharedContentIds: string[] = [];

  talkingPointContent: TalkingPointContent | null = null;

  frameLockedTracks: number[] = [];

  timelineClipboard: TimelineClipboard | null = null;

  videoClipPreview:
    | (VideoClip & {
        autoPlay?: boolean;
        selectedDimension?: string;
        videoSource?: Video['videoSource'];
        transcriptionSnapshot?: Video['transcriptionSnapshot'];
        karaokeData?: Video['karaokeData'];
        scenes?: Video['scenes'];
        extraElementData?: Video['extraElementData'];
        activeVideoTemplateId?: string;
      })
    | null = null;

  /** Remembers the last aspect ratio the user viewed per clip, so reopening
   *  the clip preview modal returns to the same AR instead of 16:9. */
  lastClipDimension: Map<string, string> = new Map();

  isIframeActive: boolean = false;

  selectedVolumeKeyPoint?: VolumeKeyPoint | null;
  // Bumped by applyVolumeKeyPoints on every mutation. Read by VolumeKeyPoints
  // components to force a re-render — deep MobX observability of
  // extraElementData[id] doesn't reliably fire on the auto-clear-to-empty path.
  volumeKeyPointsRevision: number = 0;

  transitionInProgress: 'changingAspectRatio' | null = null;

  /** Set after creating a new aspect ratio video when a template was active.
   *  VideoTemplatesContext picks this up and re-applies the template fresh. */
  pendingTemplateReapplyId: string | null = null;

  /** Callback wired by VideoTemplatesProvider for direct template re-application
   *  during aspect ratio changes. When available, bypasses the reactive
   *  pendingTemplateReapplyId mechanism for more reliable timing. */
  reapplyTemplate?: (templateId: string) => Promise<void>;

  preloadedNaturalDimensions: Record<string, NaturalDimensions> = {};

  videoSnapshots: Array<VideoStateSnapshot> = [];

  redoSnapshots: Array<VideoStateSnapshot> = [];

  undoStack: {
    undoCommand: () => void;
    redoCommand: () => void;
    activeElementIds: string[];
    selectedTrack: number;
    label?: string;
    isSaved?: boolean;
  }[] = [];

  redoStack: {
    undoCommand: () => void;
    redoCommand: () => void;
    activeElementIds: string[];
    selectedTrack: number;
    label?: string;
    isSaved?: boolean;
  }[] = [];

  resetUndoRedo() {
    this.undoStack = [];
    this.redoStack = [];
    this.videoSnapshots = [];
    this.redoSnapshots = [];

    // Keep the internal VideoEditor history in sync
    try {
      const editor = this.rootStore.editorStore?.editor;
      editor?.clearHistory();
    } catch (err) {
      console.warn('resetUndoRedo: unable to clear VideoEditor history', err);
    }
  }

  undo() {
    if (this.undoStack.length === 0) return;
    const entry = this.undoStack.pop();
    if (!entry) return;

    const { undoCommand, redoCommand, activeElementIds, selectedTrack, label } =
      entry;
    const activeElementIdsBeforeUndo = this.activeElementIds.slice();
    const selectedTrackBeforeUndo = this.selectedTrack;

    undoCommand();

    this.setActiveElements(...activeElementIds);
    if (selectedTrack !== -1) {
      this.selectedTrack = selectedTrack;
    }

    this.redoStack.push({
      undoCommand,
      redoCommand,
      activeElementIds: activeElementIdsBeforeUndo,
      selectedTrack: selectedTrackBeforeUndo,
      label,
    });
    this.recalculateTimelineScale();
    this.rootStore.editorStore?.editor?.notifyObservers(
      EditorChangeType.EXTERNAL_UNDO,
    );
  }

  redo() {
    const entry = this.redoStack.pop();
    if (!entry) return;

    const { undoCommand, redoCommand, activeElementIds, selectedTrack, label } =
      entry;
    const activeElementIdsBeforeRedo = this.activeElementIds.slice();
    const selectedTrackBeforeRedo = this.selectedTrack;

    redoCommand();

    this.setActiveElements(...activeElementIds);
    if (selectedTrack !== -1) {
      this.selectedTrack = selectedTrack;
    }

    this.undoStack.push({
      undoCommand,
      redoCommand,
      activeElementIds: activeElementIdsBeforeRedo,
      selectedTrack: selectedTrackBeforeRedo,
      label,
    });
    this.recalculateTimelineScale();
    this.rootStore.editorStore?.editor?.notifyObservers(
      EditorChangeType.EXTERNAL_REDO,
    );
  }

  resetStateBeforeInitialising() {
    // Abort any pending publish operations waiting on background save
    this._rejectPendingBackgroundSaveWaiters();

    runInAction(() => {
      this.story = undefined;
      this.currentVideo = undefined;
      // The editor still holds the previous story's project until the new
      // renderVideo → loadProject lands (the init path renders with
      // resetTimeline=false, so nothing else clears this). Consumers gate on
      // it to hide the stale timeline/filmstrip during the switch, and the
      // load-time audio-first swap requires it false on first render.
      this.stateReady = false;
      this.currentVideoVersions = undefined;
      this.currentVideoVersionsPage = 0;
      this.currentVideoVersionsTotalPages = undefined;

      // Reset content studio generated content
      this.contentStudioGeneratedContent = undefined;
      this.selectedGeneratedContent = {
        blog: null,
        socialPost: null,
        email: null,
        reelOutline: null,
      };
      this.talkingPointContent = null;
      this.unsavedShareableImages = null;
      this.replacementImages = {
        email: { value: null, isRemoved: false },
        blog: {},
        socialPost: {},
      };
      this.savedItemReplacementImages = {
        emails: {},
        blogs: {},
        socialPost: {},
      };

      this.rootStore.transcriptionStore.reset();
      this.rootStore.videoSourceStore.resetSource();
      // PLAT-11911: a seed belongs to the story it was handed over in.
      this.rootStore.studioHandoffStore.setSeed(null);

      console.log('state reset');
    });
  }

  // todo refactor
  photoDataForDato: Record<
    string,
    {
      type: 'quotes' | 'stock' | 'ai' | 'artifact';
      url: string;
      title?: string;
      alt?: string;
      cat?: string;
    } & (
      | {
          fileName: string;
        }
      | { uploadId: string }
    )
  > = {};

  // punchListData: PunchListItem[] | null = null;

  refreshStory: number = 0;

  disableAiImageGenerate = false;

  /**
   * Representation ids the scene-layout resolver has already warned about, so a
   * miss that persists across frames logs once instead of once per frame. Not
   * observable and not cleared: ids are bounded by the layout pass, and
   * once-per-id is what makes a persistent miss legible in monitoring.
   */
  private _unresolvedRepresentationIdsWarned: Set<string>;

  constructor(
    rootStore: RootStore,
    assetPrewarmingService: AssetPrewarmingService,
  ) {
    this._unresolvedRepresentationIdsWarned = new Set<string>();
    // The override object is locally cast because TypeScript's keyof-inference
    // for `AnnotationsMap<this, never>` exceeds its computation budget on
    // this 90+ field class — adding a single new override entry tips it from
    // "barely-works" to a misleading error pointing at a sibling key. The
    // runtime annotation behavior is unchanged.
    makeAutoObservable(this, {
      canRedo: computed,
      canUndo: computed,
      isKaraokeGenerated: computed,
      renderingVideoIds: false, // Derive fresh on each access (tracking store)
      rootStore: false, // Mark references to other stores as non-observable
      assetPrewarmingService: false, // Infrastructure service — not reactive
      _adaptivePlaybackSourceManager: false,
      compositionScopeService: false,
      _unresolvedRepresentationIdsWarned: false, // Log bookkeeping — not reactive
      contentStudioGeneratedContent: observable.ref, // Replaced wholesale per stream chunk
    } as Parameters<typeof makeAutoObservable>[1]);
    this.rootStore = rootStore;
    this.assetPrewarmingService = assetPrewarmingService;
    this.datoClientStore = this.rootStore.datoClientStore;

    this.timelineStore = new TimelineStore(this, rootStore);
    this.karaokeProducer = new KaraokeProducer(this);

    this.textProducer = new TextProducer(this);
    this.shapeProducer = new ShapeProducer(this);
    this.compositionScopeService = new CompositionScopeService();

    this.punchListManager = new PunchListManager(
      this,
      this.datoClientStore,
      this.getEditorStore(),
    );
    this.trackManager = new TrackManager(this);
    this.statusUpdateManager = new StatusUpdateManager(this);
    this.sceneLayoutSelectionManager = new SceneLayoutSelectionManager(
      () => this.rootStore.sceneLayoutIndexStore,
      id => {
        const elementSource =
          this.rootStore.videoSourceStore.findElementById(id);
        if (!elementSource) {
          // A layout representation can be absent from the source while the
          // index still knows it — timeline windowing prunes composition
          // children outside the playback window. Resolve to undefined so
          // callers fall back to the element they started from; throwing here
          // aborts the MobX time reaction on every rendered frame (PLAT-11065).
          //
          // Deduped per id: this closure runs from that same per-frame reaction,
          // so an unresolved id would otherwise log ~30 lines/second — the very
          // volume the throw produced. Ids are bounded by the layout pass, and
          // once-per-id is what makes a persistent miss legible in monitoring.
          if (!this._unresolvedRepresentationIdsWarned.has(id)) {
            this._unresolvedRepresentationIdsWarned.add(id);
            logger.warn(
              'Layout representation not found in video source; keeping the unresolved element',
              { elementId: id, time: this.time },
            );
          }
          return undefined;
        }
        return mapToElementState(elementSource);
      },
      () => this.activeElementIds,
      () => this.time,
    );
    reaction(
      () => this.time,
      () => {
        if (this.activeElementIds.length !== 1) {
          return;
        }

        const current = this.getActiveElement();
        const currentReal = this.getRealActiveElement();
        if (!current || !currentReal) {
          return;
        }

        const fresh =
          this.sceneLayoutSelectionManager.resolveElementForActivation(
            currentReal,
          );

        if (fresh.source.id !== current.source.id) {
          this.setActiveElements(fresh.source.id).catch(error => {
            logger.warn('Failed to sync active element after time change', {
              error,
            });
          });
        }
      },
    );

    // ---------------------------------------------------------------
    // hybrid-adaptive: recalculate layout when playhead crosses the
    // cached split-range window boundary.
    // Delegated to AdaptivePlaybackSourceManager for reuse in clip preview.
    // ---------------------------------------------------------------
    this._adaptivePlaybackSourceManager = new AdaptivePlaybackSourceManager({
      getTime: () => this.time,
      getIsPlaying: () => this.isPlaying,
      getIsScrubbing: () => this.isScrubbing,
      getNeedsRefresh: (time: number) => {
        const editorStore = this.rootStore.editorStore;
        return (
          (editorStore?.needsHybridAdaptiveRefresh(time) ?? false) ||
          (editorStore?.needsWindowingRefresh(time) ?? false)
        );
      },
      refreshTimeline: () => {
        this.rootStore.editorStore?.refreshTimeline();
      },
    });

    this.refreshManager = new RefreshManager(this);
    this.clipEditorUsableTracker = new ClipEditorUsableTracker({
      getAnalytics: () => this.rootStore.analyticsStore,
      isVideoReady: () => !this.isLoading && !this.isLoadingAssets,
      // isTranscriptionReady (Loaded + content present), not the raw status:
      // the status can sit stale-Loaded from a previous visit and only flip
      // to Loading after the editor mounts.
      isTranscriptReady: () =>
        this.rootStore.transcriptionStore.isTranscriptionReady,
      getStoryId: () => this.story?.id,
      getStoryTitle: () => this.story?.title,
      getClipTitle: () => this.currentVideo?.title,
      getVideoId: () => this.currentVideo?.id,
    });
    this.customFontsService = new CustomFontsService(this);
    this.stateChangeObserver = new StateChangeObserver([
      detectAndApplyManualChangesToKaraokeConfig,
      detectAndApplyManualChangesToTextConfig,
    ]);

    this.listenToVideoClipPreviewChange();
    this.resetMigrationStateOnVideoChange();
    this.resetSidebarSubMenusOnVideoChange();
    this.syncCaptionsSidePanels();
    this.setupIframeActiveElementListener();
    this.setupIframeDeactivationHandler();

    this.rootStore.editorStore.initializePlaybackSync({
      getCurrentTime: () => this.time,
      getIsPlaying: () => this.isPlaying,
      getIsScrubbing: () => this.isScrubbing,
    });
    this.rootStore.analyticsStore.setVideoCreatorStore(this);

    this.onExtraElementDataChange(eed => {
      this.rootStore.editorStore.editor.passExtraElementData(eed);
    });

    // Setup reactive sync for punch list items to editor
    this.punchListManager.onPunchListItemsChange(items => {
      this.rootStore.editorStore.editor.passPunchListItems(items);
    });

    // ---------------------------------------------------------------------
    // Simple bridge: take a VideoCreatorStore snapshot BEFORE each
    // VideoEditor command.  We monkey-patch executeCommand for minimal
    // intrusion and rely solely on the existing snapshot-based undo/redo.
    // ---------------------------------------------------------------------
    try {
      const editor = this.rootStore.editorStore?.editor;
      if (editor) {
        const originalExecute = editor.executeCommand.bind(editor);

        editor.executeCommand = (command: Command) => {
          // 1) snapshot current state so it can be undone later
          if (command?.shouldCreateUndoRedoEntry?.()) {
            try {
              const label = command?.getDescription?.();
              this.saveVideoStateSnapshot('undo', label, true);
              this.redoStack = [];
            } catch (e) {
              console.error('snapshot capture failed', e);
            }
          } else {
            // unless we don't want a snapshot for a given command
            console.log(
              'No undo/redo entry created for command',
              command?.getName?.(),
            );
          }

          // 2) perform the actual command
          return originalExecute(command);
        };
      }
    } catch (err) {
      console.error('Failed to patch VideoEditor.executeCommand', err);
    }
  }

  resetMigrationStateOnVideoChange() {
    reaction(
      () => this.currentVideo?.id,
      () => {
        if (this.isSaving || this.isBackgroundSaving) return; // to avoid resetting for current video when it is saved for the first time and gets an id
        this.rootStore.editorStore.resetMigrationState();
      },
    );
  }

  listenToVideoClipPreviewChange() {
    reaction(
      () => this.videoClipPreview?.id,
      (newValue, oldValue) => {
        if (newValue) {
          // Skip on temp→real ID swap — clip already has resources
          if (oldValue && isTempClipId(oldValue)) return;

          this.signVideoClipPreviewSource();
          this.timelineStore.setShouldCalcTimelineScale(true);
        }
      },
    );
  }

  resetSidebarSubMenusOnVideoChange() {
    reaction(
      () => this.currentVideo?.id,
      () => {
        if (this.isSaving || this.isBackgroundSaving) return; // avoid resetting when video is being saved
        // reset all sidebar sub-menus to defaults when switching clips
        this.aiProducerSubMenu = AIProducerCard.photo;
        this.mediaSubMenu = MediaCard.photo;
        this.designSubMenu = DesignCard.text;
        this.karaokeSubMenu = KaraokeCard.templates;
      },
    );
  }

  syncCaptionsSidePanels() {
    const isSidebarOptionCompatibleWithKaraoke = (
      option: SidebarOption,
    ): boolean => {
      return [SidebarOption.editing, SidebarOption.karaoke].includes(option);
    };
    reaction(
      () => this.sidebarOptions,
      newValue => {
        // Entering the karaoke sidebar opens the captions view. We
        // intentionally do NOT auto-close captions when leaving for an
        // incompatible sidebar (e.g. selecting a timeline element switches
        // to design/media): the captions view stays until the user switches
        // back to Transcript in the transcript's view dropdown.
        if (!this.showSubtitles && newValue === SidebarOption.karaoke) {
          this.setShowSubtitles(true);
        }
      },
    );
    reaction(
      () => this.showSubtitles,
      newValue => {
        if (
          !isSidebarOptionCompatibleWithKaraoke(this.sidebarOptions) &&
          newValue &&
          this.autoSwitchToKaraokeSidebar
        ) {
          this.sidebarOptions = SidebarOption.karaoke;
        }
      },
    );
  }

  setupIframeActiveElementListener() {
    // Listen to focus/blur events on window to detect active element changes
    const checkIframeActive = () => {
      if (!this.renderer) return;

      const isCurrentlyActive = this.renderer.isIframeActive();

      runInAction(() => {
        this.isIframeActive = isCurrentlyActive;
      });
    };

    // Check on both window focus/blur and document focus/blur
    window.addEventListener('focus', checkIframeActive, true);
    window.addEventListener('blur', checkIframeActive, true);
    document.addEventListener('focusin', checkIframeActive);
    document.addEventListener('focusout', checkIframeActive);
  }

  setupIframeDeactivationHandler() {
    reaction(
      () => this.isIframeActive,
      (isActive, wasActive) => {
        // Detect transition from active -> inactive (user finished editing)
        if (wasActive && !isActive) {
          logger.info('Iframe deactivated - dispatching FORCED_REFRESH event');
          const editor = this.rootStore.editorStore?.editor;
          if (editor) {
            // Dispatch external event to trigger full re-render
            editor.notifyObservers(EditorChangeType.FORCED_REFRESH, {
              reason: 'iframe_deactivated',
            });
          }
        }
      },
    );
  }

  /**
   * Leave the renderer's composition scope, without touching the selection.
   * Split out for play, which needs the scope reset but must NOT clear
   * `activeElementIds` — that empties every surface bound to the selected
   * element (PLAT-11402).
   */
  private async exitCompositionScope(): Promise<void> {
    await this.compositionScopeService.exitScope(this.renderer);
  }

  async deselectActiveComposition(): Promise<void> {
    if (!this.compositionScopeService.hasScopedComposition()) {
      return;
    }
    await this.exitCompositionScope();
    return this.setActiveElements().catch(err => {
      logger.warn('Failed to deselect active composition', {
        error: extractErrorInfo(err),
      });
    });
  }

  setClipPreviewAndUpdateSource(clip: typeof this.videoClipPreview) {
    this.signVideoClipPreviewSource(clip);
    this.videoClipPreview = clip;
  }

  private signVideoClipPreviewSource(
    clipPreview: typeof this.videoClipPreview = this.videoClipPreview,
  ) {
    if (!this.story?.originalVideo || !clipPreview) {
      return;
    }
    for (const key_ of ['videoSource', 'videoJson']) {
      // TODO: improve typing
      // eslint-disable-next-line no-restricted-syntax
      const key = key_ as 'videoSource';
      if (clipPreview[key]) {
        // Use the low-res (270p) preview source so it matches the URL the
        // ContentStudio pre-warmer caches (getLowResVideoUrl → mp4UrlLow).
        // Signing at a higher res would prompt an unnecessary requests to
        // fetch/cache-blob/cache-renderer-assets
        clipPreview[key] = this.replaceWithValidVideoSourceUrl(
          clipPreview[key],
          DEFAULT_PREVIEW_VIDEO_RES,
        );
      }
    }
  }

  // Lazy Initialization for stateful services
  // until we have a better way to handle this
  // App expects to to be entirely global lol
  get textBrandingService() {
    if (!this._textBrandingService)
      this._textBrandingService = new TextBrandingService(
        this,
        this.datoClientStore,
        this.rootStore.editorStore.editor,
      );

    return this._textBrandingService;
  }

  async preloadFaceDetections(storyId: string) {
    try {
      const result = await fetchFaceDetectionsApi(storyId);
      if (result) {
        runInAction(() => {
          this.faceDetections = result.detections;
        });
        void this.rootStore.speakerFaceMapStore.load(storyId, result);
        logger.info('Preloaded face detections', {
          storyId,
          count: this.faceDetections?.length || 0,
        });
      }
    } catch (error) {
      logger.warn('Failed to preload face detections', { storyId, error });
    }
  }

  async preloadSceneDetections(storyId: string) {
    try {
      const result = await fetchParsedScenesApi(storyId);
      if (result) {
        runInAction(() => {
          this.sceneDetections = result;
        });
        logger.info('Preloaded scene detections', {
          storyId,
          sceneCount: this.sceneDetections?.length || 0,
        });
      }
    } catch (error) {
      logger.warn('Failed to preload scene detections', { storyId, error });
    }
  }

  /**
   * Warm speaker caches (SpeakerRepository + speakerUtils voice mappings)
   * so the Transcript & Speakers tab loads instantly.
   */
  async preloadSpeakerData(storyId: string) {
    try {
      await Promise.all([
        this.datoClientStore.speakerRepository.getStorySpeakers(storyId),
        fetchSpeakerVoiceMappings(storyId),
      ]);
      logger.info('Preloaded speaker data', { storyId });
    } catch (error) {
      logger.warn('Failed to preload speaker data', { storyId, error });
    }
  }

  // Apply late-hydrated showcase fields (dubbing activation DE3 + supported
  // languages DI19) onto the loaded organization, guarding against a workspace
  // switch mid-flight. Lets org-scoped pages that skip the full story load
  // (e.g. the Speaker Manager via useEnsureDubbingFields) hydrate these fields
  // through a store action instead of assigning organization directly. Mirrors
  // the fire-and-forget hydration in initializeWithStory.
  applyDubbingFields(orgId: string, fields: Partial<Showcase>): void {
    if (this.organization?.id !== orgId) return;
    this.organization = { ...this.organization, ...fields };
  }

  async initializeWithStory(
    storyId: string,
    videoId?: string | null,
    showcaseSlug?: string | null,
    fromCache: boolean = false,
    createFromOriginal: boolean = false,
  ) {
    let story: Story | null | undefined = this.story;
    const returnedToTheSameStory = fromCache && story && story.id === storyId;
    // The clips grid's publish-record cache lives for the app session, so a
    // publish made in another tab (or by someone else) would otherwise never
    // reach it. Dropped here — before `this.story` flips and un-skeletons the
    // grid — so the fetch that follows is the story's first (PLAT-11591).
    // Gated like the preloads below: a cached re-entry is also an in-story
    // *video* switch (`VideoCreator` passes `fromCache` on either change), where
    // nothing published and the refetch would only make the mounted grid flash
    // its legacy-only state. A publish made here arrives via `mergeStoryRecord`.
    if (!returnedToTheSameStory) {
      this.rootStore.publishRecordsStore.clearStory(storyId);
    }

    const op1Start = performance.now();
    if (!this.datoClientStore.storyRepository)
      throw new Error('Story repository is not initialized');
    if (!returnedToTheSameStory) {
      this.resetStateBeforeInitialising();
      story = await this.findOneStory(storyId);
    } else {
      // Fake loading so that previous' video transcript version doesn't show up
      this.rootStore.transcriptionStore.transcriptionLoadingStatus =
        TranscriptionStatus.Loading;
    }
    const op1End = performance.now();

    if (!story) throw new Error('Story not found. Cannot initialize data.');
    this.story = story;

    const organization = showcaseSlug
      ? story._allReferencingShowcases.find(s => s.slug === showcaseSlug)
      : story._allReferencingShowcases[0];
    this.organization = organization;
    // Awaited later before loadKaraokePresets which reads the flags.
    let templateFlagsPromise: Promise<void> = Promise.resolve();

    if (organization?.id) {
      // STORY_QUERY's _allReferencingShowcases fragment does not include
      // translation fields (translationsEnabled, supportedLanguages, handler
      // override) — hydrate them fire-and-forget; failure defaults to empty.
      this.datoClientStore.albumRepository
        .fetchTranslationFieldsById(organization.id)
        .then(fields => {
          if (this.organization?.id === organization.id) {
            this.organization = { ...this.organization, ...fields };
          }
        })
        .catch(err => {
          logger.warn(
            'Failed to hydrate showcase translation fields',
            extractErrorInfo(err),
          );
        });
      // Dubbing activation fields (DE3) — same fire-and-forget hydration as the
      // translation fields above; failure defaults to empty (dubbing off).
      this.datoClientStore.albumRepository
        .fetchDubbingFieldsById(organization.id)
        .then(fields => {
          if (this.organization?.id === organization.id) {
            this.organization = { ...this.organization, ...fields };
          }
        })
        .catch(err => {
          logger.warn(
            'Failed to hydrate showcase dubbing fields',
            extractErrorInfo(err),
          );
        });
      // Eagerly hydrate the active AI Voice catalog into the store-scoped
      // DubbingStore (re-hydrates on workspace switch — no lazy/cold fetch).
      this.rootStore.dubbingStore.hydrate(organization.id).catch(err => {
        logger.warn(
          'Failed to hydrate AI voice catalog',
          extractErrorInfo(err),
        );
      });
      // Start template flags fetch early; .then() applies flags to
      // this.organization as soon as they arrive.
      const customerTypeId = organization.customerType?.id;
      if (customerTypeId) {
        templateFlagsPromise = this.datoClientStore.albumRepository
          .fetchCustomerTypeTemplateFlagsById(customerTypeId)
          .then(flags => {
            if (
              flags &&
              this.organization?.id === organization.id &&
              this.organization?.customerType
            ) {
              this.organization = {
                ...this.organization,
                customerType: {
                  ...this.organization.customerType,
                  ...flags,
                },
              };
            }
          })
          .catch(err => {
            logger.warn(
              'Failed to hydrate customer type template flag fields',
              extractErrorInfo(err),
            );
          });
      }
      this.rootStore.analyticsStore.setContext(
        story.id,
        organization.slug,
        organization.title,
        organization.id,
      );
      setOrganizationContext(
        organization.id,
        organization.title || organization.slug || 'Unknown',
      );
    }

    this.storyId = story.id;
    this.storyName = story.title;
    this.originalVideoAudioUrl = story.transcription?.audio?.url;
    // Populate reports null until the pipeline has probed the file (it used
    // to fabricate 180 s, which truncated mid-pending compositions —
    // PLAT-11228). Unknown stays empty so compositions take the media's
    // real length instead of a "null s" template string.
    const populatedDuration = story?.originalVideo?.video?.duration;
    this.originalVideoDuration =
      typeof populatedDuration === 'number' && populatedDuration > 0
        ? `${populatedDuration} s`
        : '';
    this.isInitialized = true;

    const op2Start = performance.now();
    // Set up transcription URLs but don't load the data yet (lazy loading)
    if (story.transcription?.elementsJson?.url) {
      this.setupTranscriptionUrls(story);
    }
    const op2End = performance.now();

    // Fire-and-forget: start waveform fetch BEFORE loadVideo (which takes ~20s).
    // Waveform only needs story.transcription.waveformData from metadata.
    if (!returnedToTheSameStory) {
      this.loadOriginalWaveformData();
    }

    const op3Start = performance.now();
    if (
      videoId &&
      this.story?.otherVideos?.find(
        v =>
          v.id === videoId || v.associatedVideos.find(av => av.id === videoId),
      )
    ) {
      await this.loadVideo(videoId);
    } else if (createFromOriginal) {
      // "Open full video in Creator Studio" — mirror the VideoSelector's
      // "Duplicate Original": create a fresh unsaved editable copy of the
      // original source (with scenes) instead of loading the published video.
      await this.createNewVideoFromSource(undefined, true);
      this.addUnsaveVideoAction();
    } else if (story.finalVideo?.id) {
      await this.loadVideo(story.finalVideo?.id);
    } else if (this.story?.otherVideos?.length) {
      const firstVideoId = this.story.otherVideos[0]?.id;
      if (firstVideoId) {
        await this.loadVideo(firstVideoId);
      } else {
        logger.warn(
          `TYPE_ERROR: First video of the story ${this.story.id} has no ID.`,
        );
      }
    } else {
      await this.createNewVideoFromSource(undefined, true);
    }
    const op3End = performance.now();

    if (returnedToTheSameStory) {
      this.rootStore.transcriptionStore.transcriptionLoadingStatus =
        TranscriptionStatus.Loaded;
    }

    const op4Start = performance.now();
    // Waveform already started above (fire-and-forget before loadVideo)
    const op4End = performance.now();

    const op5Start = performance.now();
    if (!returnedToTheSameStory) {
      // Ensure template flags are hydrated before loadKaraokePresets reads them.
      await templateFlagsPromise;
      await this.textBrandingService.loadKaraokePresets();
    }
    const op5End = performance.now();

    const op6Start = performance.now();
    if (!returnedToTheSameStory) {
      await this.preloadFaceDetections(storyId);
    }
    const op6End = performance.now();

    if (!returnedToTheSameStory) {
      // no need to wait
      this.preloadSceneDetections(storyId);
      this.preloadSpeakerData(storyId);
    }

    // PLAT-10009: seed the per-variant PublishRecord tracking from persisted
    // in-flight records so progress survives a refresh (successor to the
    // renderingVideoIds seed above; drives the live PublishRecord subscription).
    this.rootStore.publishTrackingStore.reset();
    void this.rootStore.publishTrackingStore.seedForStory(story.id);

    console.debug('initializeWithStory performance', {
      op1: `${(op1End - op1Start).toFixed(2)} ms`,
      op2: `${(op2End - op2Start).toFixed(2)} ms`,
      op3: `${(op3End - op3Start).toFixed(2)} ms`,
      op4: `${(op4End - op4Start).toFixed(2)} ms`,
      op5: `${(op5End - op5Start).toFixed(2)} ms`,
      op6: `${(op6End - op6Start).toFixed(2)} ms`,
    });
  }

  async initializeWithShowcase(showcaseSlug: string) {
    if (!this.datoClientStore.albumRepository)
      throw new Error('Album repository is not initialized');

    const showcase =
      await this.datoClientStore.albumRepository.findOneBySlug(showcaseSlug);
    if (!showcase)
      throw new Error('Showcase not found. Cannot initialize data.');
    this.organization = showcase;
    if (showcase?.id) {
      this.rootStore.analyticsStore.setContext(
        showcase.id,
        showcase.slug,
        showcase.title,
        showcase.id,
      );
      setOrganizationContext(
        showcase.id,
        showcase.title || showcase.slug || 'Unknown',
      );
    }
    // AWS stories' `originalVideo` isn't populated here; `initializeWithStory`
    // loads it.
    const storyWithVideo = showcase.stories.find(
      s => s.useAws || s.originalVideo,
    );
    if (storyWithVideo) {
      await this.initializeWithStory(storyWithVideo.id, null, showcaseSlug);
    }
  }

  async initializeData(data: {
    storyId?: string | null;
    videoId?: string | null;
    showcaseSlug?: string | null;
    fromCache?: boolean;
    createFromOriginal?: boolean;
  }) {
    const { storyId, videoId, showcaseSlug, fromCache, createFromOriginal } =
      data;

    if (!storyId && !showcaseSlug)
      throw new Error('Story id or showcase id are required');

    if (storyId) {
      await this.initializeWithStory(
        storyId,
        videoId,
        showcaseSlug,
        fromCache,
        createFromOriginal,
      );
    } else if (showcaseSlug) {
      await this.initializeWithShowcase(showcaseSlug);
    }
  }

  getResourceFieldsToPopulate(video: Video): VideoResourceField[] {
    if (video.resourcesMetadata === undefined) {
      return [];
    }
    return typedKeys(video.resourcesMetadata);
  }

  /**
   * Check if a video is a lightweight clip that needs expansion.
   * Lightweight clips have isLightweightClip=true and no persisted resources.
   */
  private isLightweightClip(video: VideoVersion): boolean {
    const clipJson = video.clipJson as ClipMetadata | undefined;
    logger.debug('isLightweightClip:check', {
      videoId: video.id,
      clipJson,
      isLightweightClip: clipJson?.isLightweightClip,
      sourceStoryId: clipJson?.sourceStoryId,
      hasVideoSourceSnapshot: !!video.resourcesMetadata?.videoSourceSnapshot,
    });
    if (!clipJson?.isLightweightClip) return false;
    if (!clipJson.sourceStoryId) return false;
    // If resources are already populated, it's been expanded
    if (video.resourcesMetadata?.videoSourceSnapshot) return false;
    return true;
  }

  async loadVideoVersion(versionId: string) {
    const videoVersion = this.currentVideoVersions?.find(
      videoVersion => videoVersion.versionId === versionId,
    );
    if (!this.story) {
      logger.warn('TYPE_ERROR: story is undefined');
      throw new Error('Story is not set');
    }
    if (videoVersion) {
      const resourcesPopulatedVideoVersion =
        await populateVideoVersionWithResources(
          this.story.id,
          videoVersion,
          this.getResourceFieldsToPopulate(videoVersion),
        );
      await this.renderVideo(
        resourcesPopulatedVideoVersion,
        false,
        DEFAULT_PREVIEW_VIDEO_RES,
      );
      this.textBrandingService.loadDefaultTemplateOnVideoLoad();
      this.resetUndoRedo();
    } else {
      logger.warn(
        `loadVideoVersion: version not found in currentVideoVersions: ${versionId}`,
      );
    }
  }

  async restoreVideoVersion(videoVersionId: string) {
    const itemVersions =
      await this.datoClientStore.datoClient?.itemVersions.restore(
        videoVersionId,
      );
    const firstVersion = itemVersions[0];
    function hasVersion(o: object): o is { versionId: string } {
      return Object.hasOwn(o, 'versionId');
    }
    if (!firstVersion || !hasVersion(firstVersion)) {
      logger.warn(
        `TYPE_ERROR: Restored item version does not contain a versionId: ${videoVersionId}`,
      );
      throw new Error(
        `Restored item version does not contain a versionId: ${videoVersionId}`,
      );
    }
    await this.loadVideoVersion(firstVersion.versionId);
  }

  async loadCurrentVideoVersionsHistory(
    page: number = 0,
    options: LoadVersionsHistoryOptions = {},
  ) {
    if (!this.currentVideo) {
      throw new Error('Cannot load version history: currentVideo not set');
    }
    if (!this.currentVideo.id) {
      logger.warn('TYPE_ERROR: currentVideo has no id');
      throw new Error('Cannot load version history: currentVideo has no id');
    }
    return this.loadVersionsHistory(this.currentVideo.id, page, options);
  }

  async loadVersionsHistory(
    videoId: string,
    page: number = 0,
    { syncLoadedVersionToHead = true }: LoadVersionsHistoryOptions = {},
  ) {
    if (!this.datoClientStore.videoRepository) {
      throw new Error('Video repository is not initialized');
    }
    try {
      this.clearVideoVersions();
      this.isVersionsHistoryLoading = true;
      const pageSize = 5;
      const { videoVersions, totalCount } =
        await this.datoClientStore.videoRepository.getVersionsByVideoId(
          videoId,
          pageSize,
          page,
          this.rootStore.userIdentityStore.isExternalUser(),
        );
      runInAction(() => {
        if (videoVersions.length > 0) {
          this.currentVideoVersions = videoVersions;
          this.currentVideoVersionsPage = page;
          const currentVideoVersion = videoVersions.find(
            videoVersion => videoVersion.meta.is_current,
          );
          if (
            syncLoadedVersionToHead &&
            this.currentVideo &&
            currentVideoVersion
          ) {
            this.currentVideo.versionId = currentVideoVersion.versionId;
          }
          this.currentVideoVersionsTotalPages = Math.ceil(
            totalCount / pageSize,
          );
        }
      });
    } catch (err) {
      logger.error('Failed to load version history', extractErrorInfo(err));
      throw err;
    } finally {
      this.isVersionsHistoryLoading = false;
    }
  }

  clearVideoVersions() {
    runInAction(() => {
      this.currentVideoVersions = undefined;
      this.currentVideoVersionsPage = 0;
      this.currentVideoVersionsTotalPages = undefined;
    });
  }

  /** PLAT-11680: gates the clip_editor_usable_creator timer. */
  setCreatorStudioMounted(mounted: boolean): void {
    this.clipEditorUsableTracker.setCreatorStudioMounted(mounted);
  }

  /** Starts the timer before the load (cold boot); the load adopts it. */
  startClipEditorUsableTimerEarly(videoId?: string, storyId?: string): void {
    this.clipEditorUsableTracker.startEarly(videoId, storyId);
  }

  clearClipEditorUsableEarlyStart(): void {
    this.clipEditorUsableTracker.clearEarlyStart();
  }

  async loadVideo(videoId: string, resetTimeline = true) {
    if (!this.datoClientStore.videoRepository) {
      throw new Error('Video repository is not initialized');
    }

    // PLAT-11911: the clip Content Studio handed over, if it's this one.
    const seed = this.rootStore.studioHandoffStore.takeSeed(videoId);

    // Handle temp clip IDs - load from in-memory optimistic data
    if (isTempClipId(videoId)) {
      return this.loadTempClip(videoId, resetTimeline);
    }

    // PLAT-11911: Content Studio's "Edit Video" reopens the last clip edited,
    // including one switched to here.
    recordLastEditedClip(this.story?.id, videoId);

    // Initialize sidebar state for this video (collapsed for new videos, restored for same video)
    this.rootStore.layoutStore.initializeSidebarForVideo(videoId);

    // Start workflow tracking for video loading
    this.currentVideoLoadingWorkflowId =
      this.rootStore.analyticsStore.startWorkflow(WorkflowType.VIDEO_LOADING, {
        videoId,
        resetTimeline,
      });

    // PLAT-11041: time until the editor is actually usable for this clip
    this.clipEditorUsableTracker.begin(videoId);

    // Share workflow ID with VideoSourceStore for tracking renderer.setSource()
    this.rootStore.videoSourceStore.currentVideoLoadingWorkflowId =
      this.currentVideoLoadingWorkflowId;

    this.isLoading = true;
    this.isError = false;
    // await until render queue drains (background save does not block clip loading)
    while (this.renderQueueing) {
      await delay(500);
    }
    this.renderVideoResLevel = null;
    try {
      if (seed) {
        // PLAT-11911: render what Content Studio already holds. Its own
        // version is set as the latest, so the existing newer-version check
        // can tell the user if the clip was saved elsewhere since.
        this.currentVideoVersions = seed.versions;
        await this.renderVideo(
          seed.video,
          resetTimeline,
          DEFAULT_PREVIEW_VIDEO_RES,
        );
        this.textBrandingService.loadDefaultTemplateOnVideoLoad();
        this.preloadNaturalDimensions();
        this.resetUndoRedo();
        void this.reconcileLoadedVideoWithHead();
        return;
      }

      await this.loadVersionsHistory(videoId);
      if (!this.currentVideoVersions) {
        logger.warn('TYPE_ERROR: currentVideoVersions is undefined');
        throw new Error('No video versions found for video id ' + videoId);
      }
      if (!this.story) {
        logger.warn('TYPE_ERROR: story is undefined');
        throw new Error('Story is not set');
      }
      const currentVideoVersion = this.currentVideoVersions.find(
        videoVersion => videoVersion.meta.is_current,
      );
      if (!currentVideoVersion) {
        logger.warn('TYPE_ERROR: currentVideoVersion is undefined');
        throw new Error(
          'No current video version found for video id ' + videoId,
        );
      }

      // Check if this is a lightweight clip that needs expansion
      const wasLightweightClip = this.isLightweightClip(currentVideoVersion);
      if (wasLightweightClip) {
        await AIClipProcessor.expandLightweightClip(
          currentVideoVersion,
          this.story,
          clipProcessorDepsFromStore(this),
        );

        // Sync expanded fields to story.otherVideos so Content Studio
        // won't re-trigger expansion if user navigates back without saving
        syncVideoToStoryEntries(
          this.story.otherVideos,
          currentVideoVersion.id,
          currentVideoVersion,
        );
      }

      // Skip populateVideoVersionWithResources if we just expanded a lightweight clip
      // because expandLightweightClip already populated the video data in memory
      const resourcesPopulatedVideoVersion = wasLightweightClip
        ? currentVideoVersion
        : await populateVideoVersionWithResources(
            this.story.id,
            currentVideoVersion,
            this.getResourceFieldsToPopulate(currentVideoVersion),
          );
      await this.renderVideo(
        resourcesPopulatedVideoVersion,
        resetTimeline,
        DEFAULT_PREVIEW_VIDEO_RES,
      );
      this.textBrandingService.loadDefaultTemplateOnVideoLoad();
      this.preloadNaturalDimensions(); // for later use
      this.resetUndoRedo();
      console.log('Video loaded');
    } catch (err) {
      console.error('error loading video', err);
      console.log('loadVideo: Set is loading:false');
      this.isLoading = false;
      this.isError = true;
    } finally {
      // this.isLoading = false;
    }
  }

  /**
   * Load a temp clip (not yet saved to Dato) from in-memory optimistic data.
   * The clip will be expanded from story data and can be edited immediately.
   */
  private async loadTempClip(
    tempId: string,
    resetTimeline: boolean,
  ): Promise<void> {
    logger.info('loadTempClip: loading from memory', { tempId });

    if (!this.story) {
      throw new Error('Story is not set');
    }

    this.isLoading = true;
    this.isError = false;

    try {
      // Find optimistic video in story.otherVideos
      const optimisticVideo = this.story.otherVideos.find(v => v.id === tempId);
      if (!optimisticVideo) {
        throw new Error(`Temp clip not found in story: ${tempId}`);
      }

      // Build synthetic VideoVersion from optimistic data
      // The optimistic video contains all needed fields from the clip producer
      // Use double assertion since OtherVideo is a subset of Video
      // Ensure required Video fields have safe defaults
      const syntheticVersion = {
        ...optimisticVideo,
        id: tempId,
        versionId: `temp-version-${tempId}`,
        meta: {
          is_current: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        },
        type: 'video',
        editor: null,
        // Required Video fields that OtherVideo doesn't include
        extraElementData: {},
        transcriptionChanges: [],
        punchList: [],
      } as unknown as VideoVersion;

      this.currentVideoVersions = [syntheticVersion];

      // Expand the lightweight clip (generates videoSource, transcriptionSnapshot, etc.)
      await AIClipProcessor.expandLightweightClip(
        syntheticVersion,
        this.story,
        clipProcessorDepsFromStore(this),
      );

      // Render the expanded video
      await this.renderVideo(
        syntheticVersion,
        resetTimeline,
        DEFAULT_PREVIEW_VIDEO_RES,
      );
      this.textBrandingService.loadDefaultTemplateOnVideoLoad();
      this.resetUndoRedo();

      logger.info('loadTempClip: complete', { tempId });
    } catch (err) {
      console.error('error loading temp clip', err);
      this.isLoading = false;
      this.isError = true;
      throw err;
    }
  }

  async loadVideoWithoutRendering(videoId: string, loadElements = false) {
    const { videoVersions } =
      await this.datoClientStore.videoRepository.getVersionsByVideoId(
        videoId,
        1,
      );
    this.currentVideoVersions = videoVersions;

    const currentVideoVersion = this.currentVideoVersions?.find(
      videoVersion => videoVersion.meta.is_current,
    );

    if (!currentVideoVersion) {
      logger.warn('TYPE_ERROR: currentVideoVersion is undefined');
      throw new Error('No current video version found for video id ' + videoId);
    }

    if (!this.story) {
      logger.warn('TYPE_ERROR: story is undefined');
      throw new Error('Story is not set');
    }

    this.currentVideo = await populateVideoVersionWithResources(
      this.story.id,
      currentVideoVersion,
      this.getResourceFieldsToPopulate(currentVideoVersion),
    );
    this.assignValidVideoSourceUrlsAndOptimize(DEFAULT_PREVIEW_VIDEO_RES);
    if (loadElements) {
      await this.loadVideoElementsWithoutRendering(this.currentVideo);
    }
  }

  abortIfNotReady() {
    if (!this.isVideoCreatorReady) {
      throw new Error('Video creator is not ready');
    }
  }

  private async performAutoFrameNewAspectRatio(
    newAspectRatio: Video['aspectRatio'],
  ): Promise<void> {
    await autoFrameNewAspectRatio(newAspectRatio, {
      editor: this.rootStore.editorStore.editor,
      editorLayoutConfig: this.rootStore.editorStore.getPreviewLayoutConfig(),
      faceDetections: this.faceDetections,
      sceneManipulationLimit: this.flags.sceneManipulationLimit ?? 0,
      preloadFaceDetections: this.preloadFaceDetections.bind(this),
      storyId: this.storyId,
      autoFramingStrategy: this.flags.autoFramingStrategy,
      shots: this.sceneDetections,
      naturalMediaDimensions: await this.getNaturalMediaDimensions(),
      // Carries a reframe-modal correction into the new aspect ratio.
      speakerLabelToFaceLabels: this.rootStore.speakerFaceMapStore.map,
    });
  }

  async loadVideoWithAspectRatio(aspectRatio: Video['aspectRatio']) {
    this.abortIfNotReady();
    if (!this.currentVideo) {
      throw new Error('Cannot change aspect ratio: currentVideo not set');
    }
    this.isLoading = true;
    const parentVideo = this.currentVideo.parentVideo || this.currentVideo;
    const currentAspectRatio = this.currentVideo.aspectRatio;
    const associatedVideo =
      parentVideo?.aspectRatio === aspectRatio
        ? parentVideo
        : parentVideo.associatedVideos.find(
            video => video.aspectRatio === aspectRatio,
          );

    const activeElement = this.getActiveElement();
    this.transitionInProgress = 'changingAspectRatio';

    if (this.activeElementIds.length) {
      await this.setActiveElements();
    }

    if (associatedVideo) {
      const nowLoadedExistingVideoStart = performance.now();
      if (!associatedVideo.id) {
        logger.warn('TYPE_ERROR: associatedVideo has no id', associatedVideo);
        throw new Error(
          'Cannot change aspect ratio: associated video has no id',
        );
      }

      await this.loadVideo(associatedVideo.id);

      this.transitionInProgress = null;
      logger.info('Change aspect ratio: leg 1 - video already exists', {
        'loading existing video took (ms)':
          performance.now() - nowLoadedExistingVideoStart,
      });
      return;
    }

    const nowCreateAssociatedVideoStart = performance.now();
    let nowChangeAspectRatioCommandStart;

    await this.createAssociatedVideo(this.currentVideo.title, aspectRatio);

    nowChangeAspectRatioCommandStart = performance.now();

    // Capture current dimensions BEFORE adjusting to new aspect ratio
    const standardDimensionsPrev =
      this.getStandardDimensionsForAspectRatio(currentAspectRatio);

    this.adjustCurrentVideoWidthToAspectRatio(aspectRatio);

    const karaokeProvider =
      this.textBrandingService.createKaraokePresetProvider();
    const variantService = new KaraokeTemplateVariantService({
      brandingService: this.textBrandingService,
      albumRepository: this.datoClientStore.albumRepository,
    });
    // original video dimensions hardcoded to 16:9
    this.rootStore.editorStore.editor.commandChangeAspectRatio(
      {
        width: VideoDimensionsWidth.AR_16_9,
        height: VideoDimensionsHeight.AR_16_9,
        aspectRatio: AspectRatio.AR_16_9,
      },
      {
        width: standardDimensionsPrev.width,
        height: standardDimensionsPrev.height,
        aspectRatio: currentAspectRatio,
      },
      {
        width: this.currentVideo.videoSource.width,
        height: this.currentVideo.videoSource.height,
        aspectRatio,
      },
      await this.getNaturalMediaDimensions(),
      this.textProducer.getBasicTextSettingByAspectRatio(aspectRatio),
      this.karaokeProducer.getKaraokeTextSettingByAspectRatio(aspectRatio),
      karaokeProvider,
      variantService,
    );
    // important in case we navigate to a different aspect ratio without saving: 16/9 -> 9/16 (discard) -> 1/1
    this.currentVideo.videoSource =
      this.rootStore.editorStore.exportProject().sources.videoSource;

    // Persist any template modifications tracked during aspect ratio change
    if (this.organization) {
      const configIdToTemplateIdMap =
        await variantService.ensureVariantsForConfigs(aspectRatio);
      variantService.applyTemplateVariantUpdates(
        this.rootStore.editorStore.editor,
        this.rootStore.karaokeStore,
        configIdToTemplateIdMap,
      );
    }

    const nowAfterChangeCleanupStart = performance.now();
    this.textBrandingService.loadDefaultTemplateOnVideoLoad();
    if (activeElement) {
      await this.setActiveElements(activeElement.source.id);
    }
    this.transitionInProgress = null;

    // Strip template elements so they can be re-applied fresh for the new aspect ratio.
    // Templates have per-aspect configs; scaling from the old aspect produces wrong results.
    const activeVideoTemplateId = this.stripTemplateElementsForNewAspect();
    if (activeVideoTemplateId) {
      if (this.reapplyTemplate) {
        await this.reapplyTemplate(activeVideoTemplateId);
      } else {
        this.pendingTemplateReapplyId = activeVideoTemplateId;
      }
    }

    // Auto-frame newly created aspect ratio if conditions are met
    const nowAutoFramingStart = performance.now();
    await this.performCreateScenesFromDetection();
    if (this.flags.enableAutoFraming) {
      await this.performAutoFrameNewAspectRatio(aspectRatio);
    }

    const nowAutoFramingEnd = performance.now();
    logger.info('Auto-framing for new aspect ratio', {
      aspectRatio,
      'took (ms)': nowAutoFramingEnd - nowAutoFramingStart,
    });

    if (nowChangeAspectRatioCommandStart) {
      logger.info("Change aspect ratio: leg 2 - video doesn't exist yet", {
        'creating associated video took (ms)':
          nowChangeAspectRatioCommandStart - nowCreateAssociatedVideoStart,
        'running change aspect ratio command took (ms)':
          nowAfterChangeCleanupStart - nowChangeAspectRatioCommandStart,
        'doing after change cleanup took (ms)':
          performance.now() - nowAfterChangeCleanupStart,
      });
    }
  }

  /**
   * Strips template elements from the timeline after a new aspect ratio video
   * is created. Templates have per-aspect configs, so the old elements must be
   * removed and the template re-applied fresh for the new aspect ratio.
   *
   * @returns The active video template ID if elements were stripped, so the
   * caller can trigger re-application via the reapplyTemplate callback.
   */
  private stripTemplateElementsForNewAspect(): string | undefined {
    if (!this.currentVideo) return;

    const settings = getElementMetaObject(
      this.currentVideo,
      VIDEO_TEMPLATE_SETTINGS_KEY,
    );

    if (!settings) return;

    const activeVideoTemplateId = settings.activeVideoTemplateId;
    if (typeof activeVideoTemplateId !== 'string' || !activeVideoTemplateId)
      return;

    if (!Array.isArray(settings.videoTemplateElements)) return;

    const templateElements: Array<{ id: string; type: string }> =
      settings.videoTemplateElements;

    const editor = this.rootStore.editorStore.editor;

    logger.info(
      'Stripping template elements for fresh aspect ratio application',
      {
        activeVideoTemplateId,
        elementCount: templateElements.length,
      },
    );

    // Remove each template element from the timeline
    for (const element of templateElements) {
      if (element.type === 'captions') {
        // Remove the entire karaoke track
        editor.timeline.removeTrack(String(KARAOKE_TRACK_NUMBER));
      } else if (element.id) {
        editor.timeline.removeElement(element.id.toString());
      }
    }

    editor.markDirty('strip_template_for_aspect');

    // Shift remaining elements back to t=0 (reverses intro card ripple)
    const allElements = [...editor.timeline.allElements];
    if (allElements.length > 0) {
      const earliestTime = allElements.reduce(
        (min, el) => Math.min(min, el.time),
        Infinity,
      );
      if (earliestTime > 0) {
        editor.commandShiftMultipleElements(
          allElements.map(e => e.id),
          -earliestTime,
          false,
          false,
        );
      }

      // Update duration to match remaining content
      const updatedElements = [...editor.timeline.allElements];
      const maxEndTime = updatedElements.reduce(
        (max, el) => Math.max(max, el.end_time),
        0,
      );
      if (maxEndTime > 0) {
        this.duration = maxEndTime;
      }
    }

    // Clean up per-element metadata
    for (const element of templateElements) {
      deleteElementMeta(this.currentVideo, element.id.toString());
    }

    // Delete the template settings entirely (clean slate for re-application)
    deleteElementMeta(this.currentVideo, VIDEO_TEMPLATE_SETTINGS_KEY);

    // Sync videoSource after modifications
    this.currentVideo.videoSource =
      this.rootStore.editorStore.exportProject().sources.videoSource;
    return activeVideoTemplateId;
  }

  async copyVideo(
    videoIdOrItem: string | VideoItem,
    newTitle: string,
    sourcePlatform: Video['sourcePlatform'],
  ) {
    try {
      const lastActionJson = JSON.stringify({
        editor: this.rootStore.userIdentityStore.currentEditor,
        date: new Date().getTime(),
        type: 'save',
      });
      const { id: newVideoId } =
        await this.datoClientStore.videoRepository.copyParentAndAssociatedVideos(
          videoIdOrItem,
          newTitle,
          sourcePlatform,
          lastActionJson,
        );
      await this.loadVideo(newVideoId);
      await this.addCurrentToStoryVideos();
      await this.loadCurrentVideoVersionsHistory(0);
    } catch (err) {
      console.error('Error copying video', err);
    }
  }

  async createNewVideoFromSource(
    newTitle?: string,
    createScenes?: boolean,
    newSource?: unknown,
    transcriptionChanges?: TranscriptChange[],
    transcriptionSnapshot?: Pick<TranscriptData, 'elements'>,
    karaokeData?: Video['karaokeData'],
    extraElementData?: Video['extraElementData'],
    sourcePlatform: SourcePlatform = 'creator-studio',
    aspectRatio?: Video['aspectRatio'],
    // false for builds that aren't a clip open (e.g. AI clip production setup)
    shouldStartUsableTimer = true,
  ) {
    // Start workflow tracking for video loading
    this.currentVideoLoadingWorkflowId =
      this.rootStore.analyticsStore.startWorkflow(WorkflowType.VIDEO_LOADING, {
        newTitle,
        sourcePlatform,
        aspectRatio,
      });

    // PLAT-11041: time until the editor is actually usable for this clip
    if (shouldStartUsableTimer) {
      this.clipEditorUsableTracker.begin();
    }

    // Share workflow ID with VideoSourceStore for tracking renderer.setSource()
    this.rootStore.videoSourceStore.currentVideoLoadingWorkflowId =
      this.currentVideoLoadingWorkflowId;

    if (!this.story) {
      logger.warn('TYPE_ERROR: story is undefined');
      throw new Error('Cannot create new video: story not set');
    }

    // If no transcriptionSnapshot provided, use original transcription from store
    if (!transcriptionSnapshot) {
      const originalTranscription =
        this.rootStore.transcriptionStore.getOriginalTranscription();
      if (originalTranscription?.elements) {
        transcriptionSnapshot = {
          elements: toJS(originalTranscription.elements),
        };
      }
    }

    const video: CurrentLoadedVideo = {
      versionId: `unsaved-version-${uuid().substring(0, 8)}`,
      title: newTitle || this.story.title,
      videoSource:
        newSource ||
        this.getDefaultSource({ videoRes: DEFAULT_PREVIEW_VIDEO_RES }),
      videoStatus: VideoStatus.Editing,
      extraElementData: extraElementData || {},
      transcriptionChanges: transcriptionChanges || [],
      transcriptionSnapshot,
      karaokeData,
      associatedVideos: [],
      aspectRatio: aspectRatio || this.getDefaultAspectRatio(),
      sourcePlatform,
    };
    this.currentVideoVersions = [];
    this.rootStore.editorStore?.resetMigrationState();
    await this.renderVideo(video, true, DEFAULT_PREVIEW_VIDEO_RES);
    this.resetUndoRedo();

    // Create scenes if requested
    if (createScenes) {
      await this.performCreateScenesFromDetection();
    }
  }

  /**
   * Creates scenes from the first available scene detection for the current story.
   * Uses preloaded scene detections if available, otherwise fetches from API.
   */
  private async performCreateScenesFromDetection(): Promise<void> {
    const storyId = this.story?.id;
    const editor = this.rootStore.editorStore?.editor;

    // Try to load scenes if not already preloaded
    if (!this.sceneDetections || this.sceneDetections.length === 0) {
      if (storyId) {
        await this.preloadSceneDetections(storyId);
      }
    }

    const naturalMediaDimensions = await this.getNaturalMediaDimensions();

    await createScenesFromDetection(
      storyId,
      editor,
      this.sceneDetections,
      this.preloadSceneDetections.bind(this),
      naturalMediaDimensions,
    );
  }

  prepareAsCurrentLoadedVideo(
    originalVideo: Video,
    title: string,
    sourcePlatform: SourcePlatform,
  ): CurrentLoadedVideo {
    const source =
      deepClone(originalVideo.videoSource) || this.getDefaultSource();
    let { elements } = source;

    const cloneStart = performance.now();
    const clonedExtraElementData = safeClone(
      originalVideo.extraElementData,
      'extraElementData',
    );
    const clonedKaraokeData = safeClone(
      originalVideo.karaokeData,
      'karaokeData',
    );
    const clonedTranscriptionChanges = safeClone(
      originalVideo.transcriptionChanges,
      'transcriptionChanges',
    );
    const clonedTranscriptionSnapshot = safeClone(
      originalVideo.transcriptionSnapshot,
      'transcriptionSnapshot',
    );
    const clonedScenes = safeClone(originalVideo.scenes, 'scenes');
    const cloneDuration = performance.now() - cloneStart;

    if (cloneDuration > 50) {
      logger.warn('prepareAsCurrentLoadedVideo:slow-clone', {
        durationMs: cloneDuration.toFixed(1),
        transcriptElements:
          originalVideo.transcriptionSnapshot?.elements?.length,
        extraElementDataKeys: Object.keys(originalVideo.extraElementData ?? {})
          .length,
      });
    }

    const video = {
      title,
      videoSource: {
        ...source,
        // duration: clip.duration,
        duration: undefined,
        elements,
      },
      videoStatus: VideoStatus.Editing,
      extraElementData: clonedExtraElementData,
      karaokeData: clonedKaraokeData,
      transcriptionChanges: clonedTranscriptionChanges,
      transcriptionSnapshot: clonedTranscriptionSnapshot,
      associatedVideos: [],
      aspectRatio: originalVideo.aspectRatio || this.getDefaultAspectRatio(),
      sourcePlatform,
      scenes: clonedScenes,
    };
    return video;
  }

  async createAssociatedVideo(newTitle: string, aspectRatio: AspectRatio) {
    if (!this.currentVideo) {
      throw new Error('Cannot create associated video: currentVideo not set');
    }
    this.rootStore.editorStore.refreshKaraoke();
    await this.prepareCurrentVideoForSave(true);
    const parentVideo = this.currentVideo.parentVideo || this.currentVideo;
    const video = this.getSafeCurrentVideoCopy();
    video.associatedVideos = [];
    delete video.id;
    // A brand-new variant has never been published — drop the source variant's
    // publish state so it isn't mistaken for a published render (e.g. the
    // FINAL status badge / download button reading an inherited
    // `videoFilePrimary`). The persisted variant has none of these until it's
    // actually published. See `resolveAspectRatioVariant`.
    delete video.videoFilePrimary;
    delete video.lastPublishJson;
    delete video._publishedAt;
    // Clear the thumbnail — it was rendered for the source AR and would appear
    // stretched in the new one. A fresh thumbnail will be generated on publish.
    delete video.thumbnail;
    video.versionId = `unsaved-version-${uuid().substring(0, 8)}`;
    video.title = newTitle;
    video.aspectRatio = aspectRatio;
    video.parentVideo = parentVideo;
    this.currentVideoVersions = [];
    await this.renderVideo(video, false, DEFAULT_PREVIEW_VIDEO_RES);
    this.resetUndoRedo();
  }

  private getSafeCurrentVideoCopy(): CurrentLoadedVideo {
    if (!this.currentVideo) {
      throw new Error('Cannot get a safe copy: currentVideo not set');
    }
    const copy = { ...this.currentVideo } as CurrentLoadedVideo;

    // Fresh, flattened arrays so the copy is acyclic before it's pushed into
    // the deep-observable undo stack or used to seed a variant. The shallow
    // spread otherwise shares the associatedVideos ref and keeps the
    // parent<->child cycle. Copy parentVideo so we never mutate the live one.
    copy.associatedVideos = (this.currentVideo.associatedVideos ?? []).map(
      flattenAssociatedVideo,
    );
    if (copy.parentVideo) {
      copy.parentVideo = {
        ...copy.parentVideo,
        associatedVideos: (copy.parentVideo.associatedVideos ?? []).map(
          flattenAssociatedVideo,
        ),
      };
    }

    this.syncPunchListToVideo(copy);
    return copy;
  }

  async renameVideo(videoId: string, newTitle: string) {
    if (!this.story) {
      logger.warn('TYPE_ERROR: story is undefined');
      throw new Error('Cannot rename video: story not set');
    }

    if (videoId === 'original_story') {
      await this.updateStory({
        id: this.story.id,
        title: newTitle,
      });
      this.story.title = newTitle;
      return;
    }

    // A rename writes the title and nothing else. For the loaded clip it used
    // to run the full background save, which waited on a temp ID (and was
    // parked behind the initial save) while the record was created with the
    // old name (PLAT-11777). Local copies land first so no surface reverts
    // while the write is in flight; a failed write puts them back.
    const previousTitle = this.applyTitleLocally(videoId, newTitle);
    // A full save of this clip serialised its title when it started and writes
    // it seconds later, after the resource uploads — landing first means being
    // overwritten. A rejected wait (failed save, switched clip) still writes.
    if (this.isBackgroundSaving && this.currentVideo?.id === videoId) {
      await this.waitForBackgroundSave().catch(() => undefined);
    }
    try {
      await this.datoClientStore.videoRepository?.updateVideo({
        id: videoId,
        title: newTitle,
      });
    } catch (error) {
      if (previousTitle !== undefined) {
        this.applyTitleLocally(videoId, previousTitle);
      }
      throw error;
    }
  }

  /**
   * Writes `title` onto every in-memory copy of the clip (story list, loaded
   * clip, its parent) and returns the title it replaced. The id can also be a
   * per-aspect-ratio variant, which every copy carries nested under its parent
   * rather than as a list entry of its own.
   */
  private applyTitleLocally(
    videoId: string,
    title: string,
  ): string | undefined {
    let previousTitle: string | undefined;
    let variantParentId: string | undefined;
    const rememberPrevious = (value: string) => {
      if (previousTitle === undefined) previousTitle = value;
    };
    if (this.story?.otherVideos) {
      this.story.otherVideos = this.story.otherVideos.map(v => {
        if (v.id === videoId) {
          rememberPrevious(v.title);
          return { ...v, title };
        }
        const index = (v.associatedVideos ?? []).findIndex(
          av => av.id === videoId,
        );
        if (index < 0) return v;
        variantParentId = v.id;
        const associatedVideos = [...v.associatedVideos];
        rememberPrevious(associatedVideos[index].title);
        associatedVideos[index] = { ...associatedVideos[index], title };
        return { ...v, associatedVideos };
      });
    }
    if (this.currentVideo?.id === videoId) {
      rememberPrevious(this.currentVideo.title);
      this.currentVideo.title = title;
    }
    if (this.currentVideo?.parentVideo?.id === videoId) {
      rememberPrevious(this.currentVideo.parentVideo.title);
      this.currentVideo.parentVideo.title = title;
    }
    this.rootStore.clipPreviewStore.invalidate(
      videoId,
      variantParentId,
      this.currentVideo?.parentVideo?.id,
    );
    return previousTitle;
  }

  async hideVideo(videoId?: string) {
    if (videoId === 'original_story' || !videoId) {
      return;
    }
    await this.updateVideoField('isHidden', true, videoId);
  }

  async updateIsClientReady(isClientReady: boolean, videoId?: string) {
    if (videoId === 'original_story' || !videoId) {
      return;
    }
    await this.updateVideoField('isClientReady', isClientReady, videoId);
  }

  async updateReviewStatus(
    status: ClipReviewStatus,
    videoId?: string,
  ): Promise<boolean> {
    if (videoId === 'original_story' || !videoId) {
      return false;
    }
    return this.updateVideoField('reviewStatus', status, videoId);
  }

  async updateClipAssignee(
    assignee: string,
    videoId?: string,
  ): Promise<boolean> {
    if (videoId === 'original_story' || !videoId) {
      return false;
    }
    return this.updateVideoField('assignee', assignee, videoId);
  }

  private async updateVideoField<
    K extends keyof AssociatedVideo & keyof CurrentLoadedVideo,
  >(key: K, value: AssociatedVideo[K], videoId: string): Promise<boolean> {
    try {
      if (this.currentVideo && this.currentVideo.id === videoId) {
        this.currentVideo[key] = value;
        await this.saveCurrentAndParentVideos(false, false);
      } else {
        await this.datoClientStore.videoRepository?.updateVideo({
          id: videoId,
          [key]: value,
        });
        if (this.currentVideo?.parentVideo?.id === videoId) {
          this.currentVideo.parentVideo[key] = value;
        }
      }
    } catch (err) {
      logger.error(
        `Failed updating video ${videoId} field [${key}]: ${value}`,
        extractErrorInfo(err),
      );
      return false;
    }

    if (!this.story) {
      logger.warn('TYPE_ERROR: story is undefined');
      throw new Error('Cannot update video field: story not set');
    }

    const updatedVideos =
      this.story.otherVideos?.map(v => {
        if (videoId === v.id) return { ...v, [key]: value };
        return v;
      }) || [];
    this.story.otherVideos = updatedVideos;
    return true;
  }

  private updateCurrentRenderingStatus() {
    this.renderingStatus = this.currentVideo?.videoStatus || VideoStatus.None;
  }

  isOriginalVideoElement(elementSourceInput: unknown) {
    const elementTypeSchema = z.enum([
      'video',
      'composition',
      'image',
      'text',
      'audio',
      'shape',
    ]);
    const videoElementSchema = z.object({
      type: elementTypeSchema,
      source: z.string().optional(),
      elements: z
        .array(
          z.object({
            type: elementTypeSchema,
            source: z.string().optional(),
          }),
        )
        .optional(),
    });

    const parseResult = videoElementSchema.safeParse(elementSourceInput);
    if (!parseResult.success) {
      logger.warn('TYPE_ERROR: elementSourceInput is not valid', {
        errors: parseResult.error.errors,
      });

      return false;
    }

    let elementSource = parseResult.data;

    if (
      elementSource.type !== 'video' &&
      elementSource.type !== 'composition'
    ) {
      return false;
    }
    if (elementSource.type === 'composition') {
      const videoElement = elementSource.elements?.find(
        el => el.type === 'video',
      );

      if (!videoElement) {
        return false;
      }
      elementSource = videoElement;
    }

    // TODO: replace this.story?.selfHostedVideo with this.story?.originalVideo
    // and remove datoHostedVideo when migration is done
    const selfHostedVideo = this.story?.selfHostedVideo;
    const datoHostedVideo = this.story?.datoHostedVideo;
    // Check for mux, dato or cloudfront urls
    if (!elementSource.source) {
      logger.warn('TYPE_ERROR: elementSource.source is undefined', {
        elementSource,
      });
      return false;
    }
    // PLAT-11228: the session-local preview stands in for the original while
    // renditions are pending — the timeline (track type, frame thumbnails,
    // linked-audio waveform) must keep treating it as the original video.
    // The synthetic URL embeds the story id, so this stays story-scoped.
    if (
      this.story?.id &&
      elementSource.source === buildLocalCanvasUrl(this.story.id)
    )
      return true;

    if (
      selfHostedVideo?.url &&
      pathStartsWithDirectory(elementSource.source, selfHostedVideo.url)
    )
      return true;

    if (datoHostedVideo?.url && elementSource.source === datoHostedVideo.url)
      return true;
    if (
      datoHostedVideo?.video?.mp4Url &&
      elementSource.source === datoHostedVideo.video.mp4Url
    )
      return true;
    return false;
  }

  private assignValidVideoSourceUrlsAndOptimize(resLevel: VideoResolution) {
    if (!this.currentVideo || !this.story) {
      throw new Error(
        'Current video or story is not initialized to optimize video source',
      );
    }
    this.replaceWithValidVideoSourceUrl(
      this.currentVideo.videoSource,
      resLevel,
    );
  }

  public getValidVideoSourceUrlForResLevel(
    resLevel: VideoResolution,
  ): string | undefined {
    if (!this.story) {
      throw new Error(
        'Current video or story is not initialized to optimize video source',
      );
    }
    let sourceVideoUrl;

    if (resLevel === VideoResolution.HD) {
      sourceVideoUrl = this.story.originalVideo.video.mp4UrlHd;
    } else if (resLevel === VideoResolution.High) {
      sourceVideoUrl = this.story.originalVideo.video.mp4UrlHigh;
    } else if (resLevel === VideoResolution.Low) {
      sourceVideoUrl = this.story.originalVideo.video.mp4UrlLow;
    } else if (resLevel === VideoResolution.Medium) {
      sourceVideoUrl = this.story.originalVideo.video.mp4UrlMedium;
    }

    if (!sourceVideoUrl) {
      // PLAT-11224: while renditions are still being produced, a missing URL
      // means "not yet", not "never" — withhold the fallback so the canvas
      // shows a placeholder instead of pulling the multi-GB unprocessed
      // original into the renderer. Permanent absence (legacy stories,
      // failed pipelines) still falls back as before.
      if (isCompressedRenditionPending(this.story)) {
        logger.debug(
          'Requested rendition still processing — withholding original fallback',
          { storyId: this.story.id, resLevel },
        );
        return undefined;
      }
      logger.warn('No video source found for optimization, using original', {
        storyId: this.story.id,
        resLevel,
      });
      sourceVideoUrl = this.story.originalVideo.url;
    }
    return sourceVideoUrl;
  }

  public replaceWithValidVideoSourceUrl(
    source: CurrentLoadedVideo['videoSource'],
    resLevel: VideoResolution,
  ) {
    if (!source?.elements) {
      return source;
    }
    const videoSourceUrl = this.getValidVideoSourceUrlForResLevel(resLevel);
    if (!videoSourceUrl) {
      // Renditions pending (PLAT-11224) — keep whatever URLs the source
      // already carries rather than blanking element sources.
      return source;
    }
    source.elements.forEach((element: Record<string, unknown>) => {
      if (this.isOriginalVideoElement(element)) {
        const videoElements = pickUnderlyingVideoElements(element);
        for (const videoElement of videoElements) {
          videoElement.source = videoSourceUrl;
        }
      }
    });
    return source;
  }

  public getStandardDimensionsForAspectRatio(aspectRatio: AspectRatio): {
    width: number;
    height: number;
  } {
    return getStandardDimensionsForAspectRatio_(aspectRatio);
  }

  private setOriginalVideoFramerate(videoSource: VideoSource) {
    let storyVideoFramerate = this.getSourceVideoFrameRate();

    if (!storyVideoFramerate || typeof storyVideoFramerate !== 'number') {
      storyVideoFramerate = DEFAULT_INIT_FRAMERATE;
    }
    // Creatomate only supports <= 60fps
    if (storyVideoFramerate && videoSource) {
      this.timelineStore.framerate = Math.min(storyVideoFramerate, 60);
      videoSource.frame_rate = Math.min(storyVideoFramerate, 60);
    }
  }

  public setStandardDimensions(
    videoSource: CurrentLoadedVideo['videoSource'],
    aspectRatio: AspectRatio,
  ) {
    let standardDimensions =
      this.getStandardDimensionsForAspectRatio(aspectRatio);
    if (standardDimensions) {
      if (
        videoSource.width !== standardDimensions.width ||
        videoSource.height !== standardDimensions.height
      ) {
        console.warn(
          `Aspect ratio dimensions mismatch: ${videoSource.width}x${videoSource.height} for ${aspectRatio}`,
          {
            story: this.story?.id,
            organization: this.organization,
            video: this.currentVideo,
            aspectRatio,
          },
        );
        console.log(
          'Standard dimensions set',
          aspectRatio,
          `${videoSource.width}x${videoSource.height}`,
        );
        videoSource.width = standardDimensions.width;
        videoSource.height = standardDimensions.height;
      }
    }
  }

  private async renderVideo(
    video: CurrentLoadedVideo,
    resetTimeline = true,
    resLevel: VideoResolution = VideoResolution.Medium,
  ) {
    if (this.activeElementIds.length) {
      await this.setActiveElements();
    }

    // Abort any pending publish operations waiting on background save
    this._rejectPendingBackgroundSaveWaiters();

    this.currentVideo = video;
    this.updateCurrentRenderingStatus();

    // PLAT-11228: layout math that ran against the audio-first element's
    // zero video dimensions bakes "NaN%" into positional props — the
    // renderer rejects the entire setSource ("Video.x: Expected a number"),
    // silently keeping its last-good (often empty) composition: black
    // canvas, dead playback. Sanitize before every render. (The outbound
    // renderer sync repairs live edits the same way — see VideoSourceStore.)
    const repairedProps = repairNaNLayoutProps(video.videoSource?.elements);
    if (repairedProps.length) {
      logger.debug('Audio-first NaN layout repaired', { repairedProps });
    }

    // PLAT-11228: audio-first elements are composed (and may have been saved)
    // before the story's video metadata existed — without a duration
    // Creatomate falls back to a 5s default and a 9-minute story renders a
    // 5-second timeline. renderVideo is the choke point for every load path
    // (fresh compose, saved video, version), so repair them here from the
    // audio file's real length before first render.
    const audioFirstUrl = this.story?.transcription?.audio?.url;
    if (audioFirstUrl && Array.isArray(video.videoSource?.elements)) {
      const renditionPending = isCompressedRenditionPending(this.story);
      // A mid-pending autosave persists the audio-first composition; opened
      // again after the rendition landed, the unlock poll never runs, so
      // the swap to real video has to happen here at the load choke point.
      const readyVideoUrl = renditionPending
        ? null
        : this.getValidVideoSourceUrlForResLevel(resLevel);
      for (const element of video.videoSource.elements as any[]) {
        // Splits/scenes wrap the media in composition elements — handle the
        // underlying video elements wherever they live.
        for (const inner of pickUnderlyingVideoElements(element)) {
          if (renditionPending) {
            // While renditions are pending, original-video elements (the
            // upload flow pre-creates a draft video carrying one) have no
            // playable source — convert them to audio-first elements before
            // first render rather than letting the renderer choke on them.
            if (
              inner?.type === 'video' &&
              this.isOriginalVideoElement(inner) &&
              !this.isAudioFirstElement(inner, audioFirstUrl) &&
              // A local-canvas source now matches isOriginalVideoElement —
              // the reset below owns reverting it (cross-session only), so
              // this conversion must never touch it.
              !isLocalCanvasSource(inner?.source)
            ) {
              inner.source = audioFirstUrl;
              logger.debug('Audio-first conversion at render', {
                id: inner?.id,
              });
            }
            // PLAT-11228: a persisted local-canvas source is only playable in
            // the session (and renderer) that cached it — repoint loads from
            // OTHER sessions to the audio-first source; the poll re-upgrades
            // once this renderer has the bytes cached. The story-id guard is
            // load-bearing: the upgrade's own renderVideo passes through here
            // while renditions are still pending, and without it this reset
            // immediately reverted the just-applied local source (veil/black
            // flicker, local video never shown).
            if (
              isLocalCanvasSource(inner?.source) &&
              this.localCanvasAttemptStoryId !== this.story?.id
            ) {
              inner.source = audioFirstUrl;
              inner.id = uuid();
              this.localCanvasStoryId = null;
              this.localCanvasAttemptStoryId = null;
              logger.debug('Local-canvas source reset at render', {
                id: inner?.id,
              });
            }
          } else if (
            readyVideoUrl &&
            // Load-time only (stateReady is false until the renderer reports
            // its first populated state): a mid-session re-render (split
            // rebuild, aspect change) may have sibling flows holding element
            // ids — re-identifying under them corrupts the composition. The
            // unlock poll's upgradeAudioFirstComposition owns the mid-session
            // swap.
            !this.stateReady &&
            (this.isAudioFirstElement(inner, audioFirstUrl) ||
              isLocalCanvasSource(inner?.source))
          ) {
            // Fresh id forces the renderer to recreate the media — it caches
            // per-element media state, and a source-only change keeps the
            // mp3's zero video dimensions (black canvas).
            inner.source = readyVideoUrl;
            inner.id = uuid();
            logger.debug('Audio-first upgrade at render', { id: inner?.id });
          }
          if (
            this.isAudioFirstElement(inner, audioFirstUrl) &&
            (inner?.duration == null ||
              String(inner.duration).includes('undefined'))
          ) {
            try {
              const seconds = await getAudioDurationFromUrl(audioFirstUrl);
              inner.duration = `${seconds} s`;
              inner.media_duration = seconds;
              logger.debug('Audio-first duration probed', { seconds });
            } catch (error) {
              logger.warn('Audio-first duration probe failed', {
                error: extractErrorInfo(error),
              });
            }
          }
        }
      }
    }

    this.punchListManager.punchListItems = this.currentVideo.punchList || [];
    const loadedSources = await this.loadVideoElementsWithoutRendering(video);

    this.setStandardDimensions(
      this.currentVideo.videoSource,
      this.currentVideo.aspectRatio,
    );

    this.setOriginalVideoFramerate(this.currentVideo.videoSource);

    // render video
    this.assignValidVideoSourceUrlsAndOptimize(resLevel);
    const videoSourceUrl = this.getValidVideoSourceUrlForResLevel(resLevel);

    if (this.currentVideo) {
      if (resetTimeline && this.renderer?.ready) {
        this.stateReady = false;
        await this.setTime(0, true);
      }
      if (this.renderer && videoSourceUrl) {
        this.renderer.warmAsset(videoSourceUrl).catch(err => {
          logger.warn(
            '[renderVideo] Failed to cache main video source',
            extractErrorInfo(err),
          );
        });
      }
      const source = this.currentVideo.videoSource;

      // Hydrate main video elements with media duration
      // TODO: handle if hydrate is not possible?
      const updatedSource = hydrateOriginalMediaDuration(source, this);

      console.log('Injecting Fonts (render Video)');
      if (!this.customFontsService) {
        logger.warn('TYPE_ERROR: customFontsService is not initialized');
        throw new Error('Custom fonts service is not initialized');
      }
      let sourceWithFontsInjected =
        this.customFontsService.getCustomFontsSource(updatedSource);

      console.log('Fonts injected, setting source (render Video)');

      if (this.renderer && this.rootStore.editorStore.isWindowingActive) {
        const refs = extractAllAssetRefs(sourceWithFontsInjected);
        if (refs.length) {
          const playhead = resetTimeline ? 0 : this.time;
          this.renderer.warmAssets(refs, playhead).catch(err => {
            logger.warn(
              '[renderVideo] Bulk asset warm failed',
              extractErrorInfo(err),
            );
          });
        }
      }

      const originalTranscription =
        this.rootStore.transcriptionStore.getOriginalTranscription();

      if (!originalTranscription) {
        logger.warn(
          'TYPE_ERROR: originalTranscription is not available in store',
        );
        return;
      }

      // reset frame locked tracks of the previous video
      this.frameLockedTracks = [];
      this.rootStore.editorStore.loadProject({
        videoSource: sourceWithFontsInjected,
        transcript: loadedSources.transcript,
        originalTranscription: originalTranscription.elements,
        originalVideoTrackId:
          this.pickOriginalVideoTrack(sourceWithFontsInjected) || null,
        karaokeData: loadedSources.karaokeData || null,
        scenes: loadedSources.scenes || null,
        originalVideoDuration: this.originalVideoDuration || undefined,
        extraElementData: this.currentVideo.extraElementData,
      });

      // // re-lock original video by default, call after source is set
      // this.rootStore.videoSourceStore.onNextStateChange(() => {
      //   this.trackManager.frameLockOriginalVideoTrack();
      // });
      this.rootStore.videoSourceStore.onNextSourceRefresh(() => {
        // when video source is finally updated and pulled from rendrer
        logger.debug(
          'Video source refreshed in store, setting isLoading:false',
        );
        runInAction(() => (this.isLoading = false));
      });

      console.log('Fonts injected, source set (render Video)');
    }
  }

  private async loadVideoElementsWithoutRendering(video: CurrentLoadedVideo) {
    // Ensure transcript data is loaded before processing video elements
    if (
      !this.rootStore.transcriptionStore.isStoryOriginalTranscriptionAvailable
    ) {
      await this.loadTranscriptIfNeeded();
    }

    // Snapshot-only load: legacy changelog replay has been removed. Load the
    // stored transcription snapshot as-is; videos without a snapshot fall back
    // to the original transcription (no edits applied).
    const transcript = video.transcriptionSnapshot?.elements
      ? toJS(video.transcriptionSnapshot.elements)
      : toJS(
          this.rootStore.transcriptionStore.getOriginalTranscription()
            ?.elements ?? [],
        );

    const defaultKaraokeConfig = {
      ...DEFAULT_KARAOKE_CONFIG,
      ...this.karaokeProducer.getKaraokeTextSettingByAspectRatio(),
    };

    const extraElementDataConfig = z
      .object({
        karaokeConfig: KaraokeConfigSchema,
        karaokeTemplateId: z.string().optional(),
      })
      .safeParse(video.extraElementData);

    const config = extraElementDataConfig.success
      ? extraElementDataConfig.data.karaokeConfig
      : defaultKaraokeConfig;

    // PLAT-10788 — hydrate the per-language karaoke cache + active variant
    // (source-first; the `?lang=` switch is a Phase 5 hook). Shared DS8/DS9 logic
    // lives in the store; it returns the active variant to feed the editor.
    const karaokeStore = this.rootStore.karaokeStore;
    const activeKaraoke = karaokeStore.hydrateLoadedByLanguage({
      karaokeData: video.karaokeData,
      karaokeDataByLanguage: video.karaokeDataByLanguage,
      transcriptLanguage: this.rootStore.transcriptionStore.rawLanguage,
      originalVideoLanguage: this.story?.originalVideoLanguage,
    });
    video.karaokeData = activeKaraoke;
    karaokeStore.legacyKaraokeConfigLoaded = {
      ...config,
      parent_template_id: extraElementDataConfig.success
        ? extraElementDataConfig.data.karaokeTemplateId
        : undefined,
    };
    return {
      transcript,
      karaokeData: video.karaokeData,
      scenes: video.scenes || null,
    };
  }

  async saveStoryAndVideo(
    asFinal: boolean = false,
    withRenderer = true,
    resetTimeline = true,
    autoSave = false,
    skipLastActionUpdate = false,
  ) {
    if (!this.story) {
      logger.warn('TYPE_ERROR: story is undefined');
      throw new Error('Cannot save story: story not set');
    }

    if (asFinal) {
      this.story.finalVideo =
        this.currentVideo?.parentVideo ?? this.currentVideo;
    }

    await this.saveCurrentAndParentVideos(
      withRenderer,
      resetTimeline,
      autoSave,
      skipLastActionUpdate,
    );

    try {
      // Block until the background save (video + story) completes so callers
      // that await saveStoryAndVideo see a fully-persisted story.
      await this.waitForBackgroundSave();
      // Punch-list photos were uploaded during the background save's prepare();
      // clear the pending blobs so the next save doesn't re-upload them.
      this.photoDataForDato = {};
    } catch (err) {
      // Background save failed; savingError is already set and RetrySavingModal
      // drives the retry. Leave photoDataForDato intact so a retry re-uploads.
      logger.warn('saveStoryAndVideo:background-save-failed', {
        error: extractErrorInfo(err),
      });
    }
  }

  prepareCurrentVideoForSave = async (
    withRenderer = true,
    snapshot?: CurrentLoadedVideo,
  ) => {
    const videoToModify = snapshot ?? this.currentVideo;
    if (!videoToModify) return;

    if (withRenderer) {
      const exportedSource =
        this.rootStore.editorStore.exportProject().sources.videoSource;
      if (!exportedSource?.elements) {
        throw { humanMessage: 'Video is not yet loaded' };
      }

      videoToModify.videoSource = exportedSource;

      // Always persist duration into clipJson for reliable display in listing views
      const d = this.duration;
      if (typeof d === 'number' && d > 0 && isFinite(d)) {
        videoToModify.clipJson = {
          ...(videoToModify.clipJson || { startTime: 0 }),
          duration: d,
        };
      }

      if (videoToModify.clipJson) {
        // Always sync the full clipJson from the authoritative source (videoToModify)
        // to story.otherVideos. This ensures fields like isLightweightClip: false
        // (set during expansion) are propagated and not lost due to stale spreads.
        for (const storyVideo of this.story?.otherVideos || []) {
          if (storyVideo.clipJson && storyVideo.id === videoToModify.id) {
            storyVideo.clipJson = { ...videoToModify.clipJson };
          }
        }
      }
    }
    if (this.story?.byExternalUser) {
      videoToModify.isClientReady = true;
    }

    let karaokeConfig = this.karaokeProducer.getKaraokeConfig();

    // Get transcription changes from our store
    videoToModify.transcriptionChanges =
      this.rootStore.transcriptionStore.getTranscriptionChangesForSave();

    let transcriptSnapshot =
      this.rootStore.transcriptionStore.getFinalTranscriptionElements();

    const karaokeStore = this.rootStore.karaokeStore;
    // PLAT-10788 — harvest only. `activeLanguage` is owned by load (Phase 4) and
    // the language switch (Phase 5); save trusts it and just captures the
    // per-language map (+ the active variant for legacy consumers).
    videoToModify.karaokeData = karaokeStore.getKaraokeData() ?? undefined;
    const karaokeByLanguage = karaokeStore.getAllKaraokeDataByLanguage();
    videoToModify.karaokeDataByLanguage =
      Object.keys(karaokeByLanguage).length > 0 ? karaokeByLanguage : undefined;

    videoToModify.transcriptionSnapshot = {
      elements: transcriptSnapshot,
    };
    videoToModify.transcriptionText =
      extractTranscriptionText(transcriptSnapshot);

    // Capture layout segments from timeline
    videoToModify.scenes = this.rootStore.editorStore.getScenesData();

    videoToModify.extraElementData.karaokeConfig = karaokeConfig;

    this.syncPunchListToVideo(videoToModify);
  };

  private syncPunchListToVideo(videoToModify: Video): void {
    const extraElementData = videoToModify.extraElementData || {};
    for (let element of Object.values(extraElementData)) {
      delete element?.punchListData;
    }
    videoToModify.punchList = this.punchListManager.punchListItems;
    videoToModify.punchList.forEach(punchListItem => {
      if (!punchListItem.id) return;
      extraElementData[punchListItem.id] = {
        ...(extraElementData[punchListItem.id] || {}),
        punchListData: punchListItem,
      };
    });
    videoToModify.extraElementData = extraElementData;
  }

  /**
   * Returns a promise that resolves once the current background save cycle
   * completes. Resolves immediately if no save is in-flight.
   * Rejects if video is switched before save completes.
   */
  waitForBackgroundSave(): Promise<void> {
    if (!this.isBackgroundSaving) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      this._backgroundSaveResolvers.push({ resolve, reject });
    });
  }

  /**
   * Reject all pending background save waiters (e.g., publish operations)
   * and clear the pending save queue. Called when switching videos.
   */
  private _rejectPendingBackgroundSaveWaiters(): void {
    const resolvers = this._backgroundSaveResolvers;
    this._backgroundSaveResolvers = [];
    this._pendingNextSave = null;

    for (const r of resolvers) {
      r.reject(new Error('Publish aborted: video changed'));
    }

    if (resolvers.length > 0) {
      logger.debug('_rejectPendingBackgroundSaveWaiters: rejected', {
        count: resolvers.length,
      });
    }
  }

  async saveCurrentAndParentVideos(
    withRenderer = true,
    _resetTimeline = true,
    autoSave = false,
    skipLastActionUpdate = false,
  ) {
    logger.debug('saveCurrentAndParentVideos', {
      videoId: this.currentVideo?.id,
    });
    // Early returns for exceptional cases
    if (
      !this.currentVideo ||
      !this.story ||
      !this.datoClientStore.videoRepository ||
      !this.datoClientStore.storyRepository
    ) {
      logger.error('saveCurrentAndParentVideos:skip:missing-deps');
      return;
    }

    // If a background save is already in-flight, queue this request as pending
    if (this.isBackgroundSaving) {
      this._pendingNextSave = { withRenderer, autoSave };
      logger.debug('saveCurrentAndParentVideos:queued-pending');
      return;
    }

    this.isBackgroundSaving = true;
    this.savingError = undefined;

    // Mark clipJson as no longer lightweight since we're saving full resources
    const clipJson = this.currentVideo.clipJson as ClipMetadata | undefined;
    if (clipJson?.isLightweightClip) {
      this.currentVideo.clipJson = { ...clipJson, isLightweightClip: false };
    }

    // Capture original ID before save — if it's a temp ID, we may need to
    // clean up the orphaned optimistic entry if the repo falls back to CREATE
    const originalTempId = this.currentVideo.id;
    const snapshotVideoId = this.currentVideo.id;

    if (!skipLastActionUpdate) {
      this.addVideoAction({
        editor: this.rootStore.userIdentityStore.currentEditor,
        date: new Date().getTime(),
        type: autoSave ? 'autosave' : 'save',
      });
    }

    // Editing a clip advances it out of "New" (the default) into "Working".
    if (
      (this.currentVideo.reviewStatus || DEFAULT_CLIP_REVIEW_STATUS) ===
      DEFAULT_CLIP_REVIEW_STATUS
    ) {
      this.currentVideo.reviewStatus = ClipReviewStatus.Working;
      const editedId = this.currentVideo.id;
      if (this.story?.otherVideos && editedId) {
        this.story.otherVideos = this.story.otherVideos.map(v =>
          v.id === editedId
            ? { ...v, reviewStatus: ClipReviewStatus.Working }
            : v,
        );
      }
    }

    // Fire and forget — do NOT await
    void this._runBackgroundSave(
      withRenderer,
      autoSave,
      snapshotVideoId,
      originalTempId,
    );
  }

  private async _runBackgroundSave(
    withRenderer: boolean,
    autoSave: boolean,
    snapshotVideoId: string | undefined,
    originalTempId: string | undefined,
  ): Promise<void> {
    try {
      const prepared = await backgroundVideoSaveService.prepare(
        this,
        withRenderer,
        autoSave,
      );
      const result = await backgroundVideoSaveService.save(prepared, this);

      if (result) {
        this._applyBackgroundSaveResult(
          result,
          autoSave,
          snapshotVideoId,
          originalTempId,
        );
      } else if (this.currentVideoLoadingWorkflowId) {
        // Fail video loading workflow on save error
        this.rootStore.analyticsStore.completeWorkflow(
          this.currentVideoLoadingWorkflowId,
          false,
          { error: 'background-save-returned-null', autoSave },
        );
        this.currentVideoLoadingWorkflowId = null;
        this.rootStore.videoSourceStore.currentVideoLoadingWorkflowId = null;
      }
    } catch (error) {
      logger.error('_runBackgroundSave:failed', {
        error: extractErrorInfo(error),
      });
      if (error instanceof Error) {
        this.savingError = error;
      }

      if (this.currentVideoLoadingWorkflowId) {
        this.rootStore.analyticsStore.completeWorkflow(
          this.currentVideoLoadingWorkflowId,
          false,
          { error: String(error), autoSave },
        );
        this.currentVideoLoadingWorkflowId = null;
        this.rootStore.videoSourceStore.currentVideoLoadingWorkflowId = null;
      }
    } finally {
      this.isBackgroundSaving = false;

      // Notify all callers waiting on this background save cycle
      // Reject if save failed, resolve if successful
      const resolvers = this._backgroundSaveResolvers;
      this._backgroundSaveResolvers = [];

      if (this.savingError) {
        for (const r of resolvers) {
          r.reject(
            new Error(`Background save failed: ${this.savingError.message}`),
          );
        }
        if (resolvers.length > 0) {
          logger.debug('_runBackgroundSave: rejected waiters due to error', {
            count: resolvers.length,
            error: this.savingError.message,
          });
        }
      } else {
        for (const r of resolvers) {
          r.resolve();
        }
        if (resolvers.length > 0) {
          logger.debug('_runBackgroundSave: resolved waiters', {
            count: resolvers.length,
          });
        }
      }

      // If a save was queued while we were saving, fire it now
      const pending = this._pendingNextSave;
      if (pending) {
        this._pendingNextSave = null;
        this.saveCurrentAndParentVideos(
          pending.withRenderer,
          true,
          pending.autoSave,
        );
      }
    }
  }

  async retryBackgroundSave(): Promise<void> {
    const prepared = this.savingError?.isBackground
      ? this.savingError.preparedVideoSave
      : undefined;
    if (!prepared) {
      logger.warn('retryBackgroundSave:skip:no-prepared-save');
      return;
    }

    const snapshotVideoId = prepared.videoSnapshot.id;
    this.savingError = undefined;
    this.isBackgroundSaving = true;

    try {
      const result = await backgroundVideoSaveService.save(prepared, this);
      if (result) {
        this._applyBackgroundSaveResult(
          result,
          false,
          snapshotVideoId,
          undefined,
        );
      }
    } catch (error) {
      logger.error('retryBackgroundSave:failed', {
        error: extractErrorInfo(error),
      });
      if (error instanceof Error) {
        this.savingError = error;
      }
    } finally {
      this.isBackgroundSaving = false;
    }
  }

  private _applyBackgroundSaveResult(
    result: BackgroundSaveResult,
    autoSave: boolean,
    snapshotVideoId: string | undefined,
    originalTempId: string | undefined,
  ): void {
    // Guard: only write back if the user hasn't switched clips during save
    if (
      this.currentVideo?.id !== snapshotVideoId ||
      this.currentVideo?.aspectRatio !== result.videoSnapshot.aspectRatio
    ) {
      logger.debug('_applyBackgroundSaveResult:skip:clip-switched', {
        expectedId: snapshotVideoId,
        actualId: this.currentVideo?.id,
        expectedAspectRatio: result.videoSnapshot.aspectRatio,
        actualAspectRatio: this.currentVideo?.aspectRatio,
      });
      return;
    }

    if (this.currentVideo) {
      this.currentVideo._publishedAt = new Date().toISOString();

      // If this was a new video OR the repo fell back to CREATE for a temp clip,
      // assign the real ID
      if (
        result.wasNewVideo ||
        (this.currentVideo.id && isTempClipId(this.currentVideo.id))
      ) {
        const hadTempId = isTempClipId(this.currentVideo.id);
        this.currentVideo.id = result.id;

        // Remove the orphaned optimistic entry from the fallback CREATE path
        if (hadTempId && isTempClipId(originalTempId) && this.story) {
          this.story.otherVideos = this.story.otherVideos.filter(
            v => v.id !== originalTempId,
          );
        }
      }

      this.currentVideo.slug = result.slug;
      this.currentVideo.hash = result.hash;
      this.currentVideo.resourcesMetadata =
        result.videoSnapshot.resourcesMetadata;
      this.currentVideo.videoSource = result.videoSnapshot.videoSource;
      this.currentVideo.transcriptionSnapshot =
        result.videoSnapshot.transcriptionSnapshot;
      this.currentVideo.karaokeData = result.videoSnapshot.karaokeData;
      this.currentVideo.karaokeDataByLanguage =
        result.videoSnapshot.karaokeDataByLanguage;
      this.currentVideo.scenes = result.videoSnapshot.scenes;

      if (this.story?.otherVideos) {
        syncVideoToStoryEntries(
          this.story.otherVideos,
          this.currentVideo.id,
          // prepareCurrentVideoForSave was called on videoSnapshot,
          // currentVideo is outdated
          result.videoSnapshot,
        );
      }

      this.rootStore.clipPreviewStore.invalidate(
        this.currentVideo.id,
        this.currentVideo.parentVideo?.id,
      );
    }

    this.loadCurrentVideoVersionsHistory(0);

    // Complete video loading workflow on first save
    if (this.currentVideoLoadingWorkflowId) {
      this.rootStore.analyticsStore.completeWorkflow(
        this.currentVideoLoadingWorkflowId,
        true,
        { videoId: result.id, autoSave },
      );
      this.currentVideoLoadingWorkflowId = null;
      this.rootStore.videoSourceStore.currentVideoLoadingWorkflowId = null;
    }
  }

  // Helper methods for saveCurrentAndParentVideos
  async addCurrentToParentVideos(
    videoSnapshot?: CurrentLoadedVideo,
    storySnapshot?: Story,
  ) {
    const videoToModify = videoSnapshot ?? this.currentVideo;
    const storyToModify = storySnapshot ?? this.story;

    if (!videoToModify) {
      logger.warn('addCurrentToParentVideos: no video to modify');
      return;
    }
    if (!videoToModify.parentVideo) {
      logger.warn('addCurrentToParentVideos: no parent video found');
      return;
    }

    // Dedup, and push a flattened entry so the live graph stays acyclic.
    const alreadyInParent = videoToModify.parentVideo.associatedVideos.some(
      v => v.id === videoToModify.id,
    );
    if (!alreadyInParent) {
      videoToModify.parentVideo.associatedVideos.push(
        flattenAssociatedVideo(videoToModify),
      );
    }

    const parentVideo = videoToModify.parentVideo;
    const videoData =
      await this.datoClientStore.videoRepository.saveOrUpdateVideo(
        parentVideo.id
          ? {
              id: parentVideo.id,
              associatedVideos: parentVideo.associatedVideos,
            }
          : parentVideo,
      );

    videoToModify.parentVideo.id = videoData.id;
    const parentStoryVideo = storyToModify?.otherVideos.find(
      v => v.id === videoToModify.parentVideo?.id,
    );
    if (parentStoryVideo) {
      const alreadyInStoryParent = parentStoryVideo.associatedVideos.some(
        v => v.id === videoToModify.id,
      );
      if (!alreadyInStoryParent) {
        parentStoryVideo.associatedVideos.push(
          flattenAssociatedVideo(videoToModify),
        );
      }
    }
  }

  // Helper methods for saveCurrentAndParentVideos
  async addCurrentToStoryVideos(
    videoSnapshot?: CurrentLoadedVideo,
    storySnapshot?: Story,
  ) {
    const videoToRead = videoSnapshot ?? this.currentVideo;
    const storyToModify = storySnapshot ?? this.story;

    if (!videoToRead?.id) {
      throw new Error('Current video id is not set');
    }
    if (!storyToModify?.id) {
      throw new Error('Story video id is not set');
    }

    await this.datoClientStore.storyRepository.addVideoToStory(
      storyToModify.id,
      videoToRead.id,
    );
    storyToModify.otherVideos = [videoToRead, ...storyToModify.otherVideos];
  }

  async addMultipleVideosToStory(videos: Video[]) {
    if (this.story === undefined) {
      throw new Error('Story is not defined');
    }
    await this.datoClientStore.storyRepository.addMultipleVideosToStory(
      this.story.id,
      videos.map(v => v.id).filter((id): id is string => !!id),
    );
    this.story.otherVideos = [...videos, ...this.story.otherVideos];
  }

  get savedSegments(): NonNullable<Story['savedSegments']> {
    return this.story?.savedSegments ?? {};
  }

  // Observable write for saved-segment highlight groups. The optimistic-update
  // + revert orchestration lives in SavedSegmentService; the store only owns
  // this assignment to `story.savedSegments` (see the service's port).
  setSavedSegments(next: NonNullable<Story['savedSegments']>): void {
    if (this.story) this.story.savedSegments = next;
  }

  /**
   * Add optimistic video entries for instant UI feedback.
   * These entries have temp IDs and will be replaced with real IDs
   * when the background save completes.
   */
  addOptimisticVideos(videos: OtherVideo[]): void {
    if (!this.story) return;
    this.story.otherVideos = [...videos, ...this.story.otherVideos];
  }

  /**
   * Replace a temp ID with the real ID after a successful save.
   * Called by the WriteQueueService when a clip save completes.
   */
  async replaceOptimisticVideoId(
    tempId: string,
    realId: string,
    slug: string,
    hash: string,
    associatedVideos?: AssociatedVideo[],
  ): Promise<void> {
    if (!this.story) return;

    // Update story.otherVideos. When the caller created the real record's
    // associated variants (e.g. the clip copy flow), fold them in so the card's
    // aspect-ratio chips appear without waiting for a full story refetch.
    this.story.otherVideos = this.story.otherVideos.map(video =>
      video.id === tempId
        ? {
            ...video,
            id: realId,
            slug,
            hash,
            ...(associatedVideos ? { associatedVideos } : {}),
          }
        : video,
    );

    // If this video is currently loaded in the editor, update its ID too
    if (this.currentVideo?.id === tempId) {
      this.currentVideo = {
        ...this.currentVideo,
        id: realId,
        slug,
        hash,
      };
      logger.info('replaceOptimisticVideoId: updated currentVideo ID', {
        tempId,
        realId,
      });

      // Now that we have a real ID, persist the expanded resources
      this.persistExpandedResourcesForCurrentVideo();
    }

    // If this video is currently shown in clip preview (Content Studio), update its ID
    if (this.videoClipPreview?.id === tempId) {
      // If videoSource is missing, expansion hasn't pushed it via
      // setClipPreviewAndUpdateSource yet — await it (cache / in-flight /
      // fresh) so persist + the adapter auto-save see expanded data.
      let expanded: OtherVideo | undefined = undefined;
      if (!this.videoClipPreview.videoSource) {
        expanded = (
          await this.rootStore.clipPreviewStore
            .prefetchLightweight(this.videoClipPreview, this.story, this)
            .catch(error => {
              logger.warn(
                'replaceOptimisticVideoId: prefetchLightweight failed for videoClipPreview',
                {
                  tempId,
                  realId,
                  clipPreviewId: this.videoClipPreview?.id,
                  ...extractErrorInfo(error),
                },
              );
              return undefined;
            })
        )?.populatedClip;
      }

      const updatedVideoClipPreview = {
        ...this.videoClipPreview,
        ...(expanded?.videoSource && {
          videoSource: expanded.videoSource,
          transcriptionSnapshot: expanded.transcriptionSnapshot,
          karaokeData: expanded.karaokeData,
          scenes: expanded.scenes,
        }),
        id: realId,
        slug,
        hash,
      };

      // Persist before assigning the real ID to block adapter auto-saves (e.g.
      // video template auto-apply)
      await this.persistExpandedResourcesForClipPreview(
        updatedVideoClipPreview as VideoVersion,
      );

      this.setClipPreviewAndUpdateSource(updatedVideoClipPreview);

      logger.info('replaceOptimisticVideoId: updated videoClipPreview ID', {
        tempId,
        realId,
      });
    }
  }

  /**
   * Persist expanded resources for current video to S3 and update Dato.
   * Called after a temp clip gets its real ID.
   */
  private async persistExpandedResourcesForCurrentVideo(): Promise<void> {
    if (!this.currentVideo || !this.story) return;

    const clipJson = this.currentVideo.clipJson as ClipMetadata | undefined;
    if (!clipJson) return;

    try {
      await AIClipProcessor.persistExpandedClipResources(
        this.currentVideo as VideoVersion,
        clipJson,
        this.story,
        clipProcessorDepsFromStore(this),
      );

      // Sync updated fields (including isLightweightClip: false) to story.otherVideos
      syncVideoToStoryEntries(
        this.story.otherVideos,
        this.currentVideo.id,
        this.currentVideo,
      );
    } catch (err) {
      logger.warn('persistExpandedResourcesForCurrentVideo: failed', {
        videoId: this.currentVideo.id,
        error: extractErrorInfo(err),
      });
    }
  }

  /**
   * Persist expanded resources for clip preview to S3 and update Dato.
   * Called after a temp clip shown in clip preview gets its real ID.
   */
  private async persistExpandedResourcesForClipPreview(
    clipPreview?: VideoVersion,
  ): Promise<void> {
    if (!clipPreview || !this.story) return;

    const clipJson = clipPreview.clipJson;
    if (!clipJson) return;

    // Defensive — caller awaits expansion. Skip if it failed.
    if (!clipPreview.videoSource) return;

    try {
      await AIClipProcessor.persistExpandedClipResources(
        clipPreview,
        clipJson,
        this.story,
        clipProcessorDepsFromStore(this),
      );

      // Sync updated fields (including isLightweightClip: false) to story.otherVideos
      syncVideoToStoryEntries(
        this.story.otherVideos,
        clipPreview.id,
        clipPreview,
      );
    } catch (err) {
      logger.warn('persistExpandedResourcesForClipPreview: failed', {
        videoId: clipPreview.id,
        error: extractErrorInfo(err),
      });
    }
  }

  /**
   * Remove a failed optimistic entry from the list.
   * Called when a clip save fails and user chooses to discard.
   */
  removeOptimisticVideo(tempId: string): void {
    if (!this.story) return;
    this.story.otherVideos = this.story.otherVideos.filter(
      v => v.id !== tempId,
    );
  }

  /**
   * The plural sibling of `removeStoryVideo`, for a group delete (PLAT-11118).
   * One repository write for the whole set rather than one per video, so the
   * grid's clips leave together and the read-modify-write cannot race itself.
   */
  async removeStoryVideos(videoIds: string[]) {
    if (this.story === undefined) {
      throw new Error('Story is not defined');
    }
    const removing = new Set(videoIds);
    if (removing.size === 0) return;

    await this.datoClientStore.storyRepository.removeVideosFromStory(
      this.story.id,
      [...removing],
    );

    const finalVideoId = this.story.finalVideo?.id;
    if (finalVideoId && removing.has(finalVideoId)) {
      this.story.finalVideo = undefined;
    }
    this.story.otherVideos = this.story.otherVideos.filter(
      v => !v.id || !removing.has(v.id),
    );
  }

  async removeStoryVideo(videoId?: string) {
    if (this.story === undefined) {
      throw new Error('Story is not defined');
    }
    if (videoId) {
      await this.datoClientStore.storyRepository.removeVideoFromStory(
        this.story.id,
        videoId,
      );
      if (this.story?.finalVideo?.id === videoId) {
        this.story.finalVideo = undefined;
      }
    }
    // remove video from list of other videos
    this.story.otherVideos = this.story.otherVideos.filter(
      v => v.id !== videoId,
    );
  }

  async loadOriginalWaveformData() {
    if (!this.story?.transcription?.waveformData) return;
    try {
      let waveformData;

      if (this.story.useAws) {
        waveformData =
          await this.datoClientStore.transcriptRepository.fetchWaveformDataFromUrl(
            this.story.transcription.waveformData.url,
          );
      } else {
        waveformData =
          await this.datoClientStore.transcriptRepository.fetchWaveformData(
            this.story.transcription.waveformData.id,
          );
      }

      this.originalWaveForm = WaveformData.create(waveformData);
      console.log('loadOriginalWaveformData: waveform data created');
      this.maxTimelineScale = Math.min(
        this.maxTimelineScale,
        Math.floor(
          this.originalWaveForm.sample_rate / this.originalWaveForm.scale,
        ),
      );

      this.timelineScale = Math.min(this.timelineScale, this.maxTimelineScale);

      let maxSample = 0;
      const max_array = this.originalWaveForm.channel(0).max_array();
      for (let i = 0; i < max_array.length; i++) {
        if (max_array[i] > maxSample) {
          maxSample = max_array[i];
        }
      }
      this.maxOriginalWaveformSample = maxSample;
      // console.log('loaded original waveform data')
    } catch (e) {
      console.log('loadOriginalWaveformData error', e);
      throw e;
    }
  }

  /**
   * PLAT-11228: inject the audio-first element into a composition that was
   * composed before the editor audio existed (auto-nav lands seconds after
   * transcription flips, and the audio URL write can lag it). No-op once an
   * audio-first element is present; refuses to clobber a composition that
   * already carries other elements.
   */
  /**
   * The editor-audio URL is re-signed on every fetch, so two references to
   * the same file differ in their query strings — compare by origin + path.
   */
  private isSameMediaFile(a: unknown, b: unknown): boolean {
    return isSameMediaFile(a, b);
  }

  /**
   * renderVideo rebuilds the transcript from the video's snapshot, which is
   * frozen at compose time — any audio-first re-render (bootstrap, source
   * swap) must refresh it from the live edit state first, or transcript
   * edits made against the audio get clobbered.
   */
  private captureLiveTranscriptSnapshot(video: CurrentLoadedVideo) {
    // The editor's transcript model is the authoritative post-edit state —
    // Remove Text (etc.) runs through editor.commandRemoveTranscript and
    // never touches TranscriptionStore's elements. Fall back to the store
    // only when no editor is live.
    const editorTranscript = this.rootStore.editorStore?.editor
      ? // eslint-disable-next-line no-restricted-syntax
        (this.rootStore.editorStore.editor.getFullTranscript() as any[])
      : null;
    const liveTranscript =
      editorTranscript && editorTranscript.length > 0
        ? editorTranscript
        : this.rootStore.transcriptionStore.getFinalTranscriptionElements();
    if (liveTranscript && liveTranscript.length > 0) {
      video.transcriptionSnapshot = { elements: toJS(liveTranscript) };
    }
  }

  // Audio-first elements stay type 'video' (with the editor-audio file as
  // source) so every "original media" surface — transcript-driven editing,
  // track labeling, isOriginalVideoElement — treats them as the main media.
  // Chromium video elements play bare mp3 audio. Identity is the source path.
  private isAudioFirstElement(element: any, audioUrl: string): boolean {
    return (
      (element?.type === 'video' || element?.type === 'audio') &&
      this.isSameMediaFile(element?.source, audioUrl)
    );
  }

  /**
   * renderVideo loads the rebuilt composition through editorStore.loadProject,
   * whose editor.loadVideoSource fires PROJECT_LOADED — an event the editor
   * sync observer does NOT map to a renderer push. Fresh page loads paint
   * through the renderer-connect bootstrap anyway, but a MID-SESSION reload
   * (audio-first bootstrap or the unlock upgrade) stays invisible: the canvas
   * keeps its last-good source and the timeline UI never re-renders. Fire
   * TIMELINE_CHANGED explicitly — it bumps timelineVersion (timeline UI) and
   * runs refreshTimeline (renderer push) + refreshTranscript + refreshKaraoke.
   */
  private notifyTimelineReloaded(reason: string) {
    logger.debug('Audio-first timeline notify', { reason });
    this.rootStore.editorStore.editor?.notifyObservers(
      EditorChangeType.TIMELINE_CHANGED,
      { reason },
    );
  }

  /**
   * A composition composed mid-pipeline can carry the wrong duration: when
   * the story's video metadata hadn't landed AND the audio metadata probe
   * lost the race, the element gets a concrete-but-wrong duration that the
   * load-time repair skips (it only fires on null/"undefined"). The result
   * is a timeline clipped to a fraction of the real length — and the
   * transcript panel clips its display to timeline duration, so the whole
   * session looks truncated. Re-check on the pending poll, but ONLY for the
   * pristine single-element untrimmed shape — an edited timeline is the
   * user's and must never be resized under them.
   */
  private audioFirstDurationRepairedFor: string | null = null;

  private async repairAudioFirstDuration(
    video: CurrentLoadedVideo,
    audioUrl: string,
    elements: any[],
  ): Promise<void> {
    if (this.audioFirstDurationRepairedFor === this.story?.id) return;
    const inner = elements.length === 1 ? elements[0] : null;
    const pristine =
      inner &&
      this.isAudioFirstElement(inner, audioUrl) &&
      inner.trim_start == null &&
      inner.trim_duration == null &&
      (inner.time ?? 0) === 0;
    if (!pristine) {
      // Edited (or wrapped) composition — leave it alone, permanently.
      this.audioFirstDurationRepairedFor = this.story?.id ?? null;
      return;
    }
    try {
      const seconds = await getAudioDurationFromUrl(audioUrl);
      this.audioFirstDurationRepairedFor = this.story?.id ?? null;
      const current = parseFloat(String(inner.duration ?? ''));
      if (!seconds || !isFinite(seconds)) return;
      if (isFinite(current) && Math.abs(current - seconds) <= 1) return;
      logger.debug('Audio-first duration repaired on poll', {
        was: inner.duration ?? null,
        now: seconds,
      });
      inner.duration = `${seconds} s`;
      inner.media_duration = seconds;
      await this.renderVideo(video, false, DEFAULT_PREVIEW_VIDEO_RES);
      this.notifyTimelineReloaded('audio-first duration repair');
    } catch {
      // Probe failed again — the next poll tick retries.
      this.audioFirstDurationRepairedFor = null;
    }
  }

  async ensureAudioFirstComposition(): Promise<boolean> {
    const audioUrl = this.story?.transcription?.audio?.url;
    const video = this.currentVideo;
    logger.debug('ensureAudioFirst state', {
      hasAudioUrl: !!audioUrl,
      hasVideo: !!video,
      versionId: video?.versionId,
      elementCount: ((video?.videoSource?.elements ?? []) as any[]).length,
      types: ((video?.videoSource?.elements ?? []) as any[]).map(e => e?.type),
    });
    if (!audioUrl || !video) return false;
    const elements = (video.videoSource?.elements ?? []) as any[];
    if (
      elements.some(el =>
        pickUnderlyingVideoElements(el).some(
          inner =>
            this.isAudioFirstElement(inner, audioUrl) ||
            isLocalCanvasSource(inner?.source),
        ),
      )
    ) {
      await this.repairAudioFirstDuration(video, audioUrl, elements);
      return true;
    }

    if (elements.length === 0) {
      video.videoSource = {
        ...video.videoSource,
        elements: [
          {
            id: uuid(),
            track: 1,
            time: 0,
            type: 'video',
            source: audioUrl,
            autoplay: true,
          },
        ],
      };
      this.captureLiveTranscriptSnapshot(video);
      await this.renderVideo(video, false, DEFAULT_PREVIEW_VIDEO_RES);
      this.notifyTimelineReloaded('audio-first inject');
      return true;
    }

    // The upload flow pre-creates a draft video whose composition already
    // carries the original-video element(s) — unplayable while renditions
    // are pending. Repoint them at the editor audio in place (type stays
    // 'video' so original-media surfaces keep working; id and timing kept,
    // so edits carry over); the unlock upgrade swaps the source back.
    const converted = bootstrapAudioFirstMedia(
      elements,
      inner => this.isOriginalVideoElement(inner),
      audioUrl,
    );
    if (!converted) return false;
    this.captureLiveTranscriptSnapshot(video);
    await this.renderVideo(video, false, DEFAULT_PREVIEW_VIDEO_RES);
    this.notifyTimelineReloaded('audio-first repoint');
    return true;
  }

  /**
   * PLAT-11228: while renditions are pending, upgrade the audio-first
   * composition to play the session's locally-held upload on the canvas.
   * Small files use the renderer embed's CacheStorage. Large files stay in
   * the parent page and are shown by a native player at the clip's source
   * offset, avoiding a multi-gigabyte cross-frame Blob clone. Strictly
   * best-effort: any missing precondition or failure leaves the shipped
   * audio-first canvas untouched, and the rendition-landed unlock swaps to
   * the real 270p source.
   */
  /** Story whose local canvas has PAINTED — drives the veil. */
  localCanvasStoryId: string | null = null;
  /** Story using the parent page's native object-URL layer for a large File. */
  nativeLocalPreviewStoryId: string | null = null;
  /**
   * Story with an attempt made (success or not) — one attempt per story, and
   * the render-time reset uses it to leave this session's own swap alone.
   * Kept separate from the painted flag so a failed or in-flight attempt
   * never drops the veil onto a black canvas.
   */
  localCanvasAttemptStoryId: string | null = null;

  async upgradeAudioFirstToLocalCanvas(): Promise<boolean> {
    const story = this.story;
    const video = this.currentVideo;
    const audioUrl = story?.transcription?.audio?.url;
    if (!story?.id || !video || !audioUrl) return false;
    if (this.localCanvasAttemptStoryId === story.id) return false;
    if (!isCompressedRenditionPending(story)) return false;
    if (!this.renderer?.ready || this.isLoading) return false;

    const uploadFlowStore =
      this.rootStore.dashboardStore?.userStoryUploadFlowStore;
    const localFile = uploadFlowStore?.localVideoFileRecord?.[story.id];
    const objectUrl = uploadFlowStore?.getLocalVideoUrl?.(story.id);
    if (!localFile || !objectUrl) return false;

    // Only upgrade a composition that is currently audio-first — never touch
    // one already on real video or already local.
    const elements = (video.videoSource?.elements ?? []) as any[];
    const hasAudioFirst = elements.some(el =>
      pickUnderlyingVideoElements(el).some(inner =>
        this.isAudioFirstElement(inner, audioUrl),
      ),
    );
    if (!hasAudioFirst) return false;

    // One attempt per story: a failed probe/cache marks done and the canvas
    // simply stays audio-first, veil included (a load-time reset clears the
    // mark so a fresh renderer re-attempts with a fresh cache push).
    this.localCanvasAttemptStoryId = story.id;
    try {
      const decodable = await probeVideoDecodable(objectUrl);
      if (!decodable) {
        logger.debug('Local canvas skipped: file not browser-decodable', {
          storyId: story.id,
        });
        return false;
      }
      if (requiresNativeLocalPreview(localFile)) {
        runInAction(() => {
          this.nativeLocalPreviewStoryId = story.id;
        });
        logger.info('Using native local preview for oversized upload', {
          storyId: story.id,
          bytes: localFile.size,
        });
        return true;
      }
      const localUrl = buildLocalCanvasUrl(story.id);
      await this.renderer.cacheAsset(localUrl, localFile);

      for (const element of elements) {
        for (const inner of pickUnderlyingVideoElements(element)) {
          if (this.isAudioFirstElement(inner, audioUrl)) {
            inner.type = 'video';
            inner.source = localUrl;
            // Fresh id forces the renderer to recreate the media — a
            // source-only change keeps the mp3's zero video dimensions.
            inner.id = uuid();
          }
        }
      }
      this.captureLiveTranscriptSnapshot(video);
      await this.renderVideo(video, false, DEFAULT_PREVIEW_VIDEO_RES);
      this.notifyTimelineReloaded('local-canvas upgrade');
      // Hold the veil until the renderer has the local media loaded —
      // dropping it on the swap alone shows a black canvas for the gap.
      const settle = await waitForMediaLoadSettle(
        () => this.isLoadingAssets,
        LOCAL_CANVAS_LOAD_TIMEOUT_MS,
      );
      if (settle === 'timeout') {
        // The renderer started loading the cached bytes and never finished
        // (seen intermittently) — left alone this is a black canvas with
        // dead playback until reload. Fail soft: back to the audio-first
        // source (fresh ids again) and keep the veil; the attempt mark
        // stays so we don't loop.
        for (const element of elements) {
          for (const inner of pickUnderlyingVideoElements(element)) {
            if (isLocalCanvasSource(inner?.source)) {
              inner.source = audioUrl;
              inner.id = uuid();
            }
          }
        }
        await this.renderVideo(video, false, DEFAULT_PREVIEW_VIDEO_RES);
        this.notifyTimelineReloaded('local-canvas revert');
        logger.warn('Local canvas never finished loading — reverted', {
          storyId: story.id,
          timeoutMs: LOCAL_CANVAS_LOAD_TIMEOUT_MS,
        });
        return false;
      }
      runInAction(() => {
        this.nativeLocalPreviewStoryId = null;
        this.localCanvasStoryId = story.id;
      });
      logger.debug('Local canvas applied', { storyId: story.id, settle });
      return true;
    } catch (error) {
      logger.warn('Local canvas upgrade failed — staying audio-first', {
        error: extractErrorInfo(error),
      });
      return false;
    }
  }

  /**
   * PLAT-11228: upgrade an audio-first composition (getDefaultSource's
   * pending branch) once the preview rendition exists. Every audio element
   * sourcing the editor audio becomes a video element with the same id,
   * timing, and trims — splits and cuts made against the audio carry over
   * 1:1 — then the composition re-renders in place. Returns false when there
   * was nothing to swap (caller falls back to a full re-init).
   */
  async upgradeAudioFirstComposition(): Promise<boolean> {
    const audioUrl = this.story?.transcription?.audio?.url;
    const video = this.currentVideo;
    if (!audioUrl || !video) {
      logger.debug('upgrade gate fail: missing audio url or video', {
        hasAudioUrl: !!audioUrl,
        hasVideo: !!video,
      });
      return false;
    }

    const videoUrl = this.getValidVideoSourceUrlForResLevel(
      DEFAULT_PREVIEW_VIDEO_RES,
    );
    if (!videoUrl) {
      logger.debug('upgrade gate fail: no videoUrl for preview res', {
        storyId: this.story?.id,
      });
      return false;
    }

    // The live edits' source of truth is the editor's canonical export —
    // the same source the save path persists. Do NOT clone the
    // renderer-facing videoSourceStore source: that is a preview PROJECTION
    // (windowed, scene-wrapped, render-only) that does not round-trip
    // through loadProject — reloading it empties the timeline. Fall back to
    // the loaded video's source when no editor is live.
    let canonicalSource: Record<string, any> | null = null;
    try {
      canonicalSource =
        this.rootStore.editorStore.exportProject().sources.videoSource;
    } catch {
      canonicalSource = null;
    }
    const source = deepClone(
      canonicalSource &&
        Array.isArray(canonicalSource.elements) &&
        canonicalSource.elements.length > 0
        ? canonicalSource
        : video.videoSource,
    ) as CurrentLoadedVideo['videoSource'];

    let swapped = false;
    for (const element of source?.elements ?? []) {
      // Splits/scenes wrap the media in composition elements — swap the
      // underlying video elements wherever they live. Fresh ids force the
      // renderer to recreate the media: it caches per-element media state,
      // and a source-only change kept the mp3's zero video dimensions
      // (black canvas after unlock). Local-canvas elements (PLAT-11228)
      // swap the same way — the cached local bytes hand off to the real
      // rendition here.
      for (const inner of pickUnderlyingVideoElements(element)) {
        if (
          this.isAudioFirstElement(inner, audioUrl) ||
          isLocalCanvasSource(inner?.source)
        ) {
          inner.type = 'video';
          inner.source = videoUrl;
          inner.id = uuid();
          swapped = true;
        }
      }
    }
    if (!swapped) {
      // Already upgraded (a previous unlock pass, a save/reload, or the
      // renderVideo load-time swap handled it) — report success so the
      // caller does NOT fall back to a full re-init, which would clobber
      // live edits with the saved snapshot.
      const alreadyOnVideo = (source?.elements ?? []).some((element: any) =>
        pickUnderlyingVideoElements(element).some(
          inner =>
            typeof inner?.source === 'string' &&
            !this.isAudioFirstElement(inner, audioUrl) &&
            !isLocalCanvasSource(inner?.source),
        ),
      );
      if (alreadyOnVideo) {
        logger.debug('upgrade no-op: composition already on video');
        return true;
      }
      logger.debug('upgrade gate fail: no matching element', {
        audioPath: audioUrl.split('?')[0],
        elements: (source?.elements ?? []).map((e: any) => ({
          type: e?.type,
          src: typeof e?.source === 'string' ? e.source.split('?')[0] : null,
        })),
      });
      return false;
    }

    this.captureLiveTranscriptSnapshot(video);

    video.videoSource = source;
    // Scenes reference element ids, and the swap just re-identified the
    // media elements — stale scene data would dangle through loadProject's
    // validation. Clear it so scenes re-initialize from the swapped
    // timeline (they are auto-generated during the pending window).
    video.scenes = undefined;
    await this.renderVideo(video, false, DEFAULT_PREVIEW_VIDEO_RES);
    this.notifyTimelineReloaded('audio-first upgrade');
    return true;
  }

  /**
   * Set up transcription URLs without loading the data
   * This is called during story initialization to prepare for lazy loading
   */
  private setupTranscriptionUrls(story: Story) {
    if (!story.transcription?.elementsJson?.url) {
      return;
    }

    // Set up the transcription version URLs in the store
    const transcriptionInfo = story.transcription;
    this.rootStore.transcriptionStore.setTranscriptionVersionUrls({
      [TranscriptVersion.LATEST]: transcriptionInfo.elementsJson.url,
      [TranscriptVersion.ORIGINAL]: transcriptionInfo.elementsJson.originalUrl,
      [TranscriptVersion.REV]: transcriptionInfo.elementsJson.revUrl,
      [TranscriptVersion.AWS]: transcriptionInfo.elementsJson.awsUrl,
      [TranscriptVersion.ASSEMBLY]: transcriptionInfo.elementsJson.assemblyUrl,
      [TranscriptVersion.AWS_REV]: transcriptionInfo.elementsJson.awsRevUrl,
    });
  }

  /**
   * Load transcription data only if it's needed and not already loaded
   * This method should be called by components that actually need transcript data
   */
  async loadTranscriptIfNeeded(): Promise<boolean> {
    if (!this.story?.id) {
      console.warn('Story ID is not set, cannot load transcription');
      return false;
    }

    // Check if transcription is already loaded
    if (this.rootStore.transcriptionStore.isTranscriptionReady) {
      console.log('Transcription already loaded, skipping');
      return true;
    }

    // Check if transcription is currently loading
    if (this.rootStore.transcriptionStore.isTranscriptionLoading) {
      console.log('Transcription is already loading, waiting for completion');
      // Wait for the current loading to complete
      return new Promise(resolve => {
        const checkLoading = () => {
          if (!this.rootStore.transcriptionStore.isTranscriptionLoading) {
            resolve(this.rootStore.transcriptionStore.isTranscriptionReady);
          } else {
            setTimeout(checkLoading, 500);
          }
        };
        checkLoading();
      });
    }

    try {
      console.log('Loading transcription on demand');
      await this.loadTranscription();
      // A failed load does not throw: TranscriptionStore catches its own
      // errors and only marks itself Failed.
      return this.rootStore.transcriptionStore.isTranscriptionReady;
    } catch (error) {
      console.error('Failed to load transcription:', error);
      return false;
    }
  }

  async loadTranscription() {
    if (!this.story?.id) {
      throw new Error('Story ID is not set');
    }

    try {
      // Delegate to transcriptionStore for loading
      await this.rootStore.transcriptionStore.loadTranscription(
        this.story.id,
        this.story.useAws,
      );
      console.log('transcription loaded');
    } catch (e) {
      console.log('loadTranscription error', e);
      throw e;
    }
  }

  disposeRenderer() {
    if (this.renderer) {
      console.log('dispose renderer');
      // WarmedRenderer.dispose() override evicts cache tracking.
      this.renderer.dispose();
      this.renderer = undefined;
    }
  }

  async initializeVideoPlayer(
    htmlElement: HTMLDivElement,
    mode: 'interactive' | 'player' = 'interactive',
    origin: string = 'creator-studio',
  ) {
    this.disposeRenderer();

    // Enable tracks reordering for better selection experience by default
    let rendererConfig = getRendererConfig(true);
    this.rootStore.editorStore?.editor.setFlag(
      'reorderLayoutCompositionTrackToBottom',
      true,
    );

    // if (Object.keys(this.flags).length === 0) {
    //   logger.warn(
    //     'No flags found during initializeVideoPlayer, using default renderer configuration',
    //   );
    // }

    // if ('enableRendererLayoutReorder' in this.flags) {
    //   rendererConfig = getRendererConfig(
    //     !!this.flags.enableRendererLayoutReorder,
    //   );
    //   // Assumption1: flags loaded before initializeVideoPlayer is called - Object.keys(videoCreator.flags).length > 0 is a condition to mount VideoPlayer in Stage.tsx, which calls initializeVideoPlayer
    //   // Assumption2: editorStore is available by the time initializeVideoPlayer is called - should be true since editorStore is part of rootStore which is initialized in constructor
    //   this.rootStore.editorStore?.editor.setFlag(
    //     'reorderLayoutCompositionTrackToBottom',
    //     rendererConfig.reorderLayoutCompositionTrackToBottom,
    //   );
    // }

    const renderer = new WarmedRenderer(
      htmlElement,
      mode,
      rendererConfig.rendererId,
      this.assetPrewarmingService,
    );

    renderer.onReady = async () => {
      await renderer.setZoom('auto');
      await renderer.setLoop(false);
      await renderer.setCacheBypassRules([]);
      this.rootStore.videoSourceStore.connectRenderer(renderer);
      // PLAT-9966: install the composition translation projection. Reads
      // through to the latest `currentVideo.extraElementData` and active
      // editor karaoke language at transform-call time, so language
      // switches and translation-cache landings pick up automatically
      // when paired with `refreshSource()` (wired in applyCaptionsLanguage
      // call-sites).
      this.installCompositionTranslationProjection();
      this.rootStore.videoSourceStore.onNextSourceRefresh(() => {
        // when video source is finally updated and pulled from rendrer
        console.log(
          'Video source refreshed in store, setting isLoading:false (renderer.onReady)',
        );
        runInAction(() => (this.isLoading = false));
        this.timelineStore.setShouldCalcTimelineScale(true);
      });
    };

    renderer.onLoad = async () => {
      runInAction(() => {
        if (this.isPlayheadDragging) {
          this.pendingIsLoadingAssets = true;
          return;
        }
        this.isLoadingAssets = true;
      });
    };

    renderer.onLoadComplete = async () => {
      runInAction(() => {
        if (this.isPlayheadDragging) {
          this.pendingIsLoadingAssets = false;
          return;
        }
        this.isLoadingAssets = false;
      });
    };

    renderer.onPlay = () => {
      runInAction(() => (this.isPlaying = true));
    };

    renderer.onPause = () => {
      runInAction(() => (this.isPlaying = false));
      runInAction(() => (this.whenPaused = new Date().getTime()));
    };

    renderer.onTimeChange = time => {
      if (
        origin === 'clip-player' &&
        this.whenPaused &&
        this.whenPaused - new Date().getTime() === 0
      ) {
        this.renderer?.play();
      }
      this.onRendererTimeChange(time);
    };

    // A fresh renderer starts in the main composition.
    this.compositionScopeService.setRendererCompositionId(null);
    renderer.onActiveCompositionChange = elementId => {
      this.compositionScopeService.setRendererCompositionId(elementId);
    };

    renderer.onActiveElementsChange = rendererElementIds => {
      // Creatomate clears its on-canvas selection whenever the preview iframe
      // loses focus — including when the user clicks into a side-panel field
      // (e.g. the Graphics → Text "Text" box) to edit the selected element.
      // That arrives here as an empty selection; honoring it would wipe
      // `activeElementIds`, so `getActiveElement()` goes undefined and the
      // settings panel collapses back to the presets list mid-edit. A genuine
      // deselect (clicking the empty canvas) fires while the iframe still holds
      // focus, so only ignore the empty event when focus has left the iframe.
      if (rendererElementIds.length === 0 && !renderer.isIframeActive()) {
        return;
      }

      const elementIds = rendererElementIds;

      runInAction(() => {
        this.activeElementIds = elementIds;
        this.textBrandingService.switchTemplateByType(elementIds);
        this.openElementSidebar(elementIds);

        if (!elementIds.length) {
          // Leave only a composition we drilled into ourselves (scene-bound
          // video selection), where an empty selection means the user clicked
          // the empty canvas. Creatomate also reports an empty selection while
          // *entering* a composition the user double-clicked (name-tag and
          // other graphics templates) — resetting there bounces them straight
          // back out, which is how composition view was lost in PLAT-10145.
          if (this.compositionScopeService.shouldResetOnDeselect()) {
            this.compositionScopeService.setActiveCompositionId(null);
            this.renderer?.setActiveComposition(null).catch(err => {
              logger.warn('Failed to reset active composition on deselect', {
                error: extractErrorInfo(err),
              });
            });
          }
          this.timelineStore.clearActiveTimelineElements();
        }

        // Resolve timeline element IDs for selection highlighting
        // When selection comes from canvas/renderer, we need to find the actual timeline parent element
        const currentTime = this.time;
        const layoutIndexStore = this.rootStore.sceneLayoutIndexStore;
        const timelineElementIds: string[] = [];

        // Element time resolver for parent element lookup
        const getElementTimeRange = (id: string) => {
          const element = this._findElementById(id);
          if (!element) return undefined;
          return { globalTime: element.globalTime, duration: element.duration };
        };

        for (const elementId of elementIds) {
          // Try to resolve the parent ID that spans the current time
          const parentId = layoutIndexStore.resolveParentIdByTime(
            elementId,
            currentTime,
            getElementTimeRange,
          );
          if (parentId) {
            timelineElementIds.push(parentId);
          } else if (!layoutIndexStore.getRecords(elementId)?.length) {
            // If no parent found, the element itself is the timeline element
            timelineElementIds.push(elementId);
          }
        }

        // if multiple elements selected, keep timeline selection unchanged to maintain multiple elemenents selection state
        if (timelineElementIds.length === 1) {
          // Update timeline store with resolved timeline element IDs
          this.timelineStore.setActiveTimelineElements(...timelineElementIds);
        }

        const newActiveElement = this.getActiveElement();
        if (newActiveElement) {
          this.sceneLayoutSelectionManager.reselectInnerElement(
            newActiveElement,
            elementId => {
              this.compositionScopeService.setActiveCompositionId(
                newActiveElement.source.id,
              );
              this.setActiveElements(elementId);
            },
          );
        }
      });
    };

    // Instead of setting onStateChange directly, we register a callback with VideoSourceStore
    // This lets the VideoSourceStore manage state changes and maintain its internal state
    this.rootStore.videoSourceStore.onStateChange(async state => {
      console.log('state changing at:', Date.now());
      if (!this.isPlaying && this.state && this.frameLockedTracks.length > 0) {
        const applied = await this.trackManager.applyChangesToFrameLockedTracks(
          this.state,
          state,
        );
        // applying changes to other tracks will trigger state change again, skipping rest
        if (applied) return;
      }

      // TODO: Convert everything within this method into a handler
      const stateChangeContext = {
        isManualInteraction:
          !this.isPlaying && !!this.renderer?.isIframeActive(),
      };
      this.stateChangeObserver.handle(this, state, stateChangeContext);

      // Only sync scene layouts when iframe is the active element (user clicked into it)
      // This prevents echoing our own programmatic changes back as "manual" updates
      if (!this.isPlaying && this.renderer?.isIframeActive()) {
        this.rootStore.editorStore.syncExternalLayoutChanges(this.state, state);
      }

      runInAction(() => {
        // populate missing field trimStart
        state.elements.forEach(element => {
          element.trimStart = element.source.trim_start || 0;
        });
        this.state = state;
        this.duration = state.duration;
        if (!this.stateReady && state.elements.length && state.duration) {
          this.timelineStore.setShouldCalcTimelineScale(true);
        }
        // `state.elements` is a *windowed* subset near the playhead (PLAT-9562),
        // so it is legitimately empty at the end of playback (playhead past the
        // last element) or over a timeline gap. Latch `stateReady` on instead of
        // recomputing it from the window — otherwise an empty end-of-playback
        // frame flips it false, disabling the `disabled={!isVideoReady}` sidebar
        // tabs until the user scrubs back (PLAT-10472). It is reset explicitly on
        // reload (see `resetTimeline` path above).
        if (state.duration && state.elements.length) {
          this.stateReady = true;
        }
      });

      if (this.isPlaying) {
        return;
      }

      if (inDebugMode()) {
        // Group by elements[i].track
        const perTrackState = groupBy(state.elements, element => element.track);
        console.log('STATE CHANGE', perTrackState);
      } else {
        // Preserving original behavior.
        console.log('Renderer state change', state);
      }

      // Use the editor model (full timeline) instead of the windowed renderer
      // state so canvas scale and audio waveforms account for elements that
      // are currently outside the playhead window.
      const allEditorElements =
        this.rootStore.editorStore.editor.timeline.allElements;
      const maxElementDuration = allEditorElements.reduce(
        (acc, el) => Math.max(el.durationSeconds, acc),
        0,
      );

      runInAction(() => {
        this.maxCanvasScale = Math.min(
          this.maxTimelineScale,
          MAX_CANVAS_WIDTH / maxElementDuration, // waveform canvas cannot be wider than 32767px, will not render
        );
      });
      for (const element of allEditorElements) {
        if (!Element.isAudioElement(element)) continue;
        const audioSource = element.source;
        if (!audioSource || this.audioTracksData[audioSource]) continue;
        this.loadWaveformForSource({
          source: audioSource,
          name: element.name ?? '',
        });
      }

      await this.trackManager.lockVideoTrackOnStateChange(state.elements);
    });

    this.renderer = renderer;
    return true;
  }

  async refreshSourceAndPlay() {
    // Exit composition mode on play so the full timeline (graphics and other
    // layers hidden while a composition is active) is visible during playback.
    // Scope only: `deselectActiveComposition` would also clear
    // `activeElementIds`, which empties whatever panel the element had open
    // (PLAT-11402). Read the flags first — the reset below nulls them.
    if (this.compositionScopeService.hasScopedComposition()) {
      // Came free with the `setActiveElements` this used to route through.
      // DeleteActiveElementService's guard keys off it.
      this.selectedTrack = -1;
    }
    await this.exitCompositionScope();

    // Force write to renderer if we're pending.
    this.rootStore.videoSourceStore.flushChanges();

    // PLAT-11119 — playback runs forward from the playhead, so the window has
    // to reach it before the renderer starts rather than 500ms into the run.
    await this.rootStore.editorStore.ensureWindowCovers(this.time);

    this.renderer?.play();
  }

  async loadWaveformForSource(source: { source: string; name: string }) {
    this.audioTracksData[source.source] = 'loading';
    try {
      const { waveformJson, duration } =
        await this.datoClientStore.transcriptRepository.fetchWaveformDataForAudioByUrl(
          source.source,
        );
      runInAction(() => {
        this.audioTracksData[source.source] = {
          waveform: WaveformData.create(waveformJson),
          originalTrackDuration: duration,
        };
      });
    } catch (err) {
      console.log('error loading waveform', err);
      delete this.audioTracksData[source.source];
    }
  }

  async onRendererTimeChange(rawTime: number) {
    if (this.isScrubbing) return;
    const time = this.normalizeRendererTime(rawTime);
    const shouldApplyVolumeKeyPoints =
      this.isPlaying && Math.abs(time - this.keyPointsLastUpdateTime) > 1;
    if (time < this.time - 1 || Math.abs(time - this.smootherTime) > 1) {
      runInAction(() => (this.smootherTime = time));
    }
    runInAction(() => (this.time = time));
    if (!shouldApplyVolumeKeyPoints) return;
    // change the audio of clips dynamically based on VolumeKeyPoints.
    // Scaled by the player-level master volume so this loop doesn't stomp
    // a mute set via the transport bar's volume control.
    this.state?.elements.forEach(element => {
      const volumeKeyPoints = getVolumeKeyPointsOf(this, element);
      const volume = interpolateVolumeAtTime(element, time, volumeKeyPoints);
      if (volume == null) return;
      this.renderer?.setModifications({
        [`${element.source.id}.volume`]:
          volume * getMasterPlaybackVolumeFactor(),
      });
    });
    runInAction(() => (this.keyPointsLastUpdateTime = time));
  }

  private normalizeRendererTime(time: number): number {
    const duration = this.duration;
    if (time > duration) {
      return duration;
    }

    const frameRate = this.timelineStore.framerate;
    if (!frameRate || frameRate <= 0) {
      return Math.max(time, 0);
    }

    const frameDuration = 1 / frameRate;
    if (Math.abs(duration - time) <= frameDuration / 2) {
      return duration;
    }

    return Math.max(time, 0);
  }

  getMaxTrack(): number {
    const tracks = Array.from(
      this.rootStore.editorStore.tracksMap.keys(),
    ).filter(track => track < KARAOKE_TRACK_NUMBER);
    return Math.max(...tracks, 1);
  }

  async setTime(
    time: number,
    resetAnimations: boolean = false,
    forceSnap: boolean = false,
  ): Promise<void> {
    // make sure time is on a single frame
    time = this.snapTime(time, forceSnap);
    time = Math.ceil(time * 10000) / 10000;
    this.time = time;
    if (resetAnimations) {
      runInAction(() => (this.smootherTime = time));
    }
    // PLAT-11119 — the renderer only holds the windowed slice of the timeline,
    // so a seek outside it lands on footage it was never sent. Rebuild the
    // window around the destination first. Skipped while scrubbing: a drag
    // issues seeks continuously and has its own debounced window reaction.
    if (!this.isScrubbing) {
      // Awaiting here lets a later seek overtake an earlier one whose window is
      // still rebuilding, and the loser would land last and win. Seeks were
      // issued synchronously before this guard existed, so only the newest may
      // command the renderer.
      const seek = ++this._seekSeq;
      await this.rootStore.editorStore.ensureWindowCovers(time);
      if (seek !== this._seekSeq) return;
    }
    await this.renderer?.setTime(time);
  }

  setSnapGuide(visible: boolean, time: number = 0, boundary?: ElementBoundary) {
    const isPlayheadBoundary =
      boundary?.type === 'reference' &&
      boundary.elementId === PLAYHEAD_SNAP_BOUNDARY_ID;

    this.isPlayheadSnapHighlighted = Boolean(visible && isPlayheadBoundary);
    this.isSnapGuideVisible = visible && !isPlayheadBoundary;
    this.snapGuideTime = time;
  }

  snapTime(time: number, forceSnap: boolean = false): number {
    // can get current video frame rate via this.currentVideo.videoSource.frame_rate
    // but not sure if that changes when different video is set as active element,
    // i.e. b-roll video
    if (forceSnap) {
      return (
        Math.round(time * this.timelineStore.framerate) /
        this.timelineStore.framerate
      );
    }
    return time;
  }

  /**
   * Creates pre-calculated snap boundaries for efficient drag operations
   * @param activeElementIds - IDs of currently active/dragged elements to exclude from snapping
   * @param snapThreshold - Maximum distance in seconds to snap (default: 0.1s)
   * @returns Pre-calculated boundaries for efficient snapping
   */
  createSnapBoundariesForDrag(
    activeElementIds: string[] = [],
    referenceTimes: Array<number | { time: number; id?: string }> = [],
  ): ElementBoundary[] {
    const elements = this.rootStore.editorStore.allElementStates;
    const frameDurationSeconds =
      this.timelineStore.framerate && this.timelineStore.framerate > 0
        ? 1 / this.timelineStore.framerate
        : 1 / 60;

    const normalizedReferences = [
      { time: this.time, id: PLAYHEAD_SNAP_BOUNDARY_ID },
      ...referenceTimes,
    ]
      .map((ref, index) =>
        typeof ref === 'number'
          ? { time: ref, id: `reference-extra-${index}` }
          : {
              time: ref.time,
              id: ref.id ?? `reference-extra-${index}`,
            },
      )
      .filter(
        reference =>
          typeof reference.time === 'number' && Number.isFinite(reference.time),
      );

    return createSnapBoundaries(
      elements,
      activeElementIds,
      normalizedReferences,
      frameDurationSeconds,
    );
  }

  async setDuration(time: number | null): Promise<void> {
    if (time != null) {
      runInAction(() => (this.duration = time));
    }

    this.rootStore.videoSourceStore.setDuration(time);
  }

  get isCurrentVideoTheLatest(): boolean {
    return !!(
      this.currentVideo &&
      this.currentVideoVersions?.find(v => v.meta.is_current)?.versionId ===
        this.currentVideo?.versionId
    );
  }

  /**
   * PLAT-10816 — an undo that crosses a language change restores the captions but
   * not `?lang=`, leaving the picker and the translated transcript on the old
   * language. The URL belongs to `LeftSidePanel`, so reuse the event it already
   * listens for rather than reaching into the router from a store.
   */
  private broadcastRestoredCaptionLanguage(
    snapshot: KaraokeLanguageSnapshot,
  ): void {
    // Nothing to sync for a snapshot predating the language capture.
    if (!snapshot.karaokeActiveLanguage) return;
    window.dispatchEvent(
      new CustomEvent(CAPTIONS_LANGUAGE_CHANGED_EVENT, {
        detail: {
          // What the load actually landed on, not what the snapshot asked for:
          // hydration falls back to the source variant when the requested
          // language has no entry, and `?lang=` must agree with the captions.
          language: resolveRestoredUiLanguage(this.rootStore.karaokeStore),
        },
      }),
    );
  }

  saveVideoStateSnapshot(
    into: 'undo' | 'redo' = 'undo',
    label?: string,
    skipSavingCheck: boolean = false,
  ) {
    // Prevent autosave if video is still loading or already saving
    if (
      !this.isVideoCreatorReady ||
      ((this.isSaving || this.isBackgroundSaving) && !skipSavingCheck)
    ) {
      return;
    }

    // saveStoryAndVideo/prepareCurrentVideoForSave are async,
    // currentVideo.videoSource and karaokeData will get stale
    //
    // PLAT-11387 — the transcript must come from the SAME read as the
    // videoSource. `TranscriptionStore` is fed by editor notifications, which
    // `executeInBatch` holds until a batch ends, and one Remove Text fans out
    // into several commands. A snapshot taken between them would pair a
    // videoSource that has moved on with a transcript that has not; restoring
    // that pair re-homes tokens onto the wrong elements, and the next edit
    // brings deleted text back.
    const eagerSources = this.rootStore.editorStore.exportProject().sources;
    const eagerVideoSource = eagerSources.videoSource;
    const eagerTranscript = eagerSources.transcript;
    // Capture karaoke data eagerly to avoid race condition with async prepareCurrentVideoForSave
    const eagerKaraokeData =
      this.rootStore.karaokeStore.getKaraokeData() ?? undefined;
    // Capture scenes eagerly so undo snapshots stay in sync with the current timeline
    const eagerScenes = this.rootStore.editorStore.getScenesData();

    if (!this.currentVideo?.id && !this.isSaving && !this.isBackgroundSaving) {
      this.saveStoryAndVideo();
    } else if (
      into === 'undo' &&
      this.isCurrentVideoTheLatest &&
      !this.isSaving &&
      !this.isBackgroundSaving &&
      Date.now() - +new Date(this.currentVideo?._publishedAt || 0) >
        AUTOSAVE_INTERVAL
    ) {
      this.saveStoryAndVideo(false, true, false, true);
    } else {
      this.prepareCurrentVideoForSave(true);
    }

    const videoSnapshot: VideoStateSnapshot = {
      ...this.getSafeCurrentVideoCopy(),
      videoSource: eagerVideoSource,
      karaokeData: eagerKaraokeData,
      scenes: eagerScenes,
      // PLAT-10816 — must come after the copy above, which carries a possibly
      // stale `karaokeDataByLanguage` from `currentVideo`.
      ...captureKaraokeLanguageState(this.rootStore.karaokeStore),
      transcriptionSnapshot: {
        elements: eagerTranscript as TranscriptData['elements'],
      },
      label,
    };

    if (into === 'undo') {
      this.videoSnapshots.push(videoSnapshot);
      //console.log('Saved video snapshot for undo:', label, toJS(videoSnapshot));
      if (this.videoSnapshots.length > MAX_UNDO_REDO_STACK_SIZE) {
        this.videoSnapshots.shift();
        this.undoStack.shift();
      }
      this.redoSnapshots = [];
      this.redoStack = [];
      this.undoStack.push({
        undoCommand: async () => {
          // we want to skip the saving check here because otherwise the redo
          // snapshot won't be set if previous saveStoryAndVideo is in progress
          this.saveVideoStateSnapshot('redo', label, true);

          const videoSnapshot = this.videoSnapshots.pop();
          if (!videoSnapshot) {
            console.error('Undo command failed: snapshot is empty');
            return;
          }

          this.isLoading = true;
          try {
            // PLAT-10816 — the load hydrates karaoke by preferring
            // `activeLanguage`, so point it at the snapshot's language first or
            // the restore falls back to the source variant. Survives the
            // `karaokeStore.reset()` inside `EditorStore.loadProject`, which
            // clears lines and flags but not the language state.
            seedKaraokeLanguageFromSnapshot(
              this.rootStore.karaokeStore,
              videoSnapshot,
            );
            await this.renderVideo(
              videoSnapshot,
              false,
              DEFAULT_PREVIEW_VIDEO_RES,
            );
            this.broadcastRestoredCaptionLanguage(videoSnapshot);
            //this.textBrandingService.loadDefaultTemplateOnVideoLoad();
            try {
              this.rootStore.editorStore?.editor?.clearHistory();
            } catch (err) {
              logger.error(
                'Undo command: clearing editor history failed',
                extractErrorInfo(err),
              );
            }
          } finally {
            this.isLoading = false;
          }
        },
        redoCommand: async () => {
          const videoSnapshotRedo = this.redoSnapshots.pop();
          if (!videoSnapshotRedo) {
            console.error('Redo command failed: snapshot is empty');
            return;
          }

          this.isLoading = true;
          this.videoSnapshots.push(videoSnapshot);
          try {
            // See the note in `undoCommand` above.
            seedKaraokeLanguageFromSnapshot(
              this.rootStore.karaokeStore,
              videoSnapshotRedo,
            );
            await this.renderVideo(
              videoSnapshotRedo,
              false,
              DEFAULT_PREVIEW_VIDEO_RES,
            );
            this.broadcastRestoredCaptionLanguage(videoSnapshotRedo);
            //this.textBrandingService.loadDefaultTemplateOnVideoLoad();
            try {
              this.rootStore.editorStore?.editor?.clearHistory();
            } catch (err) {
              logger.error(
                'Redo command: clearing editor history failed',
                extractErrorInfo(err),
              );
            }
          } finally {
            this.isLoading = false;
          }
        },
        activeElementIds: this.activeElementIds,
        selectedTrack: this.selectedTrack,
      });
    } else {
      this.redoSnapshots.push(videoSnapshot);
    }
  }

  openElementSidebar(elementIds: string[]) {
    if (elementIds.length !== 1) return;
    const elementId = elementIds[0];
    const rawElement = this.renderer
      ?.getElements()
      ?.find(e => e.source.id === elementId);

    if (!rawElement) return;

    // Route the sidebar by the top-level timeline element that contains the
    // selection (e.g. the composition a graphics element belongs to).
    // Exception: scene-based elements.
    // Fall back to the element itself.
    const element =
      this.rootStore.editorStore.getTopLevelElementStateContaining(elementId) ??
      rawElement;
    this.routeSidebarForElement(element);
  }

  /**
   * Set the active sidebar tab + sub-menu for a selection, keyed off the
   * top-level timeline element it belongs to. Shared by `openElementSidebar`
   * (renderer selection) and `setActiveElement` (direct timeline click) so the
   * element-type → tab mapping lives in one place.
   */
  private routeSidebarForElement(routingElement: ElementState) {
    if (routingElement.source.track === KARAOKE_TRACK_NUMBER) {
      this.karaokeSubMenu = KaraokeCard.styles;
      this.sidebarOptions = SidebarOption.karaoke;
    } else if (this.isLogoElement(routingElement)) {
      this.sidebarOptions = SidebarOption.media;
      this.mediaSubMenu = MediaCard.photo;
    } else if (this.isOriginalVideoElement(routingElement.source)) {
      this.sidebarOptions = SidebarOption.media;
      this.mediaSubMenu = MediaCard.video;
    } else if (this.isVideoElement(routingElement)) {
      this.sidebarOptions = SidebarOption.media;
      this.mediaSubMenu = MediaCard.video;
    } else if (this.isRegularImageElement(routingElement)) {
      this.sidebarOptions = SidebarOption.media;
      this.mediaSubMenu = MediaCard.photo;
    } else if (routingElement.source.type === 'audio') {
      this.sidebarOptions = SidebarOption.media;
      this.mediaSubMenu = MediaCard.music;
    } else if (routingElement.source.type === 'text') {
      this.sidebarOptions = SidebarOption.design;
      this.designSubMenu = DesignCard.text;
    } else if (routingElement.source.type === 'shape') {
      this.sidebarOptions = SidebarOption.design;
      this.designSubMenu = DesignCard.shape;
    } else if (routingElement.source.type === 'composition') {
      this.sidebarOptions = SidebarOption.design;
      this.designSubMenu = DesignCard.composition;
    } else {
      this.sidebarOptions = SidebarOption.editing;
    }
  }

  lockAwareSetActiveElement(
    elementId: string,
    shiftDown: boolean = false,
    doubleClick: boolean = false,
  ) {
    const elementState = this._findElementById(elementId);
    if (elementState) {
      // prefer this method as it handles locking
      this.setActiveElement(elementState, shiftDown, doubleClick);
    } else {
      this.setActiveElements(elementId);
    }
  }

  setActiveElement(
    element: ElementState,
    shiftDown: boolean = false,
    doubleClick: boolean = false,
  ) {
    element = this.resolveElementForSelection(element);
    this.compositionScopeService.setActiveCompositionId(null);

    const isPunchlistItem = this.punchListManager.punchListData?.some(
      p => element.source.id === p.id,
    );

    const imageElement = this.getImageElement(element);
    const elementSource = imageElement.source;

    if (isPunchlistItem && (!elementSource.source || doubleClick)) {
      const elementForReplacement =
        this.rootStore.editorStore.getElementStateById(element.source.id);
      if (!elementForReplacement) {
        logger.warn(
          'TYPE_ERROR: elementForReplacement is undefined when opening punchlist photo replacement',
        );
        throw new Error('Element for replacement not found in state');
      }

      this.openPhotoElementReplacementModal = {
        element: elementForReplacement,
        isForPunchlist: true,
      };
    }

    // Moved up before setActiveElements so that lock state won't get lost in reframing mode
    // `lock: true` causes active element to be non-interactable
    if (this.isOriginalVideoElement(element.source)) {
      this.trackManager.unLockVideoTrack();
      if (doubleClick) {
        this.openReframeVideoModal = true;
      }
    } else if (!this.trackManager.isVideoTrackLocked(this.state?.elements)) {
      this.trackManager.lockVideoTrack();
    }

    if (shiftDown && this.activeElementIds.length > 0) {
      if (this.activeElementIds.includes(element.source.id)) {
        this.setActiveElements(
          ...this.activeElementIds.filter(id => id !== element.source.id),
        );
        return;
      }

      const allElements = this.rootStore.editorStore.allElementStates;
      const currentActiveElement = allElements.find(
        el => el.source.id === this.activeElementIds[0],
      );
      if (
        !currentActiveElement ||
        currentActiveElement.track !== element.track ||
        !this.isOriginalVideoElement(element.source)
      ) {
        this.setActiveElements(...this.activeElementIds, element.source.id);
        return;
      }

      let elementsInBetween = [];
      if (currentActiveElement.globalTime < element.globalTime) {
        elementsInBetween = allElements.filter(
          el =>
            el.globalTime + el.duration > currentActiveElement.globalTime &&
            el.globalTime < element.globalTime + element.duration,
        );
      } else {
        elementsInBetween = allElements.filter(
          el =>
            el.globalTime <
              currentActiveElement.globalTime + currentActiveElement.duration &&
            el.globalTime + el.duration > element.globalTime,
        );
      }
      this.setActiveElements(
        ...new Set([
          ...this.activeElementIds,
          ...elementsInBetween.map(el => el.source.id),
          element.source.id,
        ]),
      );
    } else if (shiftDown) {
      const elementsInBetween =
        this.rootStore.editorStore.allElementStates.filter(
          el =>
            el.globalTime + el.duration > element.globalTime &&
            el.globalTime < element.globalTime + element.duration,
        );
      this.setActiveElements(...elementsInBetween.map(el => el.source.id));
    } else {
      this.setActiveElements(element.source.id);
    }

    // Route the sidebar by the top-level timeline element that contains the
    // selection (e.g. the composition a graphics element belongs to).
    // Exception: scene-based elements.
    // Fall back to the element itself.
    const routingElement =
      this.rootStore.editorStore.getTopLevelElementStateContaining(
        element.source.id,
      ) ?? element;
    this.routeSidebarForElement(routingElement);

    // PLAT-10391: double-clicking a timeline element toggles its settings panel
    // — open it to show the element's settings, or close it again if it's
    // already open (the rail icon stays custard since the element remains
    // selected). Single-click only routes the active tab and leaves the panel
    // as-is. Skip when the double-click already opens a dedicated modal — the
    // reframe editor for the primary video or photo replacement for punchlist
    // items — and for audio/music elements, where a double-click adds a volume
    // keypoint instead of toggling the panel.
    if (
      doubleClick &&
      !this.isOriginalVideoElement(element.source) &&
      !isPunchlistItem &&
      !areVolumeKeyPointsSupportedBy(element)
    ) {
      this.rootStore.layoutStore.toggleSidebarExpanded();
    }
  }

  private resolveElementForSelection(element: ElementState): ElementState {
    // Find composition, since that's what the canvas click would select,
    // onActiveElementsChange will enter this composition and select inner video
    // child properly (unless there are multiple children).

    const resolvedRepresentation =
      this.sceneLayoutSelectionManager.resolveElementForActivation(element);

    const resolved =
      this.sceneLayoutSelectionManager.resolveElementToComposition(
        resolvedRepresentation,
      );

    if (resolved.source.id !== element.source.id) {
      return resolved;
    }

    return element;
  }

  async setActiveElements(...elementIds: string[]): Promise<void> {
    const selectable = elementIds;
    this.activeElementIds = selectable;
    this.selectedTrack = -1;

    await this.renderer?.setActiveComposition(
      this.compositionScopeService.getActiveCompositionId(),
    );
    await this.renderer?.setActiveElements(selectable);
  }

  getActiveElement(): ElementState | undefined {
    if (!this.renderer || this.activeElementIds.length === 0) {
      return undefined;
    }

    const id = this.activeElementIds[0];
    return this._findElementById(id);
  }

  private _findElementById(id: string): ElementState | undefined {
    const fromEditor = this.rootStore.editorStore.getElementStateById(id);
    if (fromEditor) {
      return fromEditor;
    }

    // Selection (setActiveElement → resolveElementForSelection) can store a
    // synthetic scene-layout *representation* id (e.g.
    // `<id>_scene_<sceneId>_main_part0`) that only exists in the renderer's
    // video source, not the editor timeline — so the editor lookup above
    // returns undefined and the element appears unselected (empty sidebar).
    // Fall back to the video source, mirroring the resolver
    // SceneLayoutSelectionManager uses at selection time, so getActiveElement()
    // can always read back whatever selection stored.
    const elementSource = this.rootStore.videoSourceStore.findElementById(id);
    return elementSource ? mapToElementState(elementSource) : undefined;
  }

  getRealActiveElement(): ElementState | undefined {
    const activeElement = this.getActiveElement();
    if (!activeElement) {
      return undefined;
    }
    // Resolve the active representation to its underlying (parent) element via
    // the editor model: a laid-out element's parent is an editor element whose
    // raw id is not in the windowed renderer source, so the default source-only
    // seeker would throw on a windowed-out parent.
    return this.sceneLayoutSelectionManager.resolveElementToParent(
      activeElement,
      id => this.rootStore.editorStore.getElementStateById(id),
    );
  }

  pickOriginalVideoTrack = (source: VideoSource): number | null => {
    const originalVideoElement = source.elements?.find((el: unknown) =>
      this.isOriginalVideoElement(el),
    );
    return originalVideoElement ? originalVideoElement.track : null;
  };

  isSelectedVolumeKeyPoint(point: VolumeKeyPoint): boolean {
    return (
      point.time === this.selectedVolumeKeyPoint?.time &&
      point.value === this.selectedVolumeKeyPoint?.value
    );
  }

  selectVolumeKeyPoint(point: VolumeKeyPoint | null): void {
    this.selectedVolumeKeyPoint = point;
  }

  deleteSelectedVolumeKeyPoint(elementId: string): void {
    if (!this.selectedVolumeKeyPoint) {
      return;
    }
    const oldVolumeKeyPoints =
      toJS(this.currentVideo?.extraElementData?.[elementId]?.volumeKeyPoints) ||
      [];
    let newVolumeKeyPoints = oldVolumeKeyPoints.filter(
      (p: VolumeKeyPoint) => !this.isSelectedVolumeKeyPoint(p),
    );
    if (newVolumeKeyPoints.length === 2) {
      newVolumeKeyPoints = [];
    }
    this.applyVolumeKeyPoints(elementId, newVolumeKeyPoints);
    this.selectedVolumeKeyPoint = null;
    // onRendererTimeChange throttles to once per second of playback and skips
    // while paused, so push the new value to the renderer immediately here.
    this.applyVolumeAtCurrentTimeToRenderer(elementId);
  }

  deleteAllVolumeKeyPoints(elementId: string): void {
    this.applyVolumeKeyPoints(elementId, []);
    this.selectedVolumeKeyPoint = null;
    this.applyVolumeAtCurrentTimeToRenderer(elementId);
  }

  // Push a volume value to the renderer and flush its audio buffer so the
  // change is audible immediately. Used by line-drag onStop and anywhere else
  // that needs to bypass VideoSourceStore's debounced sync.
  applyVolumeForElement(elementId: string, volume: number): void {
    if (!Number.isFinite(volume)) return;
    // Scaled by the player-level master volume — modifications are the
    // audible overlay, the element's source keeps the authored value.
    this.renderer?.setModifications({
      [`${elementId}.volume`]: volume * getMasterPlaybackVolumeFactor(),
    });
    this.flushRendererAudioBuffer();
  }

  // setModifications alone takes effect only on new frames — the renderer
  // keeps playing pre-buffered audio at the old volume for several seconds.
  // Creatomate flushes its audio buffer on pause/play, so re-applying the
  // active element selection and (when playing) toggling pause/play forces
  // it to re-fetch audio at the new value.
  private flushRendererAudioBuffer(): void {
    const ids = this.activeElementIds.slice();
    this.renderer?.setActiveElements(ids);
    this.renderer?.setActiveComposition(null);
    if (this.isPlaying) {
      this.renderer?.pause();
      this.renderer?.play();
    }
  }

  // Push the correct audio volume to the renderer right now, bypassing
  // onRendererTimeChange's playback-only / once-per-second throttle.
  // <2 keypoints uses the element's authoritative source.volume; otherwise
  // interpolates at the current playhead time (matching the runtime loop).
  private applyVolumeAtCurrentTimeToRenderer(elementId: string): void {
    const element = this.state?.elements.find(el => el.source.id === elementId);
    if (!element) return;

    const volumeKeyPoints = getVolumeKeyPointsOf(this, element);
    if (!volumeKeyPoints || volumeKeyPoints.length < 2) {
      const numericVolume = parsePercent(element?.source?.volume);
      if (numericVolume == null) return;
      this.applyVolumeForElement(elementId, numericVolume);
      return;
    }

    const volume = interpolateVolumeAtTime(element, this.time, volumeKeyPoints);
    if (volume == null) return;
    this.applyVolumeForElement(elementId, volume);
  }

  async applyVolumeKeyPoints(
    elementId: string,
    volumeKeyPoints: VolumeKeyPoint[],
    undo: boolean = true,
  ) {
    if (!this.currentVideo) {
      throw new Error('Cannot applyVolumeKeyPoints: currentVideo not set');
    }
    const oldVolumeKeyPoints = toJS(
      this.currentVideo.extraElementData[elementId]?.volumeKeyPoints,
    );
    const element = this.rootStore.editorStore.getElementStateById(elementId);
    const newVolumeKeyPoints = element
      ? formatAndFixOffTimeVolumeKeyPoints(volumeKeyPoints, element.duration)
      : [];
    if (
      newVolumeKeyPoints.length === oldVolumeKeyPoints?.length &&
      newVolumeKeyPoints.every(
        (kp, idx) =>
          kp.time === oldVolumeKeyPoints[idx].time &&
          kp.value === oldVolumeKeyPoints[idx].value,
      )
    ) {
      return;
    }

    if (undo) {
      this.saveVideoStateSnapshot('undo', 'modifying volume keyframes');
    }

    // Replace extraElementData wholesale rather than mutating a key in place:
    // undo snapshots take a shallow copy of currentVideo and share this object
    // by reference, so an in-place write would retroactively corrupt the
    // pre-change snapshot with the new keypoints.
    this.currentVideo.extraElementData = {
      ...this.currentVideo.extraElementData,
      [elementId]: {
        ...(this.currentVideo.extraElementData[elementId] || {}),
        volumeKeyPoints: newVolumeKeyPoints,
      },
    };
    this.volumeKeyPointsRevision++;
  }

  private adjustCurrentVideoWidthToAspectRatio(aspectRatio: string) {
    if (!this.currentVideo) {
      throw new Error(
        'Cannot adjust current video width to aspect ratio: currentVideo not set',
      );
    }
    const [w, h] = aspectRatio.split(':').map(n => parseInt(n));
    const width = this.currentVideo.videoSource.height * (w / h);
    this.currentVideo.videoSource.width = width;
  }

  isVideoElement(element: ElementState): boolean {
    return (
      this.isVideoElementComposition(element) ||
      element.source?.type === 'video'
    );
  }

  getVideoElement(element: ElementState): ElementState | undefined {
    if (element.source.type === 'video') {
      return element;
    }
    if (element.source.type === 'composition') {
      return element.elements?.find(e => e.source.type === 'video');
    }
    return undefined;
  }

  isRegularImageElement(element: ElementState): boolean {
    return this.isImageElement(element) && !this.isLogoElement(element);
  }

  async adjustElementSourceToAspectRatio(
    elementSource: Record<string, unknown>,
  ): Promise<boolean> {
    const element = mapToElementState(elementSource);
    const isVideo = this.isVideoElement(element);
    const isRegularImage = this.isRegularImageElement(element);
    if (!isRegularImage && !isVideo) {
      return false;
    }
    const adjustments = await this.getElementAdjustmentsForAspectRatio(element);
    elementSource.x = adjustments.x;
    elementSource.y = adjustments.y;
    elementSource.width = adjustments.width;
    elementSource.height = adjustments.height;
    return true;
  }

  private async getElementAdjustmentsForAspectRatio(
    element: ElementState,
  ): Promise<Record<string, string>> {
    const result = {
      x: '50%',
      y: '50%',
      width: '100%',
      height: '100%',
    };
    if (['fill', 'contain'].includes(element.source?.fit)) {
      return result;
    }
    if (!this.currentVideo) {
      throw new Error(
        'Cannot get element adjustments for aspect ratio: currentVideo not set',
      );
    }
    const prevDimensions = {
      width: VideoDimensionsWidth.AR_16_9,
      height: VideoDimensionsHeight.AR_16_9,
    };
    const elementWidth = 100;
    const newWidth = Math.max(
      100,
      Number(
        (
          (prevDimensions.width * elementWidth) /
          this.currentVideo.videoSource.width
        ).toFixed(4),
      ),
    );
    result.width = `${newWidth}%`;
    const naturalDimensions =
      await this.getNaturalDimensionsForElement(element);
    if (naturalDimensions) {
      const naturalAspectRatio =
        naturalDimensions.width / naturalDimensions.height;
      const prevAspectRatio = prevDimensions.width / prevDimensions.height;
      result.width = `${(newWidth * naturalAspectRatio) / prevAspectRatio}%`;
    }
    return result;
  }

  private async getNaturalDimensionsForElement(
    element: ElementState,
  ): Promise<NaturalDimensionsMaybe> {
    if (element.source.arbor_naturalDimensions) {
      return element.source.arbor_naturalDimensions;
    }
    if (this.isVideoElement(element)) {
      return getVideoNaturalDimensions(element.source?.source);
    }
    if (this.isRegularImageElement(element)) {
      return getImageNaturalDimensions(
        this.getImageElement(element).source?.source,
      );
    }
    return null;
  }

  async getNaturalMediaDimensions(): Promise<
    Record<string, NaturalDimensions>
  > {
    const result: Record<string, NaturalDimensions> =
      this.preloadedNaturalDimensions;
    const exportedSource =
      this.rootStore.editorStore.exportProject().sources.videoSource;
    const elements = exportedSource?.elements;

    if (!Array.isArray(elements)) {
      return result;
    }

    for (const elementSource of elements) {
      // Skip elements that don't have a source URL
      if (!elementSource) continue;

      // Skip non-media elements
      const element = mapToElementState(elementSource);
      const isVideo = this.isVideoElement(element);
      const isImage = this.isImageElement(element); // both regular and logos
      const isComposition = this.isImageElementComposition(element);

      if (!isImage && !isVideo && !isComposition) {
        continue;
      }

      // Handle regular image and video elements
      if ((isImage || isVideo) && element.source.source) {
        if (result[element.source.source]) {
          continue;
        }

        try {
          const naturalDimensions =
            await this.getNaturalDimensionsForElement(element);
          if (naturalDimensions) {
            result[element.source.source] = {
              width: naturalDimensions.width,
              height: naturalDimensions.height,
            };
          }
        } catch (error) {
          console.warn(
            `Failed to get natural dimensions for element ${elementSource.id}`,
            error,
          );
        }
      }

      // Handle composition elements - extract image sources from child elements for Black Frame compositions that might be converted to images
      if (isComposition && element.elements) {
        for (const childElement of element.elements) {
          if (
            childElement.source?.type === 'image' &&
            childElement.source?.source &&
            typeof childElement.source.source === 'string'
          ) {
            const imageSource = childElement.source.source;
            if (result[imageSource]) {
              continue;
            }

            try {
              const naturalDimensions =
                await getImageNaturalDimensions(imageSource);
              if (naturalDimensions) {
                result[imageSource] = {
                  width: naturalDimensions.width,
                  height: naturalDimensions.height,
                };
              }
            } catch (error) {
              console.warn(
                `Failed to get natural dimensions for composition child image ${imageSource}`,
                error,
              );
            }
          }
        }
      }
    }

    return result;
  }

  private async preloadNaturalDimensions() {
    this.preloadedNaturalDimensions = await this.getNaturalMediaDimensions();
  }

  async adjustModificationsToAspectRatio(
    modifications: Record<string, unknown>,
    elementSource: ElementState['source'],
    insertId: boolean = true,
  ): Promise<Record<string, unknown>> {
    const elementSourceOutput: Record<string, unknown> = { ...elementSource };
    if (await this.adjustElementSourceToAspectRatio(elementSourceOutput)) {
      const { id } = elementSource;
      const { x, y, width, height } = elementSourceOutput;
      modifications[insertId ? `${id}.x` : 'x'] = x;
      modifications[insertId ? `${id}.y` : 'y'] = y;
      modifications[insertId ? `${id}.width` : 'width'] = width;
      modifications[insertId ? `${id}.height` : 'height'] = height;
    }
    return modifications;
  }

  // gets the source of compositions properly
  getElementSource(element: ElementState): Record<string, unknown> {
    // TODO: Replace with videoSourceStore function?
    return this.renderer?.getSource(element) || element.source;
  }

  getImageElement(element: ElementState) {
    if (element.source?.type === 'composition') {
      const image_element = element?.elements?.find(
        e => e.source?.type === 'image',
      );
      if (image_element) return image_element;
    }
    return element;
  }

  isImageElementComposition(
    element: ElementState | Record<string, any>,
  ): boolean {
    // New logic: a composition that is NOT an "image element composition" will have
    // an extraElementData meta object keyed by element.source.id with { type: 'composition' }.
    // So we *exclude* those. If such a meta object exists -> return false.
    // Otherwise, treat compositions with image content as image element compositions.

    const sourceObj: any = (element as any).source;
    const isCompositionType =
      (typeof sourceObj === 'string' &&
        (element as any).type === 'composition') ||
      sourceObj?.type === 'composition';
    if (!isCompositionType) return false;

    const meta = getElementMetaObject(
      this.currentVideo,
      sourceObj?.id || (element as any).id,
    );
    if (meta && meta.type === 'composition') {
      return false;
    }
    // Only treat as image element composition if it has a child with type 'image'
    return Array.isArray((element as any).elements)
      ? (element as any).elements.some((e: any) => e.source?.type === 'image')
      : false;
  }

  isVideoElementComposition(
    element: ElementState | Record<string, any>,
  ): boolean {
    // Mirror logic from isImageElementComposition
    const sourceObj: any = (element as any).source;
    const isCompositionType =
      (typeof sourceObj === 'string' &&
        (element as any).type === 'composition') ||
      sourceObj?.type === 'composition';
    if (!isCompositionType) return false;

    const meta = getElementMetaObject(
      this.currentVideo,
      sourceObj?.id || (element as any).id,
    );
    if (meta && meta.type === 'composition') {
      return false;
    }
    // Only treat as video element composition if it has a child with type 'video'
    return Array.isArray((element as any).elements)
      ? (element as any).elements.some((e: any) => e.source?.type === 'video')
      : false;
  }

  isLogoElement(element: ElementState): boolean {
    return (
      this.isImageElement(element) &&
      !!(
        this.currentVideo?.extraElementData[
          `logo_el_${element.source.id}`
        ] as ExtraElementData
      )?.isLogo
    );
  }

  isImageElement(element: ElementState) {
    return (
      this.isImageElementComposition(element) ||
      element.source?.type === 'image'
    );
  }

  private async resetPhotoHighlights() {
    const timelineElements = this.renderer?.getElements();
    for (let item of this.punchListManager.punchListData || []) {
      const id = item.id;
      if (!id) {
        logger.warn('TYPE_ERROR: punch list item missing id', item);
        continue;
      }
      const element = timelineElements?.find(el => el.source.id === id);

      if (element) {
        const duration = element?.duration;
        const startTime = element.localTime;
        const endTime = startTime + duration;

        const finalElements =
          this.rootStore.transcriptionStore.getFinalTranscriptionElements();
        const startIndex = findClosestTranscriptIndexToTimestamp(
          finalElements,
          startTime,
          'ts',
        );
        const endIndex = findClosestTranscriptIndexToTimestamp(
          finalElements,
          endTime,
          'end_ts',
          'ts_lookbehind',
        );
        this.punchListManager.updatePunchListItem(id, {
          line: this.rootStore.transcriptionStore.getTextFromRange(
            startIndex,
            endIndex,
          ),
          transcriptPosition: {
            startIndex,
            endIndex,
          },
        });
      }
    }
  }

  hasPhotoHighlight(id: string) {
    return !!this.punchListManager.findPunchListItemById(id);
  }

  timeout(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  async sleep(fn: Function, delay: number, ...args: unknown[]) {
    return fn(...args);
  }

  private async waitForVideoReady(timeout: number = 30000) {
    while (this.renderQueueing || this.isLoading || !this.renderer?.state) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (timeout <= 0) {
        throw new Error('Timeout waiting for video to be ready');
      }
      timeout -= 1000;
    }
  }

  async publishVideo(
    requestedResLevel: VideoResolution,
    renderUsingChunks: boolean = false,
    /** PLAT-12204 — the page's language (`usePublishLanguage`). */
    language?: string,
  ) {
    const { renderer } = this;
    if (!renderer) {
      return;
    }

    if (!this.currentVideo) {
      throw new Error('Cannot publish video: currentVideo not set');
    }
    if (!this.story) {
      throw new Error('Cannot publish video: story not set');
    }

    const resLevel = resolvePublishResolutionLevel(
      requestedResLevel,
      this.story.originalVideo?.video,
    );

    const mainClip = this.currentVideo;

    // Retained only for analytics logging on the error path
    // (`handleRenderingError`); the fan-out creates its own per-cell publishers.
    const videoPublisher = new VideoPublisher(
      {
        story: this.story,
        organization: this.organization,
        video: mainClip,
      },
      this.rootStore.analyticsStore,
      this.rootStore.userIdentityStore,
    );
    videoPublisher.trackInit({});

    try {
      await this.waitForVideoReady();
      if (this.isError) {
        throw Error('Error occurred during video loading, cannot render');
      }

      this.renderQueueing = true;
      this.renderingStatus = 'none';
      this.renderVideoResLevel = resLevel || null;

      try {
        this.generateCaptions();
      } catch (err) {
        logger.warn('Error generating captions, supress', err);
      }

      // PLAT-10788 — publish the currently-active caption language as a
      // single-cell fan-out. `PublishFanoutService` owns the save, per-language
      // source build, record creation, fingerprints, dispatch, and tracking — and
      // sets the record language + `mirrorsToLegacy` per cell (canonical only for
      // the original). So publishing while editing a translation records a
      // NON-canonical variant and never mirrors a translated render into legacy.
      // PLAT-12204 — the page's language when the caller has it: the caption
      // config only follows the page once a captions view has opened.
      const activeLang =
        language ??
        readActiveCaptionLanguage(this.rootStore.editorStore.editor);
      const service = new PublishFanoutService(
        publishFanoutDepsFromStore(this, mainClip),
      );
      const result = await service.execute({
        languages: [activeLang],
        aspectRatios: [mainClip.aspectRatio || this.getDefaultAspectRatio()],
        resLevels: [resLevel],
        renderUsingChunks,
      });

      if (result.dispatchErrors.size > 0) {
        // The fan-out already marked the cell's record failed; surface the
        // client-side error state (analytics + `renderingStatus`).
        const [firstError] = result.dispatchErrors.values();
        this.handleRenderingError(
          firstError ?? new Error('Publish dispatch failed'),
          videoPublisher,
        );
      } else {
        this.renderingStatus = 'rendering';
      }
    } catch (error) {
      this.handleRenderingError(error, videoPublisher);
    } finally {
      this.renderQueueing = false;
    }
  }

  private generateCaptions() {
    try {
      if (!this.currentVideo) {
        throw new Error('Cannot generate captions: currentVideo not set');
      }
      if (!this.currentVideo.id) {
        logger.warn('TYPE_ERROR: currentVideo.id not set in generateCaptions');
        throw new Error('Cannot generate captions: currentVideo.id not set');
      }
      const captionService = new CaptionService(
        this,
        this.datoClientStore.storyRepository,
        this.datoClientStore.captionRepository,
        this.datoClientStore.aiPromptRepository,
      );
      captionService.generateAllCaptions(
        this.currentVideo.id,
        'Social Posts',
        this.currentVideo.transcriptionText,
      );
    } catch (error) {
      logger.warn('An error occurred when generating captions', error);
    }
  }

  /**
   * Copy publish state (rendered file + visibility + action/publish metadata)
   * from a rendering-status update onto a single in-memory video node whose id
   * matches. Returns whether it matched.
   *
   * Centralises the field copy so every place the same variant can be
   * referenced stays consistent — including the variant graph reachable from
   * `currentVideo` (`parentVideo` + both `associatedVideos` arrays) that
   * `resolveAspectRatioVariant` walks for the aspect-ratio status badge. A
   * freshly created variant inherits its `parentVideo` by reference (see
   * `createAssociatedVideo`), so a sibling published while that variant is
   * loaded must be patched here or it keeps reading as an unpublished draft.
   */
  private applyRenderingStatusToNode(
    node:
      | Pick<
          Video,
          | 'id'
          | 'videoFilePrimary'
          | 'isHidden'
          | 'lastActionJson'
          | 'lastPublishJson'
        >
      | undefined
      | null,
    update: VideoRenderingStatus,
  ): boolean {
    if (!node || node.id !== update.id) return false;
    node.videoFilePrimary = update.videoFilePrimary;
    node.isHidden = update.isHidden;
    node.lastActionJson = update.lastActionJson;
    node.lastPublishJson = update.lastPublishJson;
    return true;
  }

  /**
   * PLAT-10009: apply finished PublishRecord transitions to the legacy card
   * in memory. Tracking (cache + prune of the in-flight set) is done by
   * `PublishTrackingStore` before this is called; here we only touch the loaded
   * story/currentVideo graph for the canonical (`mirrorsToLegacy`) target. We
   * synthesize a `videoFilePrimary` from the record's S3 output (URL resolved via
   * the publish-record resources endpoint — no Video re-fetch) and reuse the
   * shared in-memory patch/notify path. Non-canonical variants (translated
   * languages) surface only in the Downloads panel.
   */
  async applyRenderedPublishRecords(records: PublishRecord[]) {
    for (const record of records) {
      if (!isPublishRecordFinished(record) || !record.mirrorsToLegacy) continue;
      try {
        const update = await this.buildVideoStatusFromRecord(record);
        if (update) this.applyFinishedVideoStatus(update);
      } catch (err) {
        logger.error(
          'Failed to apply rendered publish record',
          extractErrorInfo(err),
        );
      }
    }
  }

  /**
   * Build a `VideoRenderingStatus` from a finished canonical record. On
   * success, synthesize `videoFilePrimary` pointing at the record's durable S3
   * output (resolved to a signed URL server-side). No `muxPlaybackId` yet — the
   * S3 output isn't Mux-processed; preview via `src`/Mux-in-SF is a follow-up.
   */
  private async buildVideoStatusFromRecord(
    record: PublishRecord,
  ): Promise<VideoRenderingStatus | null> {
    if (record.publishStatus === PublishStatus.Failed) {
      return {
        id: record.videoId,
        videoStatus: VideoStatus.Error,
        videoFilePrimary: undefined,
        renderId: record.renderId ?? undefined,
        isHidden: false,
        aspectRatio: record.aspectRatio as AspectRatio,
        // Mirror the render lambda's failure write (RenderApplyResult mirror).
        lastActionJson: {
          editor: record.publishedBy ?? '',
          type: 'system_render_error',
          date: Date.now(),
        },
        lastPublishJson: undefined,
      };
    }

    const output = record.outputJson?.video;
    if (!output?.fileKey) return null;
    const resolved = await requestPublishRecordResources(record.id, ['video']);
    const url = resolved.video?.url;
    if (!url) return null;

    // `.video` must be truthy so applyFinishedVideoStatus doesn't treat this as
    // an intermediate (still-rendering) update.
    const videoFilePrimary = {
      url,
      width: output.width,
      height: output.height,
      video: {
        duration:
          output.durationMs != null ? output.durationMs / 1000 : undefined,
        mp4Url: url,
      },
      customData: { aspectRatio: record.aspectRatio },
    } as unknown as StoryVideo;

    // Mirror the render lambda's success write (RenderApplyResult mirror): both
    // `lastActionJson` (system_render_confirm) and `lastPublishJson` share one
    // `date` and a `"<minDim>p"` resolution derived from the output dims, so the
    // in-memory synthesis equals what a refresh loads from Dato.
    const publishedAtMs = record.publishedAt
      ? Date.parse(record.publishedAt)
      : Date.now();
    const resolutionStr =
      output.width && output.height
        ? `${Math.min(output.width, output.height)}p`
        : '';

    return {
      id: record.videoId,
      videoStatus: VideoStatus.Rendered,
      videoFilePrimary,
      renderId: record.renderId ?? undefined,
      isHidden: false,
      aspectRatio: record.aspectRatio as AspectRatio,
      lastActionJson: {
        editor: record.publishedBy ?? '',
        type: 'system_render_confirm',
        date: publishedAtMs,
        resolution: resolutionStr,
      },
      lastPublishJson: {
        publisher: record.publishedBy ?? '',
        // Epoch-ms number, matching the render lambda's mirror (and what a Dato
        // refresh loads). Consumed via `new Date(timestamp)`.
        timestamp: publishedAtMs,
        aspectRatio: record.aspectRatio,
        resolution: resolutionStr,
      },
    };
  }

  private applyFinishedVideoStatus(video: VideoRenderingStatus) {
    console.log(
      'Rendering status update',
      video.id,
      video.videoStatus,
      video.videoFilePrimary,
    );
    if (
      (video.videoStatus === VideoStatus.Rendered &&
        !video.videoFilePrimary?.video) ||
      video.videoStatus === VideoStatus.Rendering
    ) {
      logger.info(
        'Skipping intermediate rendering status update, waiting for final state',
        {
          videoId: video.id,
          videoStatus: video.videoStatus,
          hasVideoFile: !!video.videoFilePrimary?.video,
        },
      );
      return;
    }
    this.renderingStatus = video.videoStatus;
    let videoTitle: string | undefined;

    // find video in story other videos or their associated videos with the same id as in video rendering status
    for (const storyVideo of this.story?.otherVideos ?? []) {
      const matched =
        this.applyRenderingStatusToNode(storyVideo, video) ||
        storyVideo.associatedVideos.some(av =>
          this.applyRenderingStatusToNode(av, video),
        );
      if (matched) {
        videoTitle = storyVideo.title; // associated video doesn't contain title
        break;
      }
    }

    // Patch the loaded video and the variant graph reachable from it. The
    // subscription only watches `renderingVideoIds`, so `story.otherVideos`
    // and the `currentVideo` graph are distinct object trees — patching one
    // doesn't reflect in the other. `parentVideo` (built from a separate
    // `_allReferencingVideos` query) and its `associatedVideos` siblings are
    // what `resolveAspectRatioVariant` walks once a freshly created variant is
    // loaded, so they must be patched too or a just-published sibling keeps
    // reading as a draft in the aspect-ratio dropdown.
    this.applyRenderingStatusToNode(this.currentVideo, video);
    this.applyRenderingStatusToNode(this.currentVideo?.parentVideo, video);
    for (const av of this.currentVideo?.parentVideo?.associatedVideos ?? []) {
      this.applyRenderingStatusToNode(av, video);
    }
    for (const av of this.currentVideo?.associatedVideos ?? []) {
      this.applyRenderingStatusToNode(av, video);
    }

    // Render has finished — broadcast it so surfaces holding
    // a detached copy of this video can react by id. The clip-share modal's
    // session store deep-clones its variants (incl. ones created in-session
    // that never reach the object trees patched above), so it can't observe
    // these mutations — it listens for this event instead. See
    // `useRenderStatusObserver`.
    window.dispatchEvent(
      new CustomEvent<VideoRenderFinishedEventDetail>(
        VIDEO_RENDER_FINISHED_EVENT,
        { detail: video },
      ),
    );

    if (this.renderingStatus !== 'error') this.renderVideoResLevel = null;

    this.loadCurrentVideoVersionsHistory(0);

    this.showRenderFinishedNotification(
      video,
      videoTitle || this.currentVideo?.title,
    );
  }

  private showRenderFinishedNotification(
    renderUpdate: VideoRenderingStatus,
    videoTitleRaw: string | undefined,
  ) {
    const videoTitle = videoTitleRaw ? ` ’${videoTitleRaw}’` : '';

    let state: 'error' | 'success';
    let message: string;
    switch (renderUpdate.videoStatus) {
      case VideoStatus.Rendered:
        state = 'success';
        message = `Your video${videoTitle} was published`;
        break;
      case VideoStatus.Error:
        state = 'error';
        message = `Publishing failed${
          videoTitle ? ' of' : ''
        }${videoTitle}, please contact Arbor`;
        break;
      default:
        return;
    }

    const downloadUrl =
      state === 'success' ? renderUpdate.videoFilePrimary?.url : undefined;

    this.showToast({
      state,
      message,
      action: downloadUrl
        ? {
            label: 'Download',
            onClick: () => {
              void downloadVideo({
                url: downloadUrl,
                metadata: {
                  clipId: renderUpdate.id,
                  storyId: this.story?.id,
                  storyTitle: this.story?.title,
                  videoTitle: videoTitleRaw,
                  aspectRatio: this.getCurrentAspectRatio(),
                  sourcePlatform: 'creator-studio',
                },
              });
            },
          }
        : undefined,
    });
  }

  showToast(toast: ToastRequest): number {
    this.lastToastId += 1;
    const queued: Toast = {
      ...toast,
      id: this.lastToastId,
      delay: resolveToastDelay(toast),
    };
    this.toasts = [queued, ...this.toasts].slice(0, MAX_VISIBLE_TOASTS);
    return queued.id;
  }

  /**
   * Replace a queued toast in place, keeping its position in the stack — used
   * to turn a `pending` toast into its outcome without the row jumping. If the
   * toast is gone (dismissed by the user, or pushed off by newer ones), the
   * replacement is shown as a fresh toast: the outcome, and any action on it,
   * still matters.
   */
  updateToast(id: number, toast: ToastRequest): number {
    if (!this.toasts.some(existing => existing.id === id)) {
      return this.showToast(toast);
    }
    const updated: Toast = { ...toast, id, delay: resolveToastDelay(toast) };
    this.toasts = this.toasts.map(existing =>
      existing.id === id ? updated : existing,
    );
    return id;
  }

  dismissToast(id: number) {
    this.toasts = this.toasts.filter(toast => toast.id !== id);
  }

  clearToasts() {
    this.toasts = [];
  }

  get canUndo() {
    return this.undoStack.length > 0;
  }

  getUndoLabel() {
    return this.undoStack.at(-1)?.label || this.videoSnapshots.at(-1)?.label;
  }

  getRedoLabel() {
    return this.redoStack.at(-1)?.label || this.redoSnapshots.at(-1)?.label;
  }

  get canRedo() {
    return this.redoStack.length > 0;
  }

  get isThumbnailsLoading() {
    return this.thumbnailLoadingElements.size > 0;
  }

  get isTimelineLoading() {
    return this.isLoading || this.isThumbnailsLoading || this.isLoadingAssets;
  }

  get isVideoCreatorReady() {
    return !this.isLoading && this.stateReady;
  }

  registerThumbnailLoading(elementId: string) {
    this.thumbnailLoadingElements.add(elementId);
  }

  unregisterThumbnailLoading(elementId: string) {
    this.thumbnailLoadingElements.delete(elementId);
  }

  setAiImageFeatureFlag(enabled: boolean) {
    this.disableAiImageGenerate = !enabled;
  }

  getSourceVideoFrameRate() {
    return this.story?.originalVideo?.video?.framerate;
  }

  private getDefaultSource(params?: { videoRes: VideoResolution }) {
    const videoRes = params?.videoRes;

    let sourceUrl = this.getValidVideoSourceUrlForResLevel(
      videoRes || VideoResolution.Original,
    );

    if (!sourceUrl) {
      // Renditions pending (PLAT-11224/11228) — the editor audio (published
      // at transcription time, minutes before any video rendition) drives an
      // audio-first composition: the timeline gets a real track with a
      // waveform, playback works, and edits carry over 1:1 when the video
      // swaps in (upgradeAudioFirstComposition). Without audio yet, fall
      // back to an empty composition behind the canvas veil.
      const audioUrl = this.story?.transcription?.audio?.url;
      return {
        output_format: 'mp4',
        width: this.getDefaultWidth(),
        height: this.getDefaultHeight(),
        frame_rate: this.story?.originalVideo?.video?.framerate,
        elements: audioUrl
          ? [
              {
                id: uuid(),
                // originalVideoDuration is a template string — skip it when
                // the metadata hadn't landed ("undefined s") and let the
                // audio file's natural duration win.
                ...(this.originalVideoDuration &&
                !this.originalVideoDuration.includes('undefined')
                  ? { duration: this.originalVideoDuration }
                  : {}),
                track: 1,
                time: 0,
                type: 'video',
                source: audioUrl,
                autoplay: true,
              },
            ]
          : [],
      };
    }

    // Check if the video has chapters (concatenated videos)
    const chapters = this.story?.originalVideo?.chapters;

    let elements: Record<string, unknown>[] = [];

    // Only treat as multi-chapter if there are actually multiple chapters
    // Single chapter should be treated as a regular full video (no trim needed)
    if (chapters && chapters.length > 1) {
      // Create one element for each chapter/original video
      let currentTimelinePosition = 0;

      chapters.forEach((chapter, index) => {
        const chapterId = uuid();
        const startTime = parseFloat(
          String(chapter.start_time || chapter.start / 1000),
        );
        const endTime = parseFloat(
          String(chapter.end_time || chapter.end / 1000),
        );
        const duration = endTime - startTime;

        elements.push({
          id: chapterId,
          duration: `${duration} s`,
          track: 1,
          time: currentTimelinePosition, // Position elements sequentially on timeline
          type: 'video',
          source: sourceUrl,
          trim_start: startTime,
          trim_duration: duration,
          autoplay: true,
          // Use name to identify the chapter
          name: chapter.tags?.title || `Clip ${index + 1}`,
        });

        // Update timeline position for next element
        currentTimelinePosition += duration;
      });
    } else {
      // Fallback to single element for non-concatenated videos
      const id = uuid();
      elements = [
        {
          id,
          // Unknown metadata: omit and let the media's natural length win.
          ...(this.originalVideoDuration
            ? { duration: this.originalVideoDuration }
            : {}),
          track: 1,
          time: 0,
          type: 'video',
          source: sourceUrl,
          autoplay: true,
          // locked: true,
        },
      ];
    }

    return {
      output_format: 'mp4',
      width: this.getDefaultWidth(),
      height: this.getDefaultHeight(),
      frame_rate: this.story?.originalVideo?.video?.framerate,
      // duration: this.originalVideoDuration, - let duration autoadjust
      elements,
    };
  }

  /**
   * Depends on the story's original video aspect ratio.
   */
  getDefaultAspectRatio(): AspectRatio {
    if (this.story?.originalVideo) {
      const { width, height } = this.story.originalVideo || {};
      if (width && height) {
        if (width > height) {
          return AspectRatio.AR_16_9;
        } else if (width === height) {
          return AspectRatio.AR_1_1;
        } else {
          return AspectRatio.AR_9_16;
        }
      }
    }
    return AspectRatio.AR_16_9;
  }

  getCurrentAspectRatio(): AspectRatio {
    return this.currentVideo?.aspectRatio || this.getDefaultAspectRatio();
  }

  private getDefaultWidth(): number {
    const width = this.story?.originalVideo?.width;
    if (width) {
      return Math.min(
        Math.max(width, VideoDimensionsWidth.AR_9_16),
        VideoDimensionsWidth.AR_16_9,
      );
    }
    return VideoDimensionsWidth.AR_16_9;
  }

  private getDefaultHeight(): number {
    const height = this.story?.originalVideo?.height;
    if (height) {
      // always 720
      return Math.min(
        Math.max(height, VideoDimensionsHeight.AR_9_16),
        VideoDimensionsHeight.AR_16_9,
      );
    }
    return VideoDimensionsHeight.AR_16_9;
  }

  getFreeMediaTrack(
    type: 'audio' | 'video' | 'image',
    mediaDuration: number,
    currTime = this.time,
  ) {
    const elementsOfSameType =
      this.getEditorStore().editor.timeline.allElements.filter(el => {
        if (type !== 'image')
          return el.type === type && !this.isOriginalVideoElement(el);
        return (
          (Element.isImageElement(el) || Element.isCompositionElement(el)) &&
          !Element.isLogoElement(el)
        );
      });

    const mediaSpan = currTime + mediaDuration;

    const overlappedTracks =
      elementsOfSameType
        ?.filter(el => {
          const { time, end_time } = el;
          if (
            (time <= currTime && end_time >= currTime) ||
            (time <= mediaSpan && end_time >= mediaSpan)
          ) {
            return true;
          }
          return false;
        })
        ?.map(t => t.track) || [];

    return elementsOfSameType?.find(t => !overlappedTracks.includes(t.track))
      ?.track;
  }

  getFreeMediaDuration(mediaDuration: number, currTime = this.time): number {
    return Math.min(mediaDuration, this.duration - currTime);
  }

  handleUpdateMyAudioMetadata(audios: myAudio[]) {
    const existingAudios = this.story?.myAudios || [];

    if (!this.story) {
      throw new Error('Cannot handle update my audio metadata: story not set');
    }

    this.story.myAudios = this.handleUpdateAudioMetadata(
      existingAudios,
      audios,
    );
  }

  handleUpdateOrganizationAudioMetadata(audios: myAudio[]) {
    if (!this.organization) {
      logger.warn(
        'TYPE_ERROR: organization not set in handleUpdateOrganizationAudioMetadata',
      );
      throw new Error(
        'Cannot handle update organization audio metadata: organization not set',
      );
    }
    const existingAudios = this.organization.organizationAudios || [];

    this.organization.organizationAudios = this.handleUpdateAudioMetadata(
      existingAudios,
      audios,
    );
  }

  handleUpdateAudioMetadata(existingAudios: myAudio[], audios: myAudio[]) {
    const updatedMyAudios = existingAudios.map(audio => {
      const currAudio = audios.find(a => a.id === audio.id);
      console.log('currAudio', currAudio);
      if (currAudio) {
        if (currAudio.song.url && !this.audioTracksData[currAudio.song.url]) {
          this.loadWaveformForSource({
            source: currAudio.song.url,
            name: currAudio.song.title,
          });
        }
        return currAudio;
      }
      return audio;
    });

    const isCompleted = audios.every(
      audio => audio.metadataStatus === 'success' && audio.waveformUploadId,
    );

    const isFailed = audios.some(audio => audio.metadataStatus === 'failed');

    if (isCompleted) {
      this.showToast({
        state: 'success',
        message: 'Audio uploaded',
      });
    }

    if (isFailed) {
      this.showToast({
        state: 'error',
        message: 'Audio upload failed, please contact Arbor',
      });
    }

    return updatedMyAudios;
  }

  addUnsaveVideoAction(): void {
    this.addVideoAction({
      editor: this.rootStore.userIdentityStore.currentEditor,
      type: 'unsave',
      date: new Date().getTime(),
    });
  }

  public addVideoAction(
    newVideoAction: VideoAction,
    videoSnapshot?: CurrentLoadedVideo,
    storySnapshot?: Story,
  ): void {
    const videoToModify = videoSnapshot ?? this.currentVideo;
    const storyToModify = storySnapshot ?? this.story;
    if (!videoToModify) {
      return;
    }
    videoToModify.lastActionJson = newVideoAction;
    for (const storyVideo of storyToModify?.otherVideos || []) {
      if (storyVideo.id === videoToModify.id) {
        storyVideo.lastActionJson = newVideoAction;
      }
    }
  }

  async findOneStory(id: string): Promise<Story | null> {
    const story = await this.datoClientStore.storyRepository?.findOne(id);

    // if (
    //   story &&
    //   this.rootStore.userIdentityStore.currentUserType === 'external'
    // ) {
    //   story.otherVideos = story.otherVideos.filter(c => c.isClientReady);
    // }

    return story || null;
  }

  async findManyStories(albumId: string) {
    let stories =
      (await this.datoClientStore.storyRepository?.findMany(albumId, {
        includeDrafts: true,
        excludeInvalid: false, // newly added stories may not have originalVideo.video field available yet, which will throw an error
        environment: this.rootStore.userIdentityStore.environment,
      })) || [];

    // if (this.rootStore.userIdentityStore.currentUserType === 'external') {
    //   stories = stories.map(s => ({
    //     ...s,
    //     visibleVideos: s.otherVideos.filter(v => v.isClientReady),
    //   }));
    // }

    stories.sort((a, b) => {
      // show unpublished stories first
      const aDate = a._publishedAt ? new Date(a._publishedAt) : new Date();
      const bDate = b._publishedAt ? new Date(b._publishedAt) : new Date();
      return bDate.getTime() - aDate.getTime();
    });

    return stories;
  }

  // Condensed debug data set for displaying in DebugMode.
  getDebugContent() {
    const videoSource = this.currentVideo?.videoSource ?? {};
    const { elements, ...videoSourceWithoutElements } = videoSource;

    const {
      transcriptionChanges,
      transcriptionSnapshot,
      ...currentVideoWithoutTranscriptions
    } = this.currentVideo ?? {};

    return {
      time: this.time,
      duration: this.duration,
      currentVideo: {
        ...currentVideoWithoutTranscriptions,
        videoSource: videoSourceWithoutElements,
      },
      // currentVideoVersions: this.currentVideoVersions,
    };
  }

  async createStory(story: Partial<StoryDTO>): Promise<DatoStory> {
    if (!this.datoClientStore.storyRepository) {
      throw new Error('createStory: no story repo');
    }
    return this.datoClientStore.storyRepository.create(story);
  }

  async updateStory(
    story: Partial<StoryDTO> & { id: string },
  ): Promise<DatoStory> {
    return this.datoClientStore.storyRepository.update(story);
  }

  addShareableImage(shareableImage: ShareableImageType): void {
    if (!this.story) {
      throw new Error('addShareableImage: no story loaded');
    }
    this.story.shareableImages = [
      shareableImage,
      ...(this.story.shareableImages || []),
    ];
  }

  async softDeleteStories(storyIds: string[]) {
    if (!this.datoClientStore.storyRepository) {
      throw new Error('deleteStory: no story repo');
    }
    if (this.organization) {
      return this.datoClientStore.storyRepository.removeReferencesFromShowcase(
        storyIds,
        this.organization.id,
      );
    }
  }

  async upsertStoryTeller({
    name,
    title,
  }: {
    name: string;
    title?: string;
  }): Promise<Person> {
    if (!this.datoClientStore.gqlClient) {
      throw new Error('findOrCreateStoryTeller: no gqlClient');
    }

    // Schema for PERSON_BY_NAME_QUERY response (permissive with .passthrough())
    const PersonByNameResponseSchema = z
      .object({
        allPeople: z.array(
          z
            .object({
              id: z.string(),
              title: z.string().optional(),
              name: z.string().default(''),
              email: z.string().default(''),
            })
            .passthrough(),
        ),
      })
      .passthrough();

    const rawResponse = await this.datoClientStore.gqlClient.request(
      PERSON_BY_NAME_QUERY,
      {
        name,
      },
    );

    // Parse response with Zod
    const parseResult = PersonByNameResponseSchema.safeParse(rawResponse);

    if (!parseResult.success) {
      const logger = LogManager.getLogger('video-creator-store');
      logger.error('TYPE_ERROR: Invalid GraphQL response structure', {
        query: 'PERSON_BY_NAME_QUERY',
        error: parseResult.error,
        rawResponse,
      });
      throw new Error(`Invalid GraphQL response: ${parseResult.error.message}`);
    }

    const response = parseResult.data;

    if (response.allPeople.length) {
      const person = response.allPeople[0];
      if (person.title !== title && title) {
        this.datoClientStore.datoClient?.items.update(person.id, {
          title,
        });
        person.title = title;
      }
      return person;
    }

    if (!this.datoClientStore.datoClient) {
      throw new Error('findOrCreateStoryTeller: no datoClient');
    }
    const itemType =
      await this.datoClientStore.datoClient.itemTypes.find('person');
    const newPersonAttrs = {
      name,
      title,
      email: '',
    };
    const newPerson = await this.datoClientStore.datoClient.items.create({
      item_type: { type: 'item_type', id: itemType.id },
      ...newPersonAttrs,
    });
    return {
      id: newPerson.id,
      ...newPersonAttrs,
    };
  }

  setShowSubtitles(showSubtitles: boolean) {
    this.showSubtitles = showSubtitles;
  }

  /**
   * PLAT-11877 — what the right panel showed before the Captions view was
   * picked, so leaving the view can put it back. `null` when the panel was
   * already on Captions (nothing to restore) or no view is open.
   */
  private sidebarBeforeCaptionsView: {
    option: SidebarOption;
    expanded: boolean;
  } | null = null;

  /**
   * PLAT-11689 — Captions picked in the transcript's view dropdown. Creator
   * Studio treats it as "work on the captions": the caption-line view, the
   * captions editor open beside it (on its styles view — the dropdown only
   * offers Captions once a track exists), and the captions shown on the video
   * if they had been hidden (undoable). The clip preview's adapter does the
   * same minus the sidebar it lacks. PLAT-11877 — the panel and the view move
   * together: `leaveCaptionsView` puts the panel back where it was.
   */
  openCaptionsView() {
    this.setShowSubtitles(true);
    if (this.sidebarOptions !== SidebarOption.karaoke) {
      this.sidebarBeforeCaptionsView = {
        option: this.sidebarOptions,
        expanded: this.rootStore.layoutStore.isSidebarExpanded,
      };
    }
    this.karaokeSubMenu = KaraokeCard.styles;
    this.sidebarOptions = SidebarOption.karaoke;
    this.rootStore.layoutStore.setIsSidebarExpanded(true);
    const editor = this.rootStore.editorStore?.editor;
    // PLAT-11877 — with no caption track yet, Captions is also the way to
    // make one: produce it in the showcase's default style (the one rule,
    // `getDefaultCaptionTemplateConfig`), shown. Leaving the view again never
    // takes the captions off the video.
    if (editor && editor.getKaraokeLines().length === 0) {
      const fallbackTemplate =
        this.textBrandingService.getDefaultCaptionTemplateConfig(
          this.getCurrentAspectRatio(),
        );
      if (fallbackTemplate) {
        this.textBrandingService.selectedTemplateId =
          fallbackTemplate.templateId;
      }
      if (
        produceFirstCaptions({
          editor,
          fallbackTemplate,
          transcriptLanguage: this.rootStore.transcriptionStore?.rawLanguage,
          originalVideoLanguage: this.story?.originalVideoLanguage,
          showToast: toast => this.showToast(toast),
        })
      ) {
        this.rootStore.editorStore.refreshKaraoke();
        this.rootStore.videoSourceStore.refreshSource();
      }
      return;
    }
    if (editor && areCaptionsHidden(editor)) {
      setCaptionsHidden(editor, false);
      this.rootStore.editorStore.refreshKaraoke();
      this.rootStore.videoSourceStore.refreshSource();
    }
  }

  /**
   * PLAT-11877 — the transcript's view left Captions (for Clip Text or
   * Redlines): the right panel goes back to what it showed before the
   * Captions pick — its previous tab, or collapsed — when it is still on
   * Captions. A panel the person moved elsewhere since, or one that was on
   * Captions before the pick, is left alone.
   */
  leaveCaptionsView() {
    const previous = this.sidebarBeforeCaptionsView;
    this.sidebarBeforeCaptionsView = null;
    if (!previous || this.sidebarOptions !== SidebarOption.karaoke) return;
    if (!previous.expanded || previous.option === SidebarOption.none) {
      this.collapseSidebar();
      return;
    }
    this.sidebarOptions = previous.option;
  }

  collapseSidebar() {
    // Remember the panel we're collapsing from so reopening its tab can restore
    // the same view (and keep the active element selected) rather than starting
    // fresh. See SidebarIconMenu.handleIconClick.
    if (this.sidebarOptions !== SidebarOption.none) {
      this.lastExpandedSidebarOption = this.sidebarOptions;
    }
    this.rootStore.layoutStore.setIsSidebarExpanded(false);
    this.sidebarOptions = SidebarOption.none;
  }

  getShowSubtitles(): boolean {
    return this.showSubtitles;
  }

  hasLandscapeAspectRatio(): boolean {
    const aspectRatio = this.getCurrentAspectRatio();
    return aspectRatio === AspectRatio.AR_16_9;
  }

  hasDefaultAspectRatio(): boolean {
    const aspectRatio = this.getCurrentAspectRatio();
    return aspectRatio === this.getDefaultAspectRatio();
  }

  setFlags(flags: Record<string, boolean | number | string>) {
    if (inDebugMode()) {
      console.debug('Setting flags', flags);
    }
    this.flags = {
      ...this.flags,
      ...flags,
    };
  }

  getFlag(flag: KnownFlags): boolean {
    return !!this.flags[flag];
  }

  getStringFlag(flag: StringFlags): string | undefined {
    return this.flags[flag];
  }

  getEditorStore() {
    return this.rootStore.editorStore;
  }

  handleRenderingError(error: unknown, videoPublisher: VideoPublisher) {
    this.renderingStatus = 'error';
    console.error('RENDER VIDEO | CLIENT SIDE ERROR', error);
    videoPublisher.analyticsLog(
      LogLevel.ERROR,
      'RENDER VIDEO | CLIENT SIDE ERROR',
      {
        error,
      },
    );
  }

  clearExtraElementData(ids: string[]) {
    if (!this.currentVideo) {
      return;
    }
    for (const id of ids) {
      deleteElementMeta(this.currentVideo, id);
      deleteElementMeta(this.currentVideo, `logo_el_${id}`);
    }
  }

  onExtraElementDataChange(
    cb: (extraElementData: Video['extraElementData']) => void,
  ) {
    return reaction<Video['extraElementData']>(
      () => {
        if (!this.currentVideo) {
          logger.warn(
            'TYPE_ERROR: currentVideo not set in onExtraElementDataChange',
          );
          return {};
        }
        return this.currentVideo.extraElementData;
      },
      extraElementData => {
        cb(extraElementData);
      },
    );
  }

  /**
   * Returns whether the loaded clip is kept: when `videoId` is the clip
   * Creator Studio still holds, it reopens without a reload, as from the
   * sidebar (PLAT-11911).
   */
  prepareCreatorStudioNavigation(videoId?: string): boolean {
    this.videoClipPreview = null;
    if (videoId && videoId === this.currentVideo?.id) return true;
    this.localCanvasStoryId = null;
    this.nativeLocalPreviewStoryId = null;
    this.localCanvasAttemptStoryId = null;
    this.releaseLoadedVideo();
    // Keep the story cache. During TTFE the freshly-created story can still
    // return 500 from the full STORY_QUERY even though Content Studio already
    // has the usable story and saved clip in memory. Creator's initializeData
    // uses `fromCache: true`, then replaces only currentVideo with the routed
    // clip; clearing the story here strands the route on its loading screen.
    return false;
  }

  /**
   * PLAT-11911: brings a clip Creator Studio opened without loading it (seeded
   * or kept) up to Dato's head. See `ReconcileLoadedVideoService`.
   */
  async reconcileLoadedVideoWithHead() {
    const result = await new ReconcileLoadedVideoService({
      getLoadedVideo: () => this.currentVideo,
      isLoadedVideoTheLatest: () => this.isCurrentVideoTheLatest,
      isRendering: videoId => this.renderingVideoIds.includes(videoId),
      loadVersionList: async videoId => {
        await this.loadVersionsHistory(videoId, 0, {
          syncLoadedVersionToHead: false,
        });
        return this.currentVideoVersions?.find(v => v.meta.is_current);
      },
    }).reconcile();
    if (!result.ok) {
      logger.warn('reconcileLoadedVideoWithHead: skipped', result.error);
      return;
    }
    const outcome = result.value;
    if (outcome.kind === 'fieldsChanged') {
      runInAction(() => Object.assign(outcome.video, outcome.patch));
    } else if (outcome.kind === 'contentChanged') {
      dispatchStaleVideoDetected(outcome.head);
    }
  }

  /**
   * PLAT-11911: drop the loaded clip so the next Creator Studio open loads one.
   * Same-story navigation skips resetStateBeforeInitialising, so this resets
   * the per-video state the canvas, timeline, undo and version panel read
   * before the next clip renders. Otherwise the renderer replays the last
   * composition and the reused editor shows the previous project.
   */
  releaseLoadedVideo() {
    // to prevent race condition where previous currentVideo possibly gets selected
    // or currentVideo doesn't get initialized properly, e.g. timeline not resized
    this.currentVideo = undefined;
    this.rootStore.videoSourceStore.resetSource();
    this.stateReady = false;
    this.isLoading = true;
    this.resetUndoRedo();
    this.clearVideoVersions();
  }

  /**
   * Recalculates timeline scale if the timeline is zoomed out below default level.
   */
  recalculateTimelineScale() {
    // Only recalculate timeline if below default zoom level
    const isZoomedOut = this.timelineScale <= this.defaultTimelineScale;
    if (isZoomedOut) {
      this.timelineStore.setShouldCalcTimelineScale(true);
    }
  }

  /**
   * Checks if the current timeline scale matches the default zoom level.
   * Uses epsilon comparison to handle floating-point precision.
   * The default zoom is calculated as defaultTimelineScale * 0.95.
   */
  private isAtDefaultZoomLevel(): boolean {
    const epsilon = 0.001; // Tolerance for floating-point comparison
    const expectedDefaultScale = this.defaultTimelineScale * 0.95;
    return Math.abs(this.timelineScale - expectedDefaultScale) < epsilon;
  }

  /**
   * Recalculates timeline scale if the timeline has default zoom level.
   */
  recalculateTimelineScaleIfAtDefaultZoom() {
    if (this.isAtDefaultZoomLevel()) {
      this.timelineStore.setShouldCalcTimelineScale(true);
    }
  }

  /**
   * PLAT-9966: install the per-paint composition translation projection on
   * the videoSourceStore. Reads the active caption language off the
   * editor's KaraokeConfig and the per-element controls/template id off
   * `currentVideo.extraElementData`; rewrites translatable text fields
   * with phrase/preset values from `TranslationStore.compositionPhrases`.
   *
   * Pure projection — `videoSourceStore._source` is never mutated, so save
   * paths continue to round-trip the original-language tree to S3.
   *
   * Two triggers re-fire the debounced setSource so the renderer picks up
   * a new projection:
   *   1. Caption language change — fired imperatively from
   *      `CaptionsLanguageHeaderToggle` via `refreshSource()` after
   *      `applyCaptionsLanguage` lands. The editor's KaraokeConfig is a
   *      plain class, not MobX-observable, so a reaction on it wouldn't
   *      fire.
   *   2. Translation data landing for the already-active language — the
   *      MobX reaction below subscribes to the observable
   *      `translations.get(activeLang)` slot.
   * Together they cover the two paths that can change projection output
   * without `_source` itself changing.
   */
  private installCompositionTranslationProjection(): void {
    const videoSourceStore = this.rootStore.videoSourceStore;
    const translationStore = this.rootStore.translationStore;
    const editorStore = this.rootStore.editorStore;
    videoSourceStore.setOutboundTransform(source => {
      const activeLang = readActiveCaptionLanguage(editorStore.editor);
      if (isOriginalLang(activeLang)) return { outbound: source };
      const elements = (source as { elements?: unknown }).elements;
      if (!Array.isArray(elements) || elements.length === 0)
        return { outbound: source };
      const extraElementData =
        (this.currentVideo?.extraElementData as
          | Record<string, unknown>
          | undefined) ?? null;
      const phrasesByNormalizedSource =
        translationStore.getCompositionPhrasesByNormalizedSource(activeLang);
      // PLAT-11466 — translated overlay text needs a font that can render it;
      // the workspace's own fonts carry the probed coverage that decides.
      const brandVariants = makeBrandFontVariantLookup(
        this.organization?.branding?.customFonts,
      );
      const projection = projectCompositionTranslations({
        videoSourceElements: elements as ReadonlyArray<Record<string, unknown>>,
        extraElementData,
        activeLanguage: activeLang,
        compositionPhrasesByNormalizedSource: phrasesByNormalizedSource,
        resolveFontSubstitute: (family, lang) =>
          getGraphicFontSubstitute(family, lang, brandVariants),
      });
      if (!projection.changed) return { outbound: source };
      return {
        outbound: {
          ...(source as Record<string, unknown>),
          elements: projection.elements,
        } as unknown as VideoSource,
        rewrites: projection.rewrites,
      };
    });

    // Trigger 2: translation lands for the already-active language. Cheap
    // when language is original — the reaction body returns `null` and
    // doesn't subscribe to any TranslationData entry.
    reaction(
      () => {
        const lang = readActiveCaptionLanguage(editorStore.editor);
        if (isOriginalLang(lang)) return null;
        return translationStore.translations.get(lang) ?? null;
      },
      () => {
        videoSourceStore.refreshSource();
      },
      { name: 'composition-translation-projection-refresh' },
    );
  }
}

export default VideoCreatorStore;
